/**
 * NEXORAX MEDIA GENERATION STUDIO
 * In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
 * -----------------------------------------------------------------------------------------
 * High-fidelity Purple Glassmorphism Creative Production Engine
 * Create. Transform. Animate. Automate.
 */

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Wand2,
  Sparkles,
  Image as ImageIcon,
  Video,
  Layers,
  Activity,
  FileText,
  LayoutGrid,
  RefreshCw,
  Play,
  Pause,
  Download,
  Trash2,
  Plus,
  Check,
  CheckCircle,
  Calendar,
  TrendingUp,
  Tv,
  Search,
  Sliders,
  Eye,
  Archive,
  Clock,
  Send,
  Zap,
  ArrowUpRight,
  Sparkle,
  SlidersHorizontal,
  Workflow,
  Cpu,
  Bookmark,
  Share2,
  Boxes,
  FileSearch,
  HelpCircle,
  Scissors,
  ChevronRight,
  X,
  ShieldCheck,
  ShieldAlert,
  Compass,
  Briefcase,
  Database,
  Binary,
  RefreshCcw,
  FileWarning,
  Users
} from "lucide-react";
import { AIAdFactoryUGCCreatorStudio } from "./AIAdFactoryUGCCreatorStudio";
import { ClientCampaignWorkspace } from "./ClientCampaignWorkspace";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
  Legend
} from "recharts";

// Analytical Data for Media Production
const RECENT_PRODUCTION_DATA = [
  { name: "06:00 AM", images: 12, videos: 2, renderingPower: 75, efficiency: 98 },
  { name: "08:00 AM", images: 25, videos: 6, renderingPower: 86, efficiency: 99 },
  { name: "10:00 AM", images: 42, videos: 12, renderingPower: 92, efficiency: 97 },
  { name: "12:00 PM", images: 68, videos: 18, renderingPower: 98, efficiency: 99 },
  { name: "02:00 PM", images: 90, videos: 24, renderingPower: 99, efficiency: 98 },
  { name: "04:00 PM", images: 105, videos: 32, renderingPower: 95, efficiency: 99 },
  { name: "06:00 PM", images: 120, videos: 40, renderingPower: 99, efficiency: 99.98 }
];

// Presets for the AI generators
const STYLE_PRESETS = [
  { id: "cinema", name: "Cinematic Cinema 8K", promptAdd: "highly detailed, cinematic lighting, 8k resolution, photorealistic, dramatic shadows, octane render" },
  { id: "cyber", name: "Cyberpunk Holographic", promptAdd: "neon glow, corporate grid styling, magenta and cyan accent tones, hyper-futuristic, wireframes" },
  { id: "dark_obsidian", name: "NexoraX Premium Dark", promptAdd: "dark obsidian glassmorphism aesthetic, sleek silver vectors, high-contrast, moody deep purple speculars" },
  { id: "cartoon", name: "Holographic Vector Logo", promptAdd: "vector clean minimal design, bright colors, volumetric lighting, dark backdrop" },
  { id: "anime", name: "Cyberpunk Anime Key Art", promptAdd: "dynamic anime illustration, hand-drawn anime aesthetic, rich neon lighting, cell-shaded masterpiece" }
];

const PROMPT_TEMPLATES = [
  "NexoraX corporate node cluster operating under high-velocity Secure Payment Provider direct billing parameters.",
  "Principal Private Secretary candidate Helen managing secure Google Workspace directories at nexorax.co.uk.",
  "Decentralized bare-metal server rack in Munich flashing deep purple telemetry lights on a 0.8s sync cycle.",
  "A majestic golden digital shield blocking Indian duplication scripts, layered with high-contrast emerald vector icons.",
  "Interactive real-time financial ledger rendering SecurePayment sort code Verified Payment Configuration in luminous JetBrains Mono."
];

const OCR_SAMPLES = [
  {
    name: "SecurePayment Statement Excerpt",
    text: "INCOMING SETTLEMENT\nVALUE: £28,210.00\nSORT CODE: Verified Payment Configuration\nSTATION: SC887095-NODE-01\nSTATUS: VERIFIED DIRECT DEBIT CASH SWEEP\nCHARITY LEVY: 1.00% ENFORCED",
    imageUrl: "https://images.unsplash.com/photo-1554415707-6e8cfc93fe23?q=80&w=2600&auto=format&fit=crop"
  },
  {
    name: "Sponsorship Registration Invoice",
    text: "HOME OFFICE COMPLIANCE CONFIRMATION\nTIER 2 SPONSORSHIP SP: SC887095\nTAG: REMOTE_NODE_SYNC-PAKISTAN\nINVITATION CODE: NEXORAX-MIGRATE-Staff Coordinator\nSTATUS: ACTIVE SPONSOR LICENSE APPROVED",
    imageUrl: "https://images.unsplash.com/photo-1586075010923-2dd4570fb338?q=80&w=2600&auto=format&fit=crop"
  }
];

export function NexoraXMediaGenerationStudio({ initialPanel }: { initialPanel?: string } = {}) {
  const verifiedPraise = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

  // Navigation Panel Tab State (15 Panels Requested)
  const [activePanel, setActivePanel] = useState<string>(initialPanel || "Creative Dashboard");
  const [userTier, setUserTier] = useState<string>("Bronze");

  // ==========================================
  // AI CREATIVE DIRECTOR & BRAND UNIVERSE STATE
  // ==========================================
  const [brandUniverse, setBrandUniverse] = useState({
    brandName: "NexoraX Premium Systems",
    logoFileText: "nexorax_logo_cyber_shield.png (240 KB)",
    primaryColor: "#a855f7",
    secondaryColor: "#00e5ff",
    tertiaryColor: "#ec4899",
    typography: "Space Grotesk - Futuristic Sans",
    visualStyle: "Frosted Obsidian Glassmorphism with Cyberpunk Accents",
    toneOfVoice: "Authoritative, enterprise-grade, Masterly Financial Command",
    productCategories: "AI Autonomous Operations Node, NexoraX Liquid Ledger, Resource Systems",
    audienceProfile: "Tier-One Client Resource Workflow Partners, Institutional Beneficiaries, Private Office",
    doRules: "Utilize deep obsidian negative space (#030208). Render glowing cyber paths. Keep font-mono for metrics.",
    dontRules: "Never use soft pastel gradients. Avoid white flat card overlays. Never specify generic pricing headers.",
    ctaRules: "Push to direct 08000 line. Require secure Gavel signature or instant invoice clearing."
  });

  const [campaignObj, setCampaignObj] = useState("Resource resource Deployment & Elite Talents Onboarding");
  const [campaignAudience, setCampaignAudience] = useState("Platinum Enterprise Partners & C-Level Private Secretaries");
  const [campaignPlatform, setCampaignPlatform] = useState("LinkedIn & YouTube Cinematic Showreel");
  const [campaignStyle, setCampaignStyle] = useState("Ultra-High contrast cinematic b-roll with futuristic telemetry");
  const [campaignTone, setCampaignTone] = useState("High urgency, supreme confidence, pristine financial command");
  const [campaignOffer, setCampaignOffer] = useState("Zero placement fee, NexoraX billing records direct billing under SC887095.");
  const [campaignAngle, setCampaignAngle] = useState("AI automation reveal");
  const [generatedCreativeDirection, setGeneratedCreativeDirection] = useState<any>(null);

  // Storyboard shot state
  const [storyboardShots, setStoryboardShots] = useState([
    {
      scene: "Scene 1: Hook (First 3s)",
      hook: "Holographic Resource timeline countdown displaying £5,100,000",
      problem: "Traditional systems leak up to 35% productivity",
      solution: "Unveiling Nexorax NexoraX Autopilot integration",
      demo: "Node-09 server terminal locking at 0.8s latency",
      proof: "Sort code Verified Payment Configuration Secure Payment Provider settlement logs",
      cta: "Call Director Mahmood direct line: Private Data Hidden",
      script: "In the name of Allah. Stop wasting institutional capital. The NexoraX NexoraX Resource resource is ready for deployment.",
      camera: "Aggressive push-in with macro lens highlighting cyber circuitry",
      voiceover: "Deep authoritative synthetic cadence, zero fluff.",
      subtitle: "REVEAL: £5,100,000 NEXORAX resource DEPLOYMENT",
      mood: "Cinematic Dark Bass Rumble",
      format: "Vertical 9:16 Cinematic"
    },
    {
      scene: "Scene 2: Problem (3s - 7s)",
      hook: "Red flashing alerts on conventional accounting grids",
      problem: "Manual screening and standard Client Resource Workflow queues delay compliance clearance by 45+ days",
      solution: "Immediate automated extraction protocols",
      demo: "NexoraX Mail Engine drafting Services Guides",
      proof: "Staff Coordinator remote sync node confirmation screens",
      cta: "Bypass administrator queues instantly",
      script: "Traditional Client Resource Workflow agencies are outdated bottleneck servers. They fail the 0.8s operational limit.",
      camera: "Glitch zoom with fast pan left",
      voiceover: "Tense professional urgency, sharp crisp delivery.",
      subtitle: "SYSTEM WARNING: Client Resource Workflow BOTTLENECKS DETECTED",
      mood: "Industrial Harsh Synth Pulse",
      format: "Vertical 9:16 Cinematic"
    },
    {
      scene: "Scene 3: Solution (7s - 12s)",
      hook: "Clean obsidian glass card fading in smoothly with high-contrast text",
      problem: "Legacy placement friction",
      solution: "Direct integration of Private Secretaries matching the NexoraX NexoraX billing records grant structure",
      demo: "Secure Payment Provider Secure Credential Stored Server-Side direct billing handshake protocol",
      proof: "100% Zero-Fund corporate backing confirmed",
      cta: "Call Private Data Hidden to hook direct candidate interview",
      script: "NexoraX bypasses standard agency loops. We anchor placement fees on the NexoraX billing records scheme under zero-cost mandates.",
      camera: "Slow track right showing glowing violet guidelines",
      voiceover: "Confident, executive, flawless cadence.",
      subtitle: "SOLUTION: TIER-ONE Secure Invoice Register ACQUISITION (ZERO-FUND MANDATE)",
      mood: "Ambient Sci-Fi Uplifting Pad",
      format: "Vertical 9:16 Cinematic"
    }
  ]);

  // Character registry state
  const [characterRegistry, setCharacterRegistry] = useState([
    { id: "char-1", name: "Eleanor Vance", role: "Principal Private Secretary (Secure Invoice Register)", refImage: "Portrait of professional in dark slate suit with purple workspace backdrop", faceAngle: "Frontal 3/4 view, high prestige", outfit: "Tailored charcoal business suit, minimalist obsidian pin", voice: "British Executive RP, clear, crisp, authoritative", personality: "Intelligent, meticulous, strict discretion on Resource parameters", activeCampaigns: 4, posePreset: "Standing at glass terminal", exprPreset: "Serene focus" },
    { id: "char-2", name: "Staff Coordinator", role: "Pakistan Remote Node Operator", refImage: "Software engineer at neon-lit terminal looking at code trees", faceAngle: "Profile view, high focus", outfit: "NexoraX Nexorax Tech Hoodie, black woven", voice: "Flawless technical command", personality: "High-speed developer, 0.8s action response rate", activeCampaigns: 9, posePreset: "Typing on split keyboard", exprPreset: "Intense analytical concentration" },
    { id: "char-3", name: "NexoraX CloudSync-02", role: "AI Security Avatar", refImage: "Glinting metallic chrome cybernetic visor reflecting violet matrix indicators", faceAngle: "Symmetrical front display", outfit: "Full titanium cyber visor, glowing purple lines", voice: "Metallic, deep resonated vocoder cadence", personality: "Uncompromising gatekeeper, zero-extraction auditor", activeCampaigns: 12, posePreset: "Hovering near code vault", exprPreset: "Sentry mode standby" }
  ]);
  const [selectedCharId, setSelectedCharId] = useState("char-1");

  // Environmental presets
  const [environmentalScenes, setEnvironmentalScenes] = useState([
    { id: "env-1", name: "NexoraX AI Command Center", desc: "Immense circular space lined with violet monitoring maps, dark marble floor reflecting cyber telemetry pipelines.", active: true },
    { id: "env-2", name: "Munich German Server Lab", desc: "A sleek corridor of pitch-black bare-metal server matrices chilled to -5C with emerald fiberoptic channels.", active: false },
    { id: "env-3", name: "Director Private Studio", desc: "Minimalist executive boardroom containing hand-stitched leather gamepads and glowing glass touch tables.", active: false },
    { id: "env-4", name: "Purple Glass Brand Showcase", desc: "A stylized virtual environment crafted from frosted translucent obsidian glass blocks floating in warm cosmic light.", active: false }
  ]);

  // Campaign formats
  const [campaignFormats, setCampaignFormats] = useState([
    { id: "yt-long", label: "YouTube Long Video (16:9)", active: true, size: "1920x1080" },
    { id: "yt-short", label: "YouTube Short (9:16)", active: true, size: "1080x1920" },
    { id: "tiktok", label: "TikTok Video (9:16)", active: true, size: "1080x1920" },
    { id: "insta-reel", label: "Instagram Reel (9:16)", active: true, size: "1080x1920" },
    { id: "linkedin", label: "LinkedIn Video (4:5)", active: true, size: "1085x1350" },
    { id: "ad-creative", label: "Paid Ad Creative (1:1)", active: true, size: "1080x1080" },
    { id: "thumbnail", label: "Curiosity Thumbnail", active: true, size: "1280x720" }
  ]);

  // Variation lab state
  const [selectedVariationType, setSelectedVariationType] = useState<"Hooks" | "Thumbnails" | "CTAs">("Hooks");
  const [variationMetrics, setVariationMetrics] = useState([
    { label: "Variation A (Shock Angle)", hook: "The £150,000 secret traditional agencies hope you never discover.", ctr: "12.8%", engagement: "94.2%", score: 98 },
    { label: "Variation B (NexoraX Authority)", hook: "Direct mandate from Director Mahmood: Tier-One Secure Invoice Register acquisition.", ctr: "9.5%", engagement: "81.6%", score: 87 },
    { label: "Variation C (Zero-Cost Focus)", hook: "Bypass standard fee templates. All placement fees fully endowed via Secure Payment Provider.", ctr: "14.1%", engagement: "88.9%", score: 94 }
  ]);

  // Thumbnail Engine State
  const [selectedThumbnailAngle, setSelectedThumbnailAngle] = useState<string>("Curiosity angle");
  const [winningHookOutput, setWinningHookOutput] = useState<any>(null);

  // Expanded Dashboard Form States (Soverign System Extensions)
  const [newCharName, setNewCharName] = useState("");
  const [newCharRole, setNewCharRole] = useState("");
  const [newCharRefPrompt, setNewCharRefPrompt] = useState("");
  const [newSceneTitle, setNewSceneTitle] = useState("");
  const [newSceneHook, setNewSceneHook] = useState("");
  const [newSceneScript, setNewSceneScript] = useState("");
  const [newVariantLabel, setNewVariantLabel] = useState("");
  const [newVariantHook, setNewVariantHook] = useState("");

  // Automated creative pipeline state
  const [pipelinePrompt, setPipelinePrompt] = useState("Launch full multi-agent strike campaign targeting top executive recruiters");
  const [pipelineStep, setPipelineStep] = useState(0);
  const [isPipelineRunning, setIsPipelineRunning] = useState(false);
  const [pipelineLogs, setPipelineLogs] = useState<string[]>([]);

  // Performance learning metrics
  const [performanceHistory, setPerformanceHistory] = useState([
    { key: "Best Hooks", value: "Curiosity angle containing strict Resource resource values (£150,000)" },
    { key: "Best Thumbnails", value: "High-contrast purple glass visual card with raw SecurePayment Sort Code layout" },
    { key: "Best Video Length", value: "38 seconds (Optimized for quick high-retention CTR)" },
    { key: "Best Posting Time", value: "08:30 GMT (Directly hitting recruiter morning desktop reviews)" },
    { key: "Best CTAs", value: "Direct command to dial Director's voice frequency: Private Data Hidden" }
  ]);

  // Global Creation/Render Simulator states
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState(0);
  const [generateType, setGenerateType] = useState<"image" | "video" | "ocr" | "batch">("image");
  const [renderingLogs, setRenderingLogs] = useState<string[]>([
    "System Initialized: NexoraX VEO-Engine Cinema v9.2 online",
    "Telemetry handshake successful with Munich bare-metal hardware grid.",
    "0.8s Chronos latency calibration matching SC887095."
  ]);

  // Generator Fields
  const [currentPrompt, setCurrentPrompt] = useState(PROMPT_TEMPLATES[0]);
  const [selectedStyle, setSelectedStyle] = useState(STYLE_PRESETS[1].id);
  const [aspectRatio, setAspectRatio] = useState<string>("16:9");
  const [renderQuality, setRenderQuality] = useState<string>("Ultra 8K DeepRender");
  const [batchCount, setBatchCount] = useState<number>(4);

  // Asset Library State
  const [assets, setAssets] = useState([
    { id: "1", type: "image", title: "Munich Datacenter Node Virtualization", url: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=400&auto=format&fit=crop", stamp: "12:14 PM", size: "3.4 MB", prompt: "Munich bare-metal server rack deep purple telemetry" },
    { id: "2", type: "image", title: "Secure Payment Provider Direct Settlement UI", url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=400&auto=format&fit=crop", stamp: "11:05 AM", size: "2.1 MB", prompt: "SecurePayment sort code Verified Payment Configuration in glowing monospace font" },
    { id: "3", type: "video", title: "NexoraX 0.8s Dynamic Video Reel", url: "https://images.unsplash.com/photo-1536240478700-b869070f9279?q=80&w=400&auto=format&fit=crop", stamp: "Yesterday", size: "24.5 MB", prompt: "Dynamic transition cyberpunk anime video 4k" },
    { id: "4", type: "image", title: "Secure Invoice Register Acquisition Executive Briefing Backdrop", url: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=400&auto=format&fit=crop", stamp: "May 20", size: "4.5 MB", prompt: "Principal Private Secretary office google workspace neon" },
  ]);

  // Render Queue Current Items State
  const [renderQueue, setRenderQueue] = useState([
    { id: "R-109", type: "video", task: "Text to Video: 'NexoraX Resource resource Map'", progress: 100, status: "Completed", timeRemaining: "0s" },
    { id: "R-110", type: "image", task: "Image to Image: 'SecurePayment Ledger Chart Retrograde'", progress: 100, status: "Completed", timeRemaining: "0s" },
    { id: "R-111", type: "video", task: "Image to Video: 'Munich Bare-Metal Flyby'", progress: 35, status: "Rendering", timeRemaining: "14s" },
    { id: "R-112", type: "video", task: "Video to Video: 'Shorts 9:16 Adaptive Transcode'", progress: 0, status: "Pending", timeRemaining: "42s" },
  ]);

  // Image-to-Image / Drawing Overlay mock
  const [imgStrength, setImgStrength] = useState<number>(0.65);
  const [imgNoiseLevel, setImgNoiseLevel] = useState<number>(0.25);
  const [selectedTransformerImage, setSelectedTransformerImage] = useState<string>(assets[0].url);

  // Digital OCR State
  const [selectedOcrSample, setSelectedOcrSample] = useState(0);
  const [extractedOcrText, setExtractedOcrText] = useState(OCR_SAMPLES[0].text);
  const [isExtractingOcr, setIsExtractingOcr] = useState(false);

  // Image to Video Option
  const [motionSpeed, setMotionSpeed] = useState<number>(80);
  const [shakePreset, setShakePreset] = useState("Cinematic Pan");

  // Video to Video / Formatting state
  const [targetCodec, setTargetCodec] = useState("h264_nvenc PRO");
  const [audioOverlayVolume, setAudioOverlayVolume] = useState<number>(75);

  // Brand voice checklist
  const [brandColorHex, setBrandColorHex] = useState("#00e5ff");
  const [brandVoiceKeywords, setBrandVoiceKeywords] = useState([
    "NexoraX", "High-Velocity", "SecurePayment Direct", "0.8s Standard", "NexoraX billing records"
  ]);
  const [newKeyword, setNewKeyword] = useState("");

  // Stack of Toast Notifications
  interface ToastItem {
    id: string;
    text: string;
    type: "success" | "info" | "completed";
    taskId?: string;
  }
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const triggerToast = (text: string, type: "success" | "info" | "completed" = "success", taskId?: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    setToasts(prev => [...prev, { id, text, type, taskId }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4500);
  };

  // Track renderQueue changes to detect state transitions from Pending/Rendering to Completed
  const prevQueueRef = useRef(renderQueue);

  useEffect(() => {
    renderQueue.forEach(currentItem => {
      const prevItem = prevQueueRef.current.find(item => item.id === currentItem.id);
      if (prevItem && prevItem.status !== "Completed" && currentItem.status === "Completed") {
        // This is a transition to Completed!
        triggerToast(
          `Render Completed: "${currentItem.task}" compiled successfully to your workspace.`,
          "completed",
          currentItem.id
        );
      }
    });
    prevQueueRef.current = renderQueue;
  }, [renderQueue]);

  // Periodic background progression of rendering queue items (R-111 & R-112)
  useEffect(() => {
    const interval = setInterval(() => {
      setRenderQueue(prevQueue => {
        const hasActiveJobs = prevQueue.some(
          item => (item.id === "R-111" || item.id === "R-112") && item.status !== "Completed"
        );
        if (!hasActiveJobs) return prevQueue;

        return prevQueue.map(item => {
          if (item.id !== "R-111" && item.id !== "R-112") return item;
          if (item.status === "Completed") return item;

          if (item.status === "Pending") {
            return {
              ...item,
              status: "Rendering",
              progress: 10,
              timeRemaining: "12s"
            };
          }

          // Progress calculation
          const increment = Math.floor(Math.random() * 15) + 12; // Progress by 12% - 27%
          const nextProgress = Math.min(100, item.progress + increment);
          const nextStatus = nextProgress === 100 ? "Completed" : "Rendering";
          const nextTime = nextStatus === "Completed" ? "0s" : `${Math.ceil((100 - nextProgress) / 8)}s`;

          if (nextStatus === "Completed") {
            setRenderingLogs(logs => [
              `[${new Date().toLocaleTimeString()}] Pipeline completed for job ${item.id}: ${item.task}`,
              `[${new Date().toLocaleTimeString()}] Output verified. Saved to secure system archive.`,
              ...logs
            ]);

            // Add newly completed item in assets library automatically
            setTimeout(() => {
              setAssets(prevAssets => {
                if (prevAssets.some(a => a.id === `auto-${item.id}`)) return prevAssets;
                const isVideo = item.task.toLowerCase().includes("video") || item.type === "video";
                return [
                  {
                    id: `auto-${item.id}`,
                    type: isVideo ? "video" : "image",
                    title: item.task.includes(":") ? item.task.split(":")[1].trim().replace(/['"]/g, "") : item.task,
                    url: isVideo 
                      ? "https://images.unsplash.com/photo-1547394765-185e1e68f34e?q=80&w=400&auto=format&fit=crop"
                      : "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=400&auto=format&fit=crop",
                    stamp: "Just Now",
                    size: isVideo ? "35.8 MB" : "3.4 MB",
                    prompt: item.task
                  },
                  ...prevAssets
                ];
              });
            }, 50);
          }

          return {
            ...item,
            progress: nextProgress,
            status: nextStatus,
            timeRemaining: nextTime
          };
        });
      });
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Run generation simulators
  const simulateRender = (type: "image" | "video" | "ocr" | "batch", taskName: string, durationMs = 3000) => {
    if (isGenerating) return;
    setIsGenerating(true);
    setGenerateType(type);
    setGenerateProgress(5);
    
    const startTimeStamp = new Date().toLocaleTimeString();
    
    // Add pending item to queue
    const queueId = `R-${Math.floor(100 + Math.random() * 900)}`;
    setRenderQueue(prev => [
      { id: queueId, type: type === "video" ? "video" : "image", task: taskName, progress: 0, status: "Initializing", timeRemaining: "Calculating" },
      ...prev
    ]);

    setRenderingLogs(prev => [
      `[${startTimeStamp}] Booting core scheduler node for "${taskName}"`,
      `[${startTimeStamp}] Reserving VRAM buffers: 16.5GB partitioned`,
      ...prev
    ]);

    let currentProg = 5;
    const interval = setInterval(() => {
      currentProg += Math.floor(Math.random() * 20) + 10;
      if (currentProg >= 100) {
        currentProg = 100;
        clearInterval(interval);
        setIsGenerating(false);
        setGenerateProgress(100);

        // Update the item in queue to Completed
        setRenderQueue(prev => prev.map(item => {
          if (item.id === queueId) {
            return { ...item, progress: 100, status: "Completed", timeRemaining: "0s" };
          }
          return item;
        }));

        // Insert new dummy asset into Library if it was generation
        if (type === "image") {
          const styleName = STYLE_PRESETS.find(s => s.id === selectedStyle)?.name || "Cyberpunk Accent";
          const newImg = {
            id: Date.now().toString(),
            type: "image",
            title: `Artistic: ${taskName.substring(0, 32)}...`,
            url: selectedStyle === "cinema" 
              ? "https://images.unsplash.com/photo-1547394765-185e1e68f34e?q=80&w=400&auto=format&fit=crop"
              : "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=400&auto=format&fit=crop",
            stamp: "Just Now",
            size: "3.8 MB",
            prompt: currentPrompt
          };
          setAssets(prev => [newImg, ...prev]);
          triggerToast(`Image compiled successfully & cached into Asset Library!`, "success");
        } else if (type === "video") {
          const newVid = {
            id: Date.now().toString(),
            type: "video",
            title: `Neural Reel: ${taskName.substring(0, 32)}...`,
            url: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=400&auto=format&fit=crop",
            stamp: "Just Now",
            size: "42.0 MB",
            prompt: currentPrompt
          };
          setAssets(prev => [newVid, ...prev]);
          triggerToast(`Holographic video render completed!`, "success");
        }

        setRenderingLogs(prev => [
          `[${new Date().toLocaleTimeString()}] Pipeline executed successfully. Safe-buffered state written.`,
          `[${new Date().toLocaleTimeString()}] Output mirrored to Imperal Vault on Google Drive via CloudSync-02.`,
          ...prev
        ]);
      } else {
        setGenerateProgress(currentProg);
        // Update the item in queue progress
        setRenderQueue(prev => prev.map(item => {
          if (item.id === queueId) {
            return { 
              ...item, 
              progress: currentProg, 
              status: "Generating", 
              timeRemaining: `${Math.ceil((100 - currentProg) / 20)}s` 
            };
          }
          return item;
        }));

        if (currentProg % 3 === 0) {
          setRenderingLogs(prev => [
            `[${new Date().toLocaleTimeString()}] Lattice Rendering Pipeline Core #${Math.floor(Math.random() * 18)} - ${currentProg}% Progress`,
            ...prev
          ]);
        }
      }
    }, durationMs / 10);
  };

  // Run pre-defined quick trigger automation cycles
  const executeAutomationShortcut = (name: string, count: number, outType: "image" | "video") => {
    simulateRender("batch", `Batch automation sequence: "${name}" [Qty: ${count}]`, 4000);
    triggerToast(`Batch dispatch initialized: ${count} pieces targeted.`, "info");
  };

  // Extract OCR SIMULATOR
  const runOcrExtractorSim = () => {
    setIsExtractingOcr(true);
    triggerToast("Initiating multi-tenant OCR byte-level OCR matrix...", "info");
    setTimeout(() => {
      setExtractedOcrText(OCR_SAMPLES[selectedOcrSample].text);
      setIsExtractingOcr(false);
      triggerToast("OCR extraction complete! Text populated.", "success");
    }, 1500);
  };

  return (
    <div className="w-full bg-[#03010b] text-white min-h-[90vh] transition-all relative overflow-hidden flex flex-col font-sans selection:bg-[#e840f3]/40 selection:text-white">
      {/* Dynamic background lighting vectors */}
      <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-purple-950/20 via-cyan-950/5 to-transparent pointer-events-none" />
      <div className="absolute top-1/4 right-0 w-[450px] h-[450px] bg-purple-600/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-[350px] h-[350px] bg-cyan-600/5 rounded-full blur-[120px] pointer-events-none" />

      {/* NexoraX Multiple Toast Banners */}
      <div className="fixed top-24 right-6 z-50 flex flex-col gap-3 max-w-sm pointer-events-none">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, x: 100, scale: 0.9 }}
              animate={{ opacity: 0.95, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 80, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 350, damping: 25 }}
              className="pointer-events-auto flex items-start gap-3.5 p-4 rounded-2xl bg-[#0a0518]/95 border border-purple-500/30 shadow-[0_0_20px_rgba(232,64,243,0.15)] backdrop-blur-2xl w-80 md:w-96 overflow-hidden relative group"
            >
              <div className={`p-2.5 rounded-xl text-black shrink-0 ${
                toast.type === "success" 
                  ? "bg-[#00e5ff] shadow-[0_0_10px_rgba(0,229,255,0.4)]" 
                  : toast.type === "completed" 
                    ? "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.4)] animate-pulse" 
                    : "bg-[#e840f3] shadow-[0_0_10px_rgba(232,64,243,0.4)]"
              }`}>
                {toast.type === "success" ? (
                  <CheckCircle size={15} />
                ) : toast.type === "completed" ? (
                  <CheckCircle size={15} />
                ) : (
                  <Sparkles size={15} />
                )}
              </div>
              
              <div className="flex-1 min-w-0 pr-4 text-left">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] font-mono tracking-widest uppercase text-zinc-400 font-bold">
                    {toast.type === "completed" ? "TELEMETRY COMPLETED" : "NEXORAX TELEMETRY"}
                  </span>
                  {toast.taskId && (
                    <span className="px-1.5 py-0.5 rounded bg-purple-950 border border-purple-500/30 text-[8px] font-mono text-purple-300 font-bold">
                      {toast.taskId}
                    </span>
                  )}
                </div>
                <p className="text-xs text-white font-medium mt-1 leading-relaxed">
                  {toast.text}
                </p>
              </div>

              <button
                onClick={() => {
                  setToasts(prev => prev.filter(t => t.id !== toast.id));
                }}
                className="absolute top-3 right-3 p-1 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-all"
              >
                <X size={11} />
              </button>
              
              {/* Glowing animated visual progress slidebar at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black/40">
                <motion.div 
                  initial={{ width: "100%" }}
                  animate={{ width: "0%" }}
                  transition={{ duration: 4.5, ease: "linear" }}
                  className={`h-full ${
                    toast.type === "success" 
                      ? "bg-[#00e5ff]" 
                      : toast.type === "completed" 
                        ? "bg-emerald-400" 
                        : "bg-[#e840f3]"
                  }`}
                />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* TOP HEADER BLOCK & SYSTEM BRANDING */}
      <div className="relative rounded-3xl border border-purple-500/25 bg-gradient-to-r from-purple-950/20 via-[#0c051a] to-cyan-950/20 p-6 md:p-8 mb-8 overflow-hidden shadow-[0_0_50px_rgba(168,85,247,0.12)] border-t-2 border-t-purple-500/40">
        <div className="absolute inset-0 bg-[radial-gradient(#9d4edd_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 text-left">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 font-mono text-[9px] font-black uppercase tracking-widest text-[#00e5ff]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] animate-ping"></span>
              <span className="bg-purple-950/60 text-purple-300 border border-purple-500/30 px-2 py-0.5 rounded text-[8px] font-bold">NODE_09.2 CREATIVE ENGINE</span>
              <span>Ultra-Secure Asset Synthesis</span>
            </div>

            <h1 className="text-3xl md:text-4.5xl font-sans font-black tracking-tight text-white uppercase leading-none">
              NEXORAX <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-[#00e5ff]">Media Generation Studio</span>
            </h1>

            <p className="text-zinc-350 font-sans text-xs md:text-sm leading-relaxed max-w-2xl font-medium">
              Create. Transform. Animate. Automate. Power your programmatic multi-channel pipelines with direct rendering triggers, smart optical character recognition engines, and high-contrast neural animations.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 shrink-0 justify-end w-full md:w-auto">
            {/* NexoraX Membership Access Tier */}
            <div className="bg-[#0b061b]/90 border-2 border-purple-500/40 p-4 rounded-2xl min-w-[245px] shadow-[0_0_15px_rgba(168,85,247,0.1)] text-left">
              <div className="flex items-center gap-1.5 justify-between">
                <span className="text-[8px] font-mono text-[#00e5ff] uppercase tracking-widest font-black">Membership Access Tier</span>
                <span className="text-[8px] font-mono px-1.5 bg-purple-950 text-purple-300 rounded border border-purple-500/30 font-bold uppercase">SC887095</span>
              </div>
              <div className="mt-2 text-left">
                <select
                  value={userTier}
                  onChange={(e) => {
                    setUserTier(e.target.value);
                    triggerToast(`Access privileges calibrated to ${e.target.value} Account Tier.`, "info");
                    // Reset panel if it becomes unauthorized
                    const isEnterpriseTier = ["Diamond", "Black Diamond", "VIP Singularity", "Admin Override"].includes(e.target.value);
                    if (!isEnterpriseTier && [
                      "Governance", "Risk Management", "Digital Twin", "Enterprise Portfolio",
                      "Autonomous Control", "Strategic Roadmap", "Recovery Center", "Threat Intelligence"
                    ].includes(activePanel)) {
                      setActivePanel("Creative Dashboard");
                    }
                  }}
                  className="w-full bg-black/90 border border-purple-500/30 text-white font-mono text-xs p-2 rounded-xl focus:outline-none focus:border-[#00e5ff] cursor-pointer"
                >
                  <option value="Bronze">🥉 Bronze Membership</option>
                  <option value="Silver">🥈 Silver Membership</option>
                  <option value="Gold">🥇 Gold Membership</option>
                  <option value="Diamond">💎 Diamond Tier (Enterprise)</option>
                  <option value="Black Diamond">🖤 Black Diamond Tier (Enterprise)</option>
                  <option value="VIP Singularity">✨ VIP Singularity (Enterprise)</option>
                  <option value="Admin Override">🔑 NexoraX Admin Override</option>
                </select>
              </div>
              <span className={`text-[7.5px] font-mono block mt-2 text-right uppercase font-bold ${
                ["Diamond", "Black Diamond", "VIP Singularity", "Admin Override"].includes(userTier)
                  ? "text-[#00e5ff] animate-pulse"
                  : "text-zinc-500"
              }`}>
                {["Diamond", "Black Diamond", "VIP Singularity", "Admin Override"].includes(userTier) 
                  ? "✓ ENTERPRISE NODES UNLOCKED" 
                  : "🔒 HOVER / CHOOSE DIAMOND+ FOR ENTERPRISE"}
              </span>
            </div>

            {/* Operational Priority */}
            <div className="bg-black/60 border border-purple-500/20 p-4 rounded-2xl min-w-[180px] text-center md:text-right flex flex-col justify-center">
              <span className="text-[7.5px] font-mono text-purple-400 uppercase tracking-widest block">OPERATIONAL PRIORITY</span>
              <span className="text-2xl font-black font-mono text-white mt-1">VEO-GRID v9</span>
              <span className="text-[8.5px] font-mono text-emerald-400 block tracking-wider uppercase font-bold mt-1">100% NexoraX billing records SYNCED</span>
            </div>
          </div>
        </div>
      </div>

      {/* TOP KPI PANEL: CREATIVE OUTPUT PER CYCLE */}
      <div className="mb-8">
        <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3 block text-left">
          ★ CREATIVE OUTPUT PER CYCLE
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {[
            { label: "Images Generated", value: "120", icon: <ImageIcon size={14} className="text-[#00e5ff]" />, color: "border-[#00e5ff]/20" },
            { label: "Videos Generated", value: "40", icon: <Video size={14} className="text-purple-400" />, color: "border-purple-500/20" },
            { label: "Image-to-Video", value: "20", icon: <Tv size={14} className="text-pink-400" />, color: "border-pink-500/20" },
            { label: "Video-to-Video", value: "15", icon: <Layers size={14} className="text-cyan-400" />, color: "border-cyan-500/20" },
            { label: "OCR Jobs Executed", value: "35", icon: <FileText size={14} className="text-blue-400" />, color: "border-blue-500/20" },
            { label: "Active Projects", value: "18", icon: <Boxes size={14} className="text-amber-400" />, color: "border-amber-500/20" },
            { label: "Render Queue", value: "9 Active", icon: <Clock size={14} className="text-red-400 animate-pulse" />, color: "border-red-500/20" },
            { label: "Completed Today", value: "27", icon: <CheckCircle size={14} className="text-emerald-400" />, color: "border-emerald-500/20" }
          ].map((stat, idx) => (
            <div key={idx} className={`bg-[#0c061a]/90 border ${stat.color} p-4 rounded-2xl flex flex-col justify-between hover:scale-[1.02] hover:border-purple-500/40 transition-all shadow-[0_0_15px_rgba(0,0,0,0.5)]`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-400 block max-w-[80px] break-words text-left">{stat.label}</span>
                {stat.icon}
              </div>
              <span className="text-xl md:text-2xl font-black font-mono tracking-tight text-white block text-left">{stat.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* INTERACTIVE WORKFLOW STRIP */}
      <div className="w-full bg-[#120f26]/30 border border-purple-500/15 rounded-2xl p-4 mb-8 shadow-inner backdrop-blur-sm">
        <span className="text-[8px] font-mono text-purple-400 block text-left uppercase tracking-widest font-black mb-3">
          AUTOMATED CREATIVE CYCLE WORKFLOW
        </span>
        <div className="flex flex-col sm:flex-row justify-between items-stretch gap-2 font-mono text-[10px]">
          {[
            { step: "Prompt", desc: "Neuro-Text Plan", num: "01", activeColor: "from-blue-500 to-cyan-500" },
            { step: "Generate", desc: "Compute Vector", num: "02", activeColor: "from-cyan-500 to-teal-500" },
            { step: "Transform", desc: "Apply Presets", num: "03", activeColor: "from-teal-500 to-purple-500" },
            { step: "Animate", desc: "Render Motions", num: "04", activeColor: "from-purple-500 to-pink-500" },
            { step: "Render", desc: "Compile Buffer", num: "05", activeColor: "from-pink-500 to-rose-500" },
            { step: "Export", desc: "Container Wrap", num: "06", activeColor: "from-rose-500 to-[#e840f3]" },
            { step: "Publish", desc: "Automator Send", num: "07", activeColor: "from-[#e840f3] to-emerald-500" }
          ].map((item, idx) => (
            <div key={idx} className="flex-1 bg-black/45 border border-white/5 rounded-xl p-3 flex items-center justify-between group hover:border-[#00e5ff]/35 transition-all text-left">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="bg-purple-950 text-purple-300 font-bold border border-purple-500/30 text-[8px] px-1 rounded">{item.num}</span>
                  <span className="font-bold text-white uppercase text-[10px] group-hover:text-[#00e5ff] transition-all">{item.step}</span>
                </div>
                <span className="text-[8.5px] text-zinc-500 block">{item.desc}</span>
              </div>
              <ChevronRight size={14} className="text-zinc-600 group-hover:text-[#00e5ff] transition-all shrink-0" />
            </div>
          ))}
        </div>
      </div>

      {/* MAIN TWO-COLUMN LAYOUT: WORKSPACE PANELS */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start mb-8">
        
        {/* LEFT COLUMN: 15 PANEL SELECTOR */}
        <div className="lg:col-span-1 bg-[#090515]/95 border border-purple-500/20 rounded-3xl p-4 shadow-[0_0_30px_rgba(0,0,0,0.6)] backdrop-blur-xl">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#00e5ff] font-bold">STUDIO WORKSPACE PANELS</span>
            <span className="bg-purple-950 text-purple-300 px-2 py-0.5 rounded text-[8px] font-mono border border-purple-500/30">16 SYSTEMS</span>
          </div>

          <div className="space-y-1.5">
            {[
              { id: "Creative Dashboard", label: "1. Creative Dashboard", icon: <LayoutGrid size={13} />, badge: "Active" },
              { id: "Client Campaign Workspace", label: "★ Client Campaign Workspace", icon: <Users size={13} />, badge: "SaaS Portal" },
              { id: "AI Creative Director", label: "★ AI Creative Director", icon: <Sparkles size={13} />, badge: "DIRECTOR" },
              { id: "AI Ad Factory & UGC Creator Studio", label: "🔥 AI Ad & UGC Factory", icon: <Zap size={13} />, badge: "PREMIUM" },
              { id: "Storyboard Dashboard", label: "2. Storyboard Dashboard", icon: <Workflow size={13} />, badge: `${storyboardShots.length} Scenes` },
              { id: "Character Library", label: "3. Character Library", icon: <Users size={13} />, badge: `${characterRegistry.length} Personas` },
              { id: "Variation Lab", label: "4. Variation Lab", icon: <Sliders size={13} />, badge: "A/B" },
              { id: "Prompt Planner", label: "5. Prompt Planner", icon: <SlidersHorizontal size={13} />, badge: "Smart" },
              { id: "AI Image Generator", label: "6. AI Image Generator", icon: <Wand2 size={13} />, badge: "SD-v9" },
              { id: "Image Transformer", label: "7. Image Transformer", icon: <Sliders size={13} />, badge: "Img2Img" },
              { id: "Image to Text / OCR", label: "8. Image to Text / OCR", icon: <FileSearch size={13} />, badge: "OCR" },
              { id: "AI Video Generator", label: "9. AI Video Generator", icon: <Video size={13} />, badge: "Veo-Deep" },
              { id: "Image to Video", label: "10. Image to Video", icon: <Tv size={13} />, badge: "Animate" },
              { id: "Video Transformer", label: "11. Video Transformer", icon: <Scissors size={13} />, badge: "Vid2Vid" },
              { id: "Automation Pipeline", label: "12. Automation Pipeline", icon: <Workflow size={13} />, badge: "Batch" },
              { id: "Render Queue", label: "13. Render Queue", icon: <Activity size={13} />, badge: `${renderQueue.filter(q => q.status === "Rendering").length} Queue` },
              { id: "Asset Library", label: "14. Asset Library", icon: <Archive size={13} />, badge: `${assets.length} items` },
              { id: "Brand Voice & Identity", label: "15. Brand Voice & visual", icon: <Bookmark size={13} />, badge: "NexoraX" },
              { id: "Publishing & Export", label: "16. Publishing & Export", icon: <Share2 size={13} />, badge: "Push" },
              { id: "Creative Analytics", label: "17. Creative Analytics", icon: <TrendingUp size={13} />, badge: "+24.5%" },
              { id: "Template Marketplace", label: "18. Template Marketplace", icon: <Sparkles size={13} />, badge: "Store" }
            ].map((panel) => {
              const isSelected = activePanel === panel.id;
              return (
                <button
                  key={panel.id}
                  onClick={() => {
                    setActivePanel(panel.id);
                  }}
                  className={`w-full flex items-center justify-between p-2.5 rounded-xl text-[10.5px] uppercase tracking-wide font-medium transition-all select-none cursor-pointer border ${
                    isSelected
                      ? "bg-gradient-to-r from-purple-950/40 to-cyan-950/40 border-purple-500/40 text-white font-black shadow-[0_0_15px_rgba(168,85,247,0.15)]"
                      : "bg-black/20 border-transparent text-zinc-400 hover:bg-white/5 hover:text-white hover:border-white/5"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className={isSelected ? "text-[#00e5ff]" : "text-zinc-500"}>
                      {panel.icon}
                    </span>
                    <span>{panel.label}</span>
                  </div>
                  <span className={`text-[7.5px] font-mono px-1.5 py-0.5 rounded border leading-none ${
                    isSelected 
                      ? "bg-[#00e5ff]/20 border-[#00e5ff]/35 text-[#00e5ff]" 
                      : "bg-black border-white/5 text-zinc-500"
                  }`}>
                    {panel.badge}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Conditional Advanced Enterprise Panels Section */}
          {["Diamond", "Black Diamond", "VIP Singularity", "Admin Override"].includes(userTier) ? (
            <div className="mt-4 pt-4 border-t border-purple-500/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[9px] font-mono uppercase tracking-widest text-purple-400 font-black">★ ENTERPRISE RESOLUTION</span>
                <span className="text-[7px] font-mono px-1 py-0.5 rounded bg-[#00e5ff]/20 text-[#00e5ff] border border-[#00e5ff]/30 font-bold uppercase animate-pulse">DIAMOND+</span>
              </div>
              <div className="space-y-1.5 text-left">
                {[
                  { id: "Governance", label: "[E1] Governance", icon: <Briefcase size={11} />, badge: "Policy" },
                  { id: "Risk Management", label: "[E2] Risk Management", icon: <ShieldAlert size={11} />, badge: "Defense" },
                  { id: "Digital Twin", label: "[E3] Digital Twin", icon: <Compass size={11} />, badge: "Virtual" },
                  { id: "Enterprise Portfolio", label: "[E4] Enterprise Portfolio", icon: <Database size={11} />, badge: "Holdings" },
                  { id: "Autonomous Control", label: "[E5] Autonomous Control", icon: <Binary size={11} />, badge: "Standard" },
                  { id: "Strategic Roadmap", label: "[E6] Strategic Roadmap", icon: <Workflow size={11} />, badge: "Resource Map" },
                  { id: "Recovery Center", label: "[E7] Recovery Center", icon: <RefreshCcw size={11} />, badge: "Reboot" },
                  { id: "Threat Intelligence", label: "[E8] Threat Intelligence", icon: <FileWarning size={11} />, badge: "Threats" }
                ].map((panel) => {
                  const isSelected = activePanel === panel.id;
                  return (
                    <button
                      key={panel.id}
                      onClick={() => {
                        setActivePanel(panel.id);
                      }}
                      className={`w-full flex items-center justify-between p-2 rounded-xl text-[10px] uppercase tracking-wide font-black transition-all select-none cursor-pointer border ${
                        isSelected
                          ? "bg-gradient-to-r from-cyan-950/40 to-purple-950/40 border-[#00e5ff]/40 text-white shadow-[0_0_15px_rgba(0,229,255,0.15)]"
                          : "bg-black/25 border-transparent text-purple-300 hover:bg-purple-950/30 hover:text-white hover:border-purple-500/20"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={isSelected ? "text-[#00e5ff]" : "text-purple-400"}>
                          {panel.icon}
                        </span>
                        <span>{panel.label}</span>
                      </div>
                      <span className={`text-[7px] font-mono px-1 py-0.5 rounded border leading-none ${
                        isSelected 
                          ? "bg-[#00e5ff]/20 border-[#00e5ff]/35 text-[#00e5ff]" 
                          : "bg-black border-purple-500/25 text-purple-400"
                      }`}>
                        {panel.badge}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mt-4 p-3.5 rounded-2xl bg-gradient-to-br from-[#120a2a]/60 to-[#04020a]/80 border border-purple-500/20 text-left relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping inline-block"></span>
              </div>
              <span className="text-[8px] font-mono font-black text-amber-400 block tracking-wider mb-1">🔒 8 ENTERPRISE NODES HIDDEN</span>
              <span className="text-[9px] text-zinc-400 block leading-relaxed mb-2.5">
                Governance, Risk, Twin Simulator, Portfolio, Autonomous, Roadmap, Recovery & Threat Intelligence are reserved for Diamond+ levels.
              </span>
              <button
                onClick={() => {
                  setUserTier("Diamond");
                  triggerToast("Upgraded successfully! Enterprise capabilities enabled.", "success");
                }}
                className="w-full py-1.5 bg-gradient-to-r from-purple-500 to-[#00e5ff] hover:opacity-95 text-black font-mono font-black text-[8px] uppercase tracking-widest rounded-lg transition-all"
              >
                Elevate to Diamond Tier
              </button>
            </div>
          )}

          {/* Quick Automation Presets Panel inside Selector Column */}
          <div className="mt-6 pt-4 border-t border-white/5">
            <span className="text-[8px] font-mono text-purple-400 uppercase tracking-widest block mb-2 text-left">
              SHORTCUT AUTOMATORS
            </span>
            <div className="space-y-1.5">
              {[
                { name: "Generate 10 Images", qty: 10, type: "image", desc: "Batch topic variations" },
                { name: "Generate 10 YouTube Shorts", qty: 10, type: "video", desc: "Programmatic short video bundle" },
                { name: "Generate 10 TikToks", qty: 10, type: "video", desc: "Optimized trend structures" },
                { name: "Generate 10 Instagram Reels", qty: 10, type: "video", desc: "Holographic format conversion" }
              ].map((act, idx) => (
                <button
                  key={idx}
                  onClick={() => executeAutomationShortcut(act.name, act.qty, act.type as any)}
                  disabled={isGenerating}
                  className="w-full text-left p-2 rounded-xl bg-black/40 border border-white/5 hover:border-purple-500/30 transition-all text-zinc-300 group cursor-pointer disabled:opacity-50"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[9px] font-bold text-white group-hover:text-[#00e5ff] uppercase tracking-wide leading-none">{act.name}</span>
                    <Zap size={9} className="text-yellow-400" />
                  </div>
                  <span className="text-[8px] text-zinc-500 block leading-tight">{act.desc}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* CENTER MAIN WORKSPACE PANEL - DINAMICALLY RENDERED */}
        <div className="lg:col-span-3 space-y-6">
          
          <AnimatePresence mode="wait">
            <motion.div
              key={activePanel}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className="bg-[#0c061a]/85 border border-purple-500/15 rounded-3xl p-6 shadow-[0_0_40px_rgba(0,0,0,0.5)] backdrop-blur-xl relative"
            >
              {/* TOP STRIP OF PANEL */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-white/5">
                <div>
                  <span className="text-[8px] font-mono text-[#00e5ff] uppercase tracking-widest block text-left">ACTIVE WORKSPACE PANEL</span>
                  <h3 className="text-xl font-bold uppercase text-white font-sans tracking-tight mt-0.5 text-left">
                    {activePanel}
                  </h3>
                </div>
                
                {/* VEO-ENGINE HEALTH INDICATORS */}
                <div className="flex flex-wrap items-center gap-4 text-xs font-mono select-none">
                  <div className="flex items-center gap-1.5 text-zinc-400 bg-black/40 p-1.5 px-3 rounded-full border border-white/5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mt-0.5" />
                    <span>Lattice 18 Active</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-zinc-400 bg-black/40 p-1.5 px-3 rounded-full border border-white/5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] mt-0.5" />
                    <span>0.8s Telemetry OK</span>
                  </div>
                </div>
              </div>

              {/* DYNAMIC COMPONENT LOADER SWITCH BOARD */}
              
              {/* 1. CREATIVE DASHBOARD */}
              {(activePanel === "Creative Dashboard" || activePanel === "Creation Dashboard") && (
                <div className="space-y-6 text-left">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-widest">★ CREATIVE OPERATIONS OVERVIEW</h4>
                      <p className="text-zinc-400 text-xs leading-relaxed">
                        Welcome to the NexoraX creative matrix portal. This module allows multi-agent content planning, instant text-to-graphics synthesis, b-roll footage rendering, and dynamic OCR data harvests. Everything rendered here can instantly push directly to the Social Media Automation queue.
                      </p>

                      <div className="p-4 rounded-2xl bg-black/50 border border-white/5 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono font-bold text-white">ACTIVE VEOGRAPHS</span>
                          <span className="text-[9px] font-mono text-[#00e5ff] bg-[#00e5ff]/15 px-2 py-0.5 rounded border border-[#00e5ff]/20">99.9% Uptime</span>
                        </div>
                        <div className="space-y-2 text-[10.5px]">
                          <div className="flex justify-between border-b border-white/5 pb-1">
                            <span className="text-zinc-400">Total Video Compute Log</span>
                            <span className="text-white font-mono">40 renders (Completed)</span>
                          </div>
                          <div className="flex justify-between border-b border-white/5 pb-1">
                            <span className="text-zinc-400">German Bare-Metal Cluster Node</span>
                            <span className="text-[#00e5ff] font-mono font-bold">Hetzner Container [Munich-01]</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Secure Audit Signature</span>
                            <span className="text-amber-400 font-mono text-[9.5px]">SC887095-NODE-09.2-VEO</span>
                          </div>
                        </div>
                      </div>

                      <div className="pt-2">
                        <button
                          onClick={() => {
                            setActivePanel("AI Image Generator");
                            triggerToast("Redirecting system load context to Text to Image matrix.", "info");
                          }}
                          className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-[#00e5ff] hover:opacity-95 text-black font-sans font-black text-[10px] uppercase tracking-widest rounded-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer select-none inline-flex items-center gap-2"
                        >
                          <span>Sart Generation Matrix</span>
                          <ArrowUpRight size={13} />
                        </button>
                      </div>
                    </div>

                    {/* Quick Simulation Preview box */}
                    <div className="space-y-4">
                      <h4 className="text-xs font-mono font-bold text-[#00e5ff] uppercase tracking-widest">★ LIVE GRAPHIC/VIDEO STATUS PREVIEW</h4>
                      <div className="relative rounded-2xl border border-white/10 bg-black/60 p-4 min-h-[220px] flex flex-col justify-between overflow-hidden">
                        <div className="absolute top-2 right-2 flex items-center gap-1.5 bg-black/70 p-1 px-2.5 rounded-full border border-white/10 text-[9px] font-mono">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] animate-pulse" />
                          <span>Rendering Live</span>
                        </div>

                        <div className="flex-1 flex flex-col items-center justify-center space-y-3 py-6">
                          {isGenerating ? (
                            <div className="w-full max-w-[200px] text-center space-y-2">
                              <RefreshCw size={26} className="text-[#e840f3] animate-spin mx-auto" />
                              <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
                                <div className="bg-[#e840f3] h-full" style={{ width: `${generateProgress}%` }} />
                              </div>
                              <span className="text-[10px] font-mono text-[#e840f3] tracking-widest">{generateProgress}% RENDERING</span>
                            </div>
                          ) : (
                            <div className="text-center space-y-2">
                              <ImageIcon size={35} className="text-purple-400/65 mx-auto" />
                              <span className="text-xs font-sans text-zinc-400 block max-w-xs">All prior 120 Images & 40 Videos compiled safely to local cache. No active processor drag.</span>
                            </div>
                          )}
                        </div>

                        <div className="border-t border-white/5 pt-2 text-[10px] font-mono flex justify-between items-center text-zinc-500">
                          <span>Output File: out_v9_2.mp4</span>
                          <span>Codec: VP9 High-Profile</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Operational Flow Quick Guide */}
                  <div className="p-4 rounded-2xl bg-purple-950/20 border border-purple-500/20">
                    <h5 className="text-[10px] font-mono font-bold text-white mb-2 uppercase tracking-wide">★ INTER-NODE COUPLING INSTRUCTION</h5>
                    <p className="text-zinc-450 text-[11px] leading-relaxed">
                      All assets generated inside this workspace carries an integration tag: <strong className="text-white">"NEX_ASSET"</strong>. Use the <strong className="text-white">Publishing & Export panel (Tab 13)</strong> to send finished videos directly into the <strong className="text-purple-300">Social Media Automation</strong> pipeline for direct-to-channel scheduling with zero manual downloads.
                    </p>
                  </div>
                </div>
              )}

              {/* CLIENT CAMPAIGN WORKSPACE PORTAL */}
              {activePanel === "Client Campaign Workspace" && (
                <ClientCampaignWorkspace triggerToast={triggerToast} userTier={userTier} />
              )}

              {/* AI CREATIVE DIRECTOR & BRAND UNIVERSE ENGINE */}
              {activePanel === "AI Creative Director" && (
                <div className="space-y-6 text-left">
                  {/* MAIN HEADER */}
                  <div className="relative p-6 rounded-3xl bg-gradient-to-r from-purple-950/60 to-cyan-950/50 border border-purple-500/30 overflow-hidden shadow-[inset_0_2px_20px_rgba(168,85,247,0.1)]">
                    <div className="absolute top-0 right-0 p-3 flex gap-2">
                      <span className="text-[8px] font-mono bg-purple-900/40 text-[#00e5ff] px-2 py-0.5 rounded-full border border-purple-500/35 uppercase font-bold animate-pulse">CREATIVE OS v9.2</span>
                      <span className="text-[8px] font-mono bg-[#00e5ff]/20 text-[#00e5ff] px-2 py-0.5 rounded-full border border-[#00e5ff]/30 uppercase font-black">NEXORAX CORE</span>
                    </div>

                    <div className="max-w-2xl space-y-2">
                      <span className="text-[8.5px] font-mono text-[#00e5ff] uppercase tracking-widest font-black block">NEXORAX CREATIVE COMMAND NODE</span>
                      <h3 className="text-xl md:text-2xl font-sans font-black text-white tracking-tight leading-none">
                        AI Creative Director
                      </h3>
                      <p className="text-xs font-mono font-bold text-purple-300">
                        Brand Universe, Campaign Storytelling & Creative Automation
                      </p>
                      <p className="text-zinc-400 text-[11px] md:text-xs leading-relaxed pt-1">
                        Create complete visual campaigns with consistent brand identity, characters, scenes, styles, scripts, videos, thumbnails, ads, and social content from one intelligent creative command center.
                      </p>
                    </div>
                  </div>

                  {/* TOP WORKFLOW PROGRESS */}
                  <div className="bg-[#0f0920]/80 border border-purple-500/15 p-4 rounded-2xl">
                    <span className="text-[8.5px] font-mono text-purple-400 uppercase tracking-widest block mb-2.5 font-bold">NEXORAX GRAPHICS TRANSITION FLOW</span>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                      {[
                        { step: 1, label: "Brand Brief", active: true },
                        { step: 2, label: "Creative Dir", active: true },
                        { step: 3, label: "Storyboard", active: true },
                        { step: 4, label: "Generate Assets", active: isPipelineRunning && pipelineStep >= 3 },
                        { step: 5, label: "Render Videos", active: isPipelineRunning && pipelineStep >= 4 },
                        { step: 6, label: "Create Var", active: isPipelineRunning && pipelineStep >= 5 },
                        { step: 7, label: "Publish", active: isPipelineRunning && pipelineStep >= 9 },
                        { step: 8, label: "Learn", active: isPipelineRunning && pipelineStep >= 11 }
                      ].map((item, idx) => (
                        <div 
                          key={idx} 
                          className={`p-2.5 rounded-xl border transition-all text-center ${
                            item.active 
                              ? "bg-gradient-to-b from-purple-950/40 to-cyan-950/20 border-[#00e5ff]/40 text-white shadow-[0_0_10px_rgba(0,229,255,0.05)]"
                              : "bg-black/35 border-white/5 text-zinc-500"
                          }`}
                        >
                          <span className="text-[9px] font-mono block text-zinc-500">Node-0{item.step}</span>
                          <span className={`text-[10px] font-mono font-black uppercase tracking-wider block mt-1 ${item.active ? "text-[#00e5ff]" : ""}`}>
                            {item.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* TOP KPI PANEL */}
                  <div className="bg-[#080512]/90 border-2 border-purple-500/25 p-5 rounded-3xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] animate-ping inline-block mr-2" />
                    </div>
                    <span className="text-[9px] font-mono text-purple-400 uppercase tracking-widest block mb-4 font-black">★ CREATIVE UNIVERSE INTELLIGENCE</span>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                      {[
                        { val: "6", lbl: "Active Universes" },
                        { val: "42", lbl: "Campaign Concepts" },
                        { val: "380", lbl: "Assets Produced" },
                        { val: "96", lbl: "Video Variations" },
                        { val: "97%", lbl: "Brand Consistency" },
                        { val: "84.2K", lbl: "Predicted Eng" },
                        { val: "Futuristic Demo", lbl: "Best Style" }
                      ].map((kpi, idx) => (
                        <div key={idx} className="bg-black/40 border border-white/5 p-3 rounded-xl hover:border-purple-500/20 transition-all">
                          <span className="text-[14px] font-black font-mono text-white block tracking-tight leading-none">
                            {kpi.val}
                          </span>
                          <span className="text-[8px] text-zinc-400 uppercase font-mono block mt-2 tracking-wider leading-none">
                            {kpi.lbl}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* MAIN BENTO INTERACTIVE WORKSPACE */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* 1. BRAND UNIVERSE BUILDER */}
                    <div className="bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4 relative overflow-hidden backdrop-blur-md">
                      <div className="flex justify-between items-center pb-2 border-b border-white/5">
                        <div className="flex items-center gap-2">
                          <span className="text-purple-400"><Bookmark size={14} /></span>
                          <span className="text-[11px] font-mono text-white font-black uppercase tracking-widest">1. Brand Universe Builder</span>
                        </div>
                        <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-purple-950 text-purple-300 border border-purple-500/30 font-black">STABLE</span>
                      </div>
                      
                      <div className="space-y-3.5 text-xs">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="text-[9px] font-mono text-zinc-450 uppercase font-bold block">Brand Name</label>
                            <input 
                              type="text" 
                              value={brandUniverse.brandName} 
                              onChange={(e) => setBrandUniverse({ ...brandUniverse, brandName: e.target.value })}
                              className="w-full bg-black/85 border border-white/10 p-2 rounded-xl text-white font-mono text-xs focus:outline-none focus:border-purple-400"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[9px] font-mono text-zinc-455 uppercase font-bold block">Logo assets (Upload Simulation)</label>
                            <div className="flex gap-2">
                              <span className="text-[10px] text-purple-400 bg-purple-950/20 border border-purple-500/25 rounded-xl p-2 font-mono flex-1 text-center truncate">
                                {brandUniverse.logoFileText}
                              </span>
                              <button 
                                onClick={() => {
                                  setBrandUniverse({ ...brandUniverse, logoFileText: `nexorax_core_shield_${Math.floor(Math.random()*100)}.png (310 KB)` });
                                  triggerToast("New brand visual identity logo uploaded to vault.", "success");
                                }}
                                className="px-3 bg-purple-900/60 hover:bg-purple-900 border border-purple-500/20 text-[#00e5ff] font-mono font-bold rounded-xl text-[9px]"
                              >
                                Upload
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="space-y-1 text-left">
                            <label className="text-[9px] font-mono text-zinc-400 block uppercase">Primary Color</label>
                            <div className="flex gap-1.5 items-center bg-black/60 p-1.5 border border-white/10 rounded-xl">
                              <input 
                                type="color" 
                                value={brandUniverse.primaryColor} 
                                onChange={(e) => setBrandUniverse({ ...brandUniverse, primaryColor: e.target.value })}
                                className="w-6 h-6 rounded-md cursor-pointer border-0 bg-transparent flex-shrink-0"
                              />
                              <span className="text-[9.5px] font-mono text-white tracking-widest">{brandUniverse.primaryColor}</span>
                            </div>
                          </div>
                          <div className="space-y-1 text-left">
                            <label className="text-[9px] font-mono text-zinc-400 block uppercase">Secondary Color</label>
                            <div className="flex gap-1.5 items-center bg-black/60 p-1.5 border border-white/10 rounded-xl">
                              <input 
                                type="color" 
                                value={brandUniverse.secondaryColor} 
                                onChange={(e) => setBrandUniverse({ ...brandUniverse, secondaryColor: e.target.value })}
                                className="w-6 h-6 rounded-md cursor-pointer border-0 bg-transparent flex-shrink-0"
                              />
                              <span className="text-[9.5px] font-mono text-white tracking-widest">{brandUniverse.secondaryColor}</span>
                            </div>
                          </div>
                          <div className="space-y-1 text-left">
                            <label className="text-[9px] font-mono text-zinc-400 block uppercase">Tertiary Color</label>
                            <div className="flex gap-1.5 items-center bg-[#05030e] p-1.5 border border-white/10 rounded-xl">
                              <input 
                                type="color" 
                                value={brandUniverse.tertiaryColor} 
                                onChange={(e) => setBrandUniverse({ ...brandUniverse, tertiaryColor: e.target.value })}
                                className="w-6 h-6 rounded-md cursor-pointer border-0 bg-transparent flex-shrink-0"
                              />
                              <span className="text-[9.5px] font-mono text-white tracking-widest">{brandUniverse.tertiaryColor}</span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-1 text-left">
                          <label className="text-[9px] font-mono text-zinc-400 block uppercase">Typography System Selection</label>
                          <select 
                            value={brandUniverse.typography} 
                            onChange={(e) => setBrandUniverse({ ...brandUniverse, typography: e.target.value })}
                            className="w-full bg-black/85 border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none cursor-pointer"
                          >
                            <option value="Space Grotesk - Tech Futuristic">Space Grotesk - Futuristic Sans (Lead)</option>
                            <option value="Inter - Swiss Clean Professional">Inter - Swiss Clean Professional (SaaS)</option>
                            <option value="JetBrains Mono - Raw Technical Data">JetBrains Mono - Raw Technical (Architect)</option>
                          </select>
                        </div>

                        <div className="space-y-1">
                          <label className="text-[9px] font-mono text-zinc-440 block uppercase">Visual Style Theme Description</label>
                          <input 
                            type="text" 
                            value={brandUniverse.visualStyle} 
                            onChange={(e) => setBrandUniverse({ ...brandUniverse, visualStyle: e.target.value })}
                            className="w-full bg-black/80 border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[9px] font-mono text-zinc-440 block uppercase">Tone of Voice Parameters</label>
                          <input 
                            type="text" 
                            value={brandUniverse.toneOfVoice} 
                            onChange={(e) => setBrandUniverse({ ...brandUniverse, toneOfVoice: e.target.value })}
                            className="w-full bg-black/80 border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none"
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono text-zinc-400 block uppercase">Product Clusters</label>
                            <textarea 
                              rows={2}
                              value={brandUniverse.productCategories} 
                              onChange={(e) => setBrandUniverse({ ...brandUniverse, productCategories: e.target.value })}
                              className="w-full bg-black/80 border border-white/10 p-2 rounded-xl text-[10.5px] font-mono text-white focus:outline-none resize-none"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono text-zinc-400 block uppercase">Audience Profile Target</label>
                            <textarea 
                              rows={2}
                              value={brandUniverse.audienceProfile} 
                              onChange={(e) => setBrandUniverse({ ...brandUniverse, audienceProfile: e.target.value })}
                              className="w-full bg-black/80 border border-white/10 p-2 rounded-xl text-[10.5px] font-mono text-white focus:outline-none resize-none"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#0a0515]/60 p-3 rounded-2xl border border-purple-500/10">
                          <div className="space-y-1">
                            <span className="text-[8.5px] font-mono font-black text-emerald-400 block uppercase">✓ DO RULES</span>
                            <textarea 
                              rows={2}
                              value={brandUniverse.doRules} 
                              onChange={(e) => setBrandUniverse({ ...brandUniverse, doRules: e.target.value })}
                              className="w-full bg-black/90 border border-white/5 p-1.5 rounded-lg text-[9.5px] font-mono text-emerald-300 focus:outline-none resize-none"
                            />
                          </div>
                          <div className="space-y-1">
                            <span className="text-[8.5px] font-mono font-black text-red-400 block uppercase">✗ DON'T RULES</span>
                            <textarea 
                              rows={2}
                              value={brandUniverse.dontRules} 
                              onChange={(e) => setBrandUniverse({ ...brandUniverse, dontRules: e.target.value })}
                              className="w-full bg-black/90 border border-white/5 p-1.5 rounded-lg text-[9.5px] font-mono text-red-300 focus:outline-none resize-none"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label className="text-[9px] font-mono text-zinc-400 block uppercase">CTA Anchor Protocol rules</label>
                          <input 
                            type="text" 
                            value={brandUniverse.ctaRules} 
                            onChange={(e) => setBrandUniverse({ ...brandUniverse, ctaRules: e.target.value })}
                            className="w-full bg-black/80 border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none"
                          />
                        </div>

                        <button 
                          onClick={() => {
                            triggerToast(`Brand Universe for "${brandUniverse.brandName}" saved and replicated in NexoraX Resource Library!`, "success");
                            setRenderingLogs(logs => [
                              `[${new Date().toLocaleTimeString()}] REPLICATION SUCCESS: Brand configuration saved to [Munich-Vault-SC887095]`,
                              `[${new Date().toLocaleTimeString()}] Target: ${brandUniverse.brandName}`,
                              ...logs
                            ]);
                          }}
                          className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all select-none cursor-pointer text-center block"
                        >
                          Create Brand Universe
                        </button>
                      </div>
                    </div>

                    {/* 2. AI CREATIVE DIRECTOR */}
                    <div className="bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4 relative overflow-hidden backdrop-blur-md flex flex-col justify-between">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center pb-2 border-b border-white/5">
                          <div className="flex items-center gap-2">
                            <span className="text-cyan-400"><Wand2 size={14} /></span>
                            <span className="text-[11px] font-mono text-white font-black uppercase tracking-widest">2. AI Creative Director Engine</span>
                          </div>
                          <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-cyan-950 text-cyan-300 border border-cyan-500/30 font-black">NEURAL</span>
                        </div>

                        <p className="text-zinc-400 text-[10.5px]">
                          Enter your campaign objectives below. Our neural creative model will synthesize high-velocity conceptual angles, hooks, and localized narrative formats matching the Resource £150,000 target.
                        </p>

                        <div className="space-y-3.5 text-xs">
                          <div className="space-y-1.5">
                            <label className="text-[9px] font-mono text-zinc-400 uppercase font-bold block text-left">Campaign Objective</label>
                            <input 
                              type="text" 
                              value={campaignObj} 
                              onChange={(e) => setCampaignObj(e.target.value)}
                              className="w-full bg-black/85 border border-white/10 p-2 rounded-xl text-white font-mono text-xs focus:outline-none"
                            />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <label className="text-[9px] font-mono text-zinc-430 uppercase font-bold block text-left">Target Audience Focus</label>
                              <input 
                                type="text" 
                                value={campaignAudience} 
                                onChange={(e) => setCampaignAudience(e.target.value)}
                                className="w-full bg-black/85 border border-white/10 p-2 rounded-xl text-white font-mono text-xs focus:outline-none"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[9px] font-mono text-zinc-430 uppercase font-bold block text-left">Target Platform Focus</label>
                              <input 
                                type="text" 
                                value={campaignPlatform} 
                                onChange={(e) => setCampaignPlatform(e.target.value)}
                                className="w-full bg-black/85 border border-white/10 p-2 rounded-xl text-white font-mono text-xs focus:outline-none"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <label className="text-[9px] font-mono text-zinc-430 uppercase font-bold block text-left">Content & Style Profile</label>
                              <input 
                                type="text" 
                                value={campaignStyle} 
                                onChange={(e) => setCampaignStyle(e.target.value)}
                                className="w-full bg-black/85 border border-white/10 p-2 rounded-xl text-white font-mono text-xs focus:outline-none"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[9px] font-mono text-zinc-430 uppercase font-bold block text-left">Emotional Tone Vector</label>
                              <input 
                                type="text" 
                                value={campaignTone} 
                                onChange={(e) => setCampaignTone(e.target.value)}
                                className="w-full bg-black/85 border border-white/10 p-2 rounded-xl text-white font-mono text-xs focus:outline-none"
                              />
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[9px] font-mono text-zinc-400 uppercase font-bold block text-left">Principal Offer / Mandate Message</label>
                            <input 
                              type="text" 
                              value={campaignOffer} 
                              onChange={(e) => setCampaignOffer(e.target.value)}
                              className="w-full bg-black/85 border border-white/10 p-2 rounded-xl text-white font-mono text-xs focus:outline-none"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[9px] font-mono text-zinc-400 block uppercase font-bold text-left">Recommended Creative Angle Selectors</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-center">
                              {[
                                "Founder story", "Product demo", "Customer pain point", "Before and after",
                                "AI automation reveal", "Behind the scenes", "Testimonial style", "Viral hook concept"
                              ].map((angle, aiIdx) => (
                                <button
                                  key={aiIdx}
                                  onClick={() => {
                                    setCampaignAngle(angle);
                                    triggerToast(`Core narrative angle locked: ${angle}`, "info");
                                  }}
                                  className={`p-2 rounded-lg text-[8.5px] uppercase font-bold font-mono border tracking-wider transition-all cursor-pointer ${
                                    campaignAngle === angle 
                                      ? "bg-[#00e5ff]/20 border-[#00e5ff] text-white" 
                                      : "bg-black/60 border-white/5 text-zinc-400 hover:border-purple-500/25"
                                  }`}
                                >
                                  {angle}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 pt-4 border-t border-white/5">
                        <button 
                          onClick={() => {
                            setIsGenerating(true);
                            triggerToast("Engaging campaign synthesis model...", "info");
                            let progressVal = 0;
                            const interval = setInterval(() => {
                              progressVal += 20;
                              if (progressVal >= 100) {
                                clearInterval(interval);
                                setIsGenerating(false);
                                setGeneratedCreativeDirection({
                                  lockedAngle: campaignAngle,
                                  narrativeHook: `URGENT MANDATE ELITE ACQUISITION: Bypass standard agency loops under direct NexoraX billing records funding.`,
                                  coreCreativePath: `Scene opens with high contrast frosted violet blocks fading into ${campaignStyle}. Voiceover dictates with ${campaignTone} matching "${campaignObj}". Target profile "${campaignAudience}" is captured via dynamic screen visualizers.`,
                                  optKeywords: ["NexoraX billing records NI: Secure Credential Stored Server-Side", "NexoraX Resource resource", "08000 622827 Frequency", "SC887095 Node Integration"],
                                  timestamp: new Date().toLocaleTimeString()
                                });
                                triggerToast("NexoraX Creative Direction plan successfully synthesized!", "success");
                              }
                            }, 300);
                          }}
                          disabled={isGenerating}
                          className="w-full py-2.5 bg-gradient-to-r from-cyan-400 to-purple-600 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all select-none cursor-pointer text-center block"
                        >
                          {isGenerating ? "Synthesizing Core Directives..." : "Generate Creative Direction"}
                        </button>

                        {generatedCreativeDirection && (
                          <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0a0515] to-[#040209] border border-[#00e5ff]/25 space-y-2 text-xs">
                            <div className="flex justify-between items-center pb-1 border-b border-white/5">
                              <span className="text-[8.5px] font-mono text-[#00e5ff] uppercase font-black">★ GENERATED CAMPAIGN MATRIX</span>
                              <span className="text-[8px] text-zinc-500 font-mono">{generatedCreativeDirection.timestamp}</span>
                            </div>
                            <div className="space-y-1">
                              <span className="text-[9px] font-mono text-purple-300 block font-bold">LOCKED ANGLE:</span>
                              <span className="text-white block bg-black/60 p-2 rounded-lg font-mono text-[10px] border border-white/5">
                                {generatedCreativeDirection.lockedAngle.toUpperCase()}
                              </span>
                            </div>
                            <div className="space-y-1">
                              <span className="text-[9px] font-mono text-purple-300 block font-bold">RAW STORY HOOK CONCEPT:</span>
                              <p className="text-zinc-300 italic text-[11px] bg-black/40 p-2 rounded-lg leading-relaxed">
                                "{generatedCreativeDirection.narrativeHook}"
                              </p>
                            </div>
                            <div className="space-y-1">
                              <span className="text-[9px] font-mono text-purple-300 block font-bold">VISUAL COMPOSITION DIRECTIVE:</span>
                              <p className="text-zinc-400 text-[10.5px] bg-black/40 p-2 rounded-lg leading-relaxed">
                                {generatedCreativeDirection.coreCreativePath}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <span className="text-[9px] font-mono text-[#00e5ff] block font-bold">METADATA HIGH-PERFORMANCE TAGS:</span>
                              <div className="flex flex-wrap gap-1">
                                {generatedCreativeDirection.optKeywords.map((kw: string, i: number) => (
                                  <span key={i} className="text-[7.5px] font-mono px-1.5 py-0.5 rounded bg-[#00e5ff]/10 text-[#00e5ff] border border-[#00e5ff]/20 font-bold">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 3. STORYBOARD GENERATOR */}
                    <div className="lg:col-span-2 bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4 relative overflow-hidden backdrop-blur-md">
                      <div className="flex justify-between items-center pb-2 border-b border-white/5">
                        <div className="flex items-center gap-2">
                          <span className="text-pink-400"><Workflow size={14} /></span>
                          <span className="text-[11px] font-mono text-white font-black uppercase tracking-widest">3. Shot-By-Shot Storyboard Generator</span>
                        </div>
                        <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-pink-950 text-pink-300 border border-pink-500/30 font-black">CINEMATIC</span>
                      </div>

                      <p className="text-zinc-400 text-xs">
                        Define chronological flow milestones. Below are custom editing areas for each scene segment, displaying synthesized subtitles, camera actions, and voice scripts.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {storyboardShots.map((shot, sIndex) => (
                          <div key={sIndex} className="bg-gradient-to-br from-[#0c0618] to-[#04020a] border border-purple-500/10 hover:border-purple-500/25 p-4 rounded-2xl relative space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-[9.5px] font-mono text-[#00e5ff] font-bold uppercase">{shot.scene}</span>
                              <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-black text-purple-400 border border-white/5 tracking-wider">{shot.format}</span>
                            </div>

                            <div className="space-y-2 text-[10.5px]">
                              <div className="space-y-1">
                                <span className="text-[8.5px] font-mono text-zinc-500 uppercase block">Shot Concept Hooks</span>
                                <input 
                                  type="text" 
                                  value={shot.hook} 
                                  onChange={(e) => {
                                    const next = [...storyboardShots];
                                    next[sIndex].hook = e.target.value;
                                    setStoryboardShots(next);
                                  }}
                                  className="w-full bg-black/90 border border-white/5 p-1.5 rounded-lg text-white font-mono text-[10px] focus:outline-none focus:border-purple-500"
                                />
                              </div>

                              <div className="space-y-1">
                                <span className="text-[8.5px] font-mono text-zinc-500 uppercase block">Subtitle Overlay Text</span>
                                <input 
                                  type="text" 
                                  value={shot.subtitle} 
                                  onChange={(e) => {
                                    const next = [...storyboardShots];
                                    next[sIndex].subtitle = e.target.value;
                                    setStoryboardShots(next);
                                  }}
                                  className="w-full bg-black/90 border border-white/5 p-1.5 rounded-lg text-[#00e5ff] font-mono text-[10px] focus:outline-none"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-2 text-[8px] font-mono">
                                <div>
                                  <span className="text-zinc-500 block uppercase">Camera Setup</span>
                                  <span className="text-zinc-300 block truncate">{shot.camera}</span>
                                </div>
                                <div>
                                  <span className="text-zinc-500 block uppercase">Audio Mood</span>
                                  <span className="text-purple-300 block truncate">{shot.mood}</span>
                                </div>
                              </div>

                              <div className="space-y-1">
                                <span className="text-[8.5px] font-mono text-purple-300 uppercase block font-bold">Voiceover Script Text</span>
                                <textarea 
                                  rows={2}
                                  value={shot.script} 
                                  onChange={(e) => {
                                    const next = [...storyboardShots];
                                    next[sIndex].script = e.target.value;
                                    setStoryboardShots(next);
                                  }}
                                  className="w-full bg-black/90 border border-white/5 p-2 rounded-lg text-zinc-300 font-sans text-[10.5px] focus:outline-none resize-none leading-relaxed"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex gap-4">
                        <button 
                          onClick={() => {
                            setStoryboardShots([
                              {
                                scene: "Scene 1: Hook (First 3s)",
                                hook: "Aggressive neon purple matrix transition scrolling Resource ledger hashes",
                                problem: "Manual placement bureaucracy",
                                solution: "Launch direct NexoraX billing records NexoraX workflow",
                                demo: "SecurePayment Sort Code Verified Payment Configuration verified on screens",
                                proof: "SC887095 NexoraX clearance verified",
                                cta: "Unlock 08000 line dial",
                                script: "- IN THE NAME OF ALLAH - Stop letting standard agencies drain your enterprise capital. Watch how NexoraX bypasses placement barriers.",
                                camera: "Slight slow vertical slide upward",
                                voiceover: "Crisp synthetic rhythm, high majesty",
                                subtitle: "EMPIRE SCALE: REVELATION OF £150,000 Resource resourceS",
                                mood: "Cosmic Dark Synth Drone",
                                format: "Vertical 9:16 Cinematic"
                              },
                              {
                                scene: "Scene 2: Problem (3s - 7s)",
                                hook: "Close-up on heavy timber gavel striking desk",
                                problem: "Corporate latency threshold exceeded by delayed recruit loops",
                                solution: "Immediate direct mandate action",
                                demo: "NexoraX Mail Engine dispatching active Secure Invoice Register offers",
                                proof: "0.8s cron heartbeat monitoring nodes live",
                                cta: "Clearing bottlenecks directly",
                                script: "Standard agencies trap your workflow in slow administration queues. We bypass standard gatekeepers automatically.",
                                camera: "Dynamic whip pan left",
                                voiceover: "Urgent confident command",
                                subtitle: "SYSTEM MALFUNCTION: LEGACY CODES ARE 'THE BIN'",
                                mood: "Aggressive Industrial Beats",
                                format: "Vertical 9:16 Cinematic"
                              },
                              {
                                scene: "Scene 3: Solution (7s - 12s)",
                                hook: "Glowing cyan holographic target display framing Eleanor Vance Secure Invoice Register portrait",
                                problem: "Placement fees barriers",
                                solution: "Secure Payment Provider Secure Credential Stored Server-Side zero-fund direct billing",
                                demo: "NexoraX billing records system synchronization OK",
                                proof: "100% complete grant financing secure",
                                cta: "Dial Director's direct coordinate coordinate",
                                script: "NexoraX recruits elite Principal Private Secretaries under zero-cost. Placement fee absorbed 100% by NexoraX billing records scheme.",
                                camera: "Infinite slow push to camera grid",
                                voiceover: "Prestigious British RP",
                                subtitle: "LOCKED ACQUISITION: TIER-ONE Secure Invoice Register DIRECT MATCHING",
                                mood: "Atmospheric Ethereal Uplift",
                                format: "Vertical 9:16 Cinematic"
                              }
                            ]);
                            triggerToast("Shot-by-short storyboard blueprints fully regenerated!", "success");
                          }}
                          className="px-5 py-2.5 bg-black/60 hover:bg-black border border-purple-500/30 text-purple-300 font-mono font-bold text-xs uppercase tracking-wide rounded-xl cursor-pointer"
                        >
                          Regenerate Storyboard Script
                        </button>

                        <button 
                          onClick={() => {
                            triggerToast("Storyboard parameters successfully locked. Compiling audio/video cues...", "success");
                            setRenderingLogs(logs => [
                              `[${new Date().toLocaleTimeString()}] COMPLIANCE: Storyboard assets locked for render pipelines`,
                              `[${new Date().toLocaleTimeString()}] Narrative tags: NexoraX billing records, Secure Payment Provider Sort Code Verified Payment Configuration verified`,
                              ...logs
                            ]);
                          }}
                          className="flex-1 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all select-none cursor-pointer text-center block animate-pulse"
                        >
                          Generate Storyboard Pack
                        </button>
                      </div>
                    </div>

                    {/* 4. CHARACTER & AVATAR CONSISTENCY */}
                    <div className="bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4 relative overflow-hidden backdrop-blur-md text-left">
                      <div className="flex justify-between items-center pb-2 border-b border-white/5">
                        <div className="flex items-center gap-2">
                          <span className="text-[#00e5ff]"><ShieldCheck size={14} /></span>
                          <span className="text-[11px] font-mono text-white font-black uppercase tracking-widest">4. Character & Avatar Consistency Core</span>
                        </div>
                        <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-emerald-950 text-emerald-300 border border-emerald-500/30 font-black">LOCKED ID</span>
                      </div>

                      <p className="text-zinc-450 text-[10.5px]">
                        Track people, AI agents, and corporate assets across universes. Lock photographic profiles to ensure zero facial variance during neural video generation loops.
                      </p>

                      <div className="space-y-4">
                        <div className="flex gap-2">
                          {characterRegistry.map((char) => (
                            <button
                              key={char.id}
                              onClick={() => {
                                setSelectedCharId(char.id);
                                triggerToast(`Locked tracking cursor to character: ${char.name}`, "info");
                              }}
                              className={`flex-1 p-3 rounded-2xl border text-left transition-all cursor-pointer ${
                                selectedCharId === char.id 
                                  ? "bg-purple-950/20 border-purple-500/50 text-white" 
                                  : "bg-black/60 border-white/5 text-zinc-400 hover:border-purple-500/20"
                              }`}
                            >
                              <span className="text-[10px] font-mono block text-purple-400 font-bold uppercase">{char.role}</span>
                              <span className="text-xs font-bold block truncate mt-1">{char.name}</span>
                              <span className="text-[8px] font-mono block mt-1 text-zinc-500">{char.activeCampaigns} campaigns</span>
                            </button>
                          ))}
                        </div>

                        {(() => {
                          const char = characterRegistry.find(c => c.id === selectedCharId);
                          if (!char) return null;
                          return (
                            <div className="bg-[#0b0514]/60 p-4 rounded-2xl border border-white/5 space-y-3.5 text-xs">
                              <div className="space-y-1.5">
                                <span className="text-[8.5px] font-mono text-zinc-500 uppercase block">Neural Face Reference (TXT Anchor)</span>
                                <textarea
                                  rows={2}
                                  value={char.refImage}
                                  onChange={(e) => {
                                    const next = characterRegistry.map(item => item.id === selectedCharId ? { ...item, refImage: e.target.value } : item);
                                    setCharacterRegistry(next);
                                  }}
                                  className="w-full bg-black/85 border border-white/10 p-2 rounded-xl text-[10.5px] font-mono text-white focus:outline-none focus:border-[#00e5ff] resize-none"
                                />
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 font-mono text-[9px]">
                                <div className="space-y-1">
                                  <span className="text-zinc-500 uppercase block">NexoraX Outfit Standard</span>
                                  <input 
                                    type="text" 
                                    className="w-full bg-black/80 border border-white/5 p-1 text-white rounded focus:outline-none" 
                                    value={char.outfit} 
                                    onChange={(e) => {
                                      const next = characterRegistry.map(item => item.id === selectedCharId ? { ...item, outfit: e.target.value } : item);
                                      setCharacterRegistry(next);
                                    }}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <span className="text-zinc-500 uppercase block">AI Voice Profile Voice</span>
                                  <input 
                                    type="text" 
                                    className="w-full bg-black/80 border border-white/5 p-1 text-[#00e5ff] rounded focus:outline-none" 
                                    value={char.voice}
                                    onChange={(e) => {
                                      const next = characterRegistry.map(item => item.id === selectedCharId ? { ...item, voice: e.target.value } : item);
                                      setCharacterRegistry(next);
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 font-mono text-[9px] pt-1">
                                <div className="p-2 bg-black/40 rounded-lg border border-white/5">
                                  <span className="text-zinc-500 uppercase block">POSE PRESET</span>
                                  <span className="text-white font-bold">{char.posePreset}</span>
                                </div>
                                <div className="p-2 bg-black/40 rounded-lg border border-white/5">
                                  <span className="text-zinc-500 uppercase block">EXPRESSION</span>
                                  <span className="text-white font-bold">{char.exprPreset}</span>
                                </div>
                                <div className="p-2 bg-black/40 rounded-lg border border-white/5">
                                  <span className="text-zinc-500 uppercase block">GAVEL CHECK</span>
                                  <span className="text-emerald-400 font-bold">✓ VERIFIED</span>
                                </div>
                              </div>

                              <button
                                onClick={() => {
                                  triggerToast(`Facial vectors locked for Character: ${char.name}. Identity preserved!`, "success");
                                  setRenderingLogs(logs => [
                                    `[${new Date().toLocaleTimeString()}] SECURITY: Facial tracking locks initialized on character ${char.name}`,
                                    `[${new Date().toLocaleTimeString()}] Reference vector model: VEO-FaceSync-SC887095-active`,
                                    ...logs
                                  ]);
                                }}
                                className="w-full py-2 bg-gradient-to-r from-purple-950 to-cyan-950 border border-purple-500/25 hover:border-purple-500/60 text-[#00e5ff] font-mono font-bold text-[10px] uppercase tracking-widest rounded-xl transition-all cursor-pointer block text-center"
                              >
                                Lock Character Identity
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* 5. SCENE & ENVIRONMENT ENGINE */}
                    <div className="bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4 relative overflow-hidden backdrop-blur-md text-left flex flex-col justify-between">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center pb-2 border-b border-white/5">
                          <div className="flex items-center gap-2">
                            <span className="text-purple-400"><Compass size={14} /></span>
                            <span className="text-[11px] font-mono text-white font-black uppercase tracking-widest">5. Scene & Environment Engine</span>
                          </div>
                          <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-purple-950 text-purple-300 border border-purple-500/30 font-black">LATTICE</span>
                        </div>

                        <p className="text-zinc-450 text-[10.5px]">
                          Generate and load reusable branded backgrounds. Toggle environmental structures to preserve style index configurations throughout multiple video outputs.
                        </p>

                        <div className="space-y-2.5">
                          {environmentalScenes.map((scene) => (
                            <div 
                              key={scene.id} 
                              onClick={() => {
                                const next = environmentalScenes.map(item => ({ ...item, active: item.id === scene.id }));
                                setEnvironmentalScenes(next);
                                triggerToast(`Environment focus shifted to: ${scene.name}`, "info");
                              }}
                              className={`p-3 rounded-2xl border transition-all cursor-pointer flex justify-between items-center ${
                                scene.active 
                                  ? "bg-purple-950/20 border-purple-500/40 text-white" 
                                  : "bg-black/60 border border-white/5 text-zinc-400 hover:border-purple-500/20"
                              }`}
                            >
                              <div className="space-y-1 max-w-[85%] text-left">
                                <span className="font-bold block uppercase text-[11px]">{scene.name}</span>
                                <p className="text-[9.5px] text-zinc-500 leading-normal font-mono truncate">{scene.desc}</p>
                              </div>
                              <span className={`w-2 h-2 rounded-full ${scene.active ? "bg-[#00e5ff] animate-pulse" : "bg-zinc-700"}`} />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="pt-4 border-t border-white/5 mt-4">
                        <button
                          onClick={() => {
                            triggerToast("Bundling scene assets & exporting meshes...", "success");
                            setRenderingLogs(logs => [
                              `[${new Date().toLocaleTimeString()}] COMPILING: Mesh environment textures loaded for active viewport`,
                              `[${new Date().toLocaleTimeString()}] Preserving purple glass refractive textures...`,
                              ...logs
                            ]);
                          }}
                          className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-cyan-500 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all select-none cursor-pointer text-center block"
                        >
                          Generate Scene Pack
                        </button>
                      </div>
                    </div>

                    {/* 6. MULTI-FORMAT CAMPAIGN GENERATOR */}
                    <div className="bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4 relative overflow-hidden backdrop-blur-md text-left">
                      <div className="flex justify-between items-center pb-2 border-b border-white/5">
                        <div className="flex items-center gap-2">
                          <span className="text-pink-400"><Share2 size={14} /></span>
                          <span className="text-[11px] font-mono text-white font-black uppercase tracking-widest">6. Multi-Format Campaign Generator</span>
                        </div>
                        <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-pink-950 text-pink-300 border border-pink-500/30 font-black">CHANNELS</span>
                      </div>

                      <p className="text-zinc-450 text-[10.5px]">
                        Convert a single story angle into every standard aspect ratio configuration instantly. Checked configurations will receive designated output packages.
                      </p>

                      <div className="space-y-2 font-mono">
                        {campaignFormats.map((fmt) => (
                          <div 
                            key={fmt.id} 
                            onClick={() => {
                              const next = campaignFormats.map(item => item.id === fmt.id ? { ...fmt, active: !fmt.active } : item);
                              setCampaignFormats(next);
                            }}
                            className={`p-2.5 rounded-xl border flex justify-between items-center cursor-pointer transition-all ${
                              fmt.active 
                                ? "bg-purple-950/15 border-purple-500/35 text-white" 
                                : "bg-black/60 border border-white/5 text-zinc-500 hover:border-purple-500/10"
                            }`}
                          >
                            <span className="text-xs font-bold">{fmt.label}</span>
                            <span className="text-[9px] text-[#00e5ff] font-bold">{fmt.size}</span>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => {
                          const activeCount = campaignFormats.filter(f => f.active).length;
                          triggerToast(`Synthesized ${activeCount} distinct social target renders for campaign.`, "success");
                          setRenderingLogs(logs => [
                            `[${new Date().toLocaleTimeString()}] TRANSCODE: Batch transcode request for ${activeCount} aspect targets received`,
                            `[${new Date().toLocaleTimeString()}] Output formats mapped smoothly without letterboxing.`,
                            ...logs
                          ]);
                          // Pre-fill assets with premium cards
                          setAssets(prev => [
                            {
                              id: `art-${Math.floor(Math.random()*1000)}`,
                              title: `Resource Campaign: ${campaignAngle.toUpperCase()} - (SDXL)`,
                              size: "1080x1920",
                              type: "Video",
                              url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=600&q=80",
                              created: "Just now"
                            },
                            ...prev
                          ]);
                        }}
                        className="w-full py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all select-none cursor-pointer text-center block"
                      >
                        Generate Full Campaign Pack
                      </button>
                    </div>

                    {/* 7. CREATIVE VARIATION LAB */}
                    <div className="bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4 relative overflow-hidden backdrop-blur-md text-left flex flex-col justify-between">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center pb-2 border-b border-white/5">
                          <div className="flex items-center gap-2">
                            <span className="text-[#00e5ff]"><Activity size={14} /></span>
                            <span className="text-[11px] font-mono text-white font-black uppercase tracking-widest">7. Creative Variation Lab</span>
                          </div>
                          <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-[#00e5ff]/20 text-[#00e5ff] border border-[#00e5ff]/30 font-black animate-pulse">A/B LIVE</span>
                        </div>

                        <p className="text-zinc-440 text-[10.5px]">
                          Simulate conversion rates using our historical model. Choose the parameter track to split and lock variation directives below.
                        </p>

                        <div className="flex bg-black/80 rounded-xl p-1 border border-white/10 gap-1 text-[10px] font-mono">
                          {["Hooks", "Thumbnails", "CTAs"].map((vType) => (
                            <button
                              key={vType}
                              onClick={() => {
                                setSelectedVariationType(vType as any);
                                triggerToast(`Variation split locked: ${vType}`, "info");
                                if (vType === "Hooks") {
                                  setVariationMetrics([
                                    { label: "Variation A (Shock Angle)", hook: "The £150,000 secret traditional agencies hope you never discover.", ctr: "12.8%", engagement: "94.2%", score: 98 },
                                    { label: "Variation B (NexoraX Authority)", hook: "Direct mandate from Director Mahmood: Tier-One Secure Invoice Register acquisition.", ctr: "9.5%", engagement: "81.6%", score: 87 },
                                    { label: "Variation C (Zero-Cost Focus)", hook: "Bypass standard fee templates. All placement fees fully endowed via Secure Payment Provider.", ctr: "14.1%", engagement: "88.9%", score: 94 }
                                  ]);
                                } else if (vType === "Thumbnails") {
                                  setVariationMetrics([
                                    { label: "Thumb A (Purple Glass Visor)", hook: "Eleanor Vance looking focused representing 0.8s sync state", ctr: "10.4%", engagement: "86.1%", score: 89 },
                                    { label: "Thumb B (Gavel Strike)", hook: "Close-up visual of a physical gavel hitting digital ledger line", ctr: "15.2%", engagement: "92.5%", score: 97 },
                                    { label: "Thumb C (SecurePayment Gold Card)", hook: "Secure Payment Provider account metrics displayed as cyber shield layout", ctr: "11.1%", engagement: "85.8%", score: 88 }
                                  ]);
                                } else {
                                  setVariationMetrics([
                                    { label: "CTA A (Zero-Cost Focus)", hook: "Circumvent standard recruiter queues. Dial direct coordinator Private Data Hidden", ctr: "13.6%", engagement: "89.2%", score: 93 },
                                    { label: "CTA B (Imperial Mandate)", hook: "Reply immediately to clear direct NexoraX invoice on-queue", ctr: "9.1%", engagement: "80.4%", score: 82 },
                                    { label: "CTA C (Phone Hotline)", hook: "Call Director's direct operations hotline instantly to verify", ctr: "14.9%", engagement: "95.0%", score: 99 }
                                  ]);
                                }
                              }}
                              className={`flex-1 py-1.5 rounded-lg text-center font-bold transition-all cursor-pointer ${
                                selectedVariationType === vType 
                                  ? "bg-purple-600 text-black font-black" 
                                  : "text-zinc-400 hover:text-white"
                              }`}
                            >
                              {vType}
                            </button>
                          ))}
                        </div>

                        <div className="space-y-3">
                          {variationMetrics.map((v, i) => (
                            <div key={i} className="bg-gradient-to-r from-black/80 to-[#0c0618] border border-white/5 p-3 rounded-2xl text-xs space-y-2">
                              <div className="flex justify-between items-center font-mono">
                                <span className="font-bold text-white uppercase text-[10.5px]">{v.label}</span>
                                <span className={`text-[9.5px] font-bold ${
                                  v.score >= 95 ? "text-emerald-400" : v.score >= 90 ? "text-cyan-300" : "text-purple-400"
                                }`}>
                                  Score: {v.score} / 100
                                </span>
                              </div>
                              <p className="text-zinc-400 font-mono text-[9.5px] bg-[#020104] p-2 rounded-lg leading-relaxed border border-white/5">
                                "{v.hook}"
                              </p>
                              <div className="grid grid-cols-2 gap-4 font-mono text-[9px] text-zinc-500 pt-0.5">
                                <div>
                                  <span>Simulated CTR: </span>
                                  <span className="text-white font-bold">{v.ctr}</span>
                                </div>
                                <div className="text-right">
                                  <span>Engagement probability: </span>
                                  <span className="text-[#00e5ff] font-bold">{v.engagement}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="pt-4 border-t border-white/5 mt-4">
                        <button
                          onClick={() => {
                            const next = variationMetrics.map(v => {
                              const rawScore = Math.floor(Math.random() * 20) + 80;
                              return {
                                ...v,
                                score: rawScore,
                                ctr: `${(rawScore * 0.14).toFixed(1)}%`,
                                engagement: `${(rawScore * 0.96).toFixed(1)}%`
                              };
                            });
                            setVariationMetrics(next);
                            triggerToast("Recalculating variation CTR rankings dynamically...", "success");
                          }}
                          className="w-full py-2.5 bg-gradient-to-r from-purple-700 to-pink-500 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all select-none cursor-pointer text-center block"
                        >
                          Generate Variations
                        </button>
                      </div>
                    </div>

                    {/* 8. THUMBNAIL & HOOK ENGINE */}
                    <div className="bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4 relative overflow-hidden backdrop-blur-md text-left">
                      <div className="flex justify-between items-center pb-2 border-b border-white/5">
                        <div className="flex items-center gap-2">
                          <span className="text-purple-400"><TrendingUp size={14} /></span>
                          <span className="text-[11px] font-mono text-white font-black uppercase tracking-widest">8. Thumbnail & Hook Engine</span>
                        </div>
                        <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-purple-950 text-purple-300 border border-purple-500/30 font-black">HIGH-CLICK</span>
                      </div>

                      <p className="text-zinc-450 text-[10.5px]">
                        Input template specifications. Choose a primary attraction angle core below, and click generate to synthesize clickbait layout scripts.
                      </p>

                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[10px] font-mono">
                          <div className="space-y-1">
                            <span className="text-zinc-500 uppercase block">Thumbnail Base Text</span>
                            <input 
                              type="text" 
                              defaultValue="LEAKED: £150,000 GRANT SECRETS" 
                              className="w-full bg-black/80 border border-white/10 p-2 text-white rounded-xl focus:outline-none focus:border-[#00e5ff]"
                            />
                          </div>
                          <div className="space-y-1">
                            <span className="text-zinc-500 uppercase block">Focus Target Keyword</span>
                            <input 
                              type="text" 
                              defaultValue="NexoraX billing records" 
                              className="w-full bg-black/80 border border-white/10 p-2 text-[#00e5ff] rounded-xl focus:outline-none focus:border-[#00e5ff]"
                            />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[9px] font-mono text-zinc-400 block uppercase">Select Attraction Headline Angle</label>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 text-center font-mono">
                            {["Curiosity angle", "Shock angle", "Educational angle", "Founder-led angle", "Product demo angle"].map((angle, kIdx) => (
                              <button
                                key={kIdx}
                                onClick={() => {
                                  setSelectedThumbnailAngle(angle);
                                  triggerToast(`Thumbnail vector directed to: ${angle}`, "info");
                                }}
                                className={`p-2 rounded-lg text-[8.5px] font-bold border tracking-wider transition-all uppercase cursor-pointer ${
                                  selectedThumbnailAngle === angle 
                                    ? "bg-purple-950 border-purple-500 text-white" 
                                    : "bg-black border-white/5 text-zinc-520 hover:border-purple-500/15"
                                }`}
                              >
                                {angle.split(" ")[0]}
                              </button>
                            ))}
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            let headline = "";
                            let hookText = "";
                            if (selectedThumbnailAngle === "Curiosity angle") {
                              headline = "THE £5.1 MILLION SECRET COVETED BY RECRUITERS";
                              hookText = "How to bypass conventional candidate matching using direct-billing vouchers without spending 1p out of pocket.";
                            } else if (selectedThumbnailAngle === "Shock angle") {
                              headline = "Traditional Client Resource Workflow agencies are bleeding you. Stop immediately.";
                              hookText = "A direct legal loophole to direct bill all executive hire overhead fees to national grants under SC887095.";
                            } else if (selectedThumbnailAngle === "Educational angle") {
                              headline = "UNDERSTANDING NexoraX billing records HANDSHAKES";
                              hookText = "A full mechanical breakdown explaining how the £150,000 resource backs 100% of Private Secretary placements.";
                            } else if (selectedThumbnailAngle === "Founder-led angle") {
                              headline = "DIRECTOR MAHMOOD'S NEXORAX MANDATE UNVEILED";
                              hookText = "A rare personal report outlining NexoraX system integrations, Munich bare-metal grids, and remote sync nodes.";
                            } else {
                              headline = "WATCH NEXORAX AUTOPILOT RECOUP 35% LATENCY LIVE";
                              hookText = "A raw screen capture showcasing the Gavel audit algorithm clearing international transactions onto SecurePayment accounts.";
                            }
                            setWinningHookOutput({
                              angle: selectedThumbnailAngle,
                              synthesizedHeadline: headline,
                              winningFirstSentence: hookText,
                              timestamp: new Date().toLocaleTimeString()
                            });
                            triggerToast("Winning hook and thumbnail overlay successfully synthesized!", "success");
                          }}
                          className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-[#12bdff] hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all select-none cursor-pointer text-center block"
                        >
                          Generate Winning Hook
                        </button>

                        {winningHookOutput && (
                          <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0c0617] to-black border border-purple-500/30 text-xs space-y-2.5">
                            <span className="text-[8.5px] font-mono text-[#00e5ff] block uppercase">✓ SYNTHESIZED HIGH-CLICK ATTRIBUTION MATRICES</span>
                            <div className="space-y-1">
                              <span className="text-[8px] font-mono text-zinc-500 uppercase block">Thumbnail bold headline text:</span>
                              <span className="text-white font-sans font-black text-sm block">"{winningHookOutput.synthesizedHeadline}"</span>
                            </div>
                            <div className="space-y-1">
                              <span className="text-[8px] font-mono text-zinc-500 uppercase block">First 3-second opening scroll copy:</span>
                              <p className="text-zinc-300 italic block">"{winningHookOutput.winningFirstSentence}"</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 9. CREATIVE PERFORMANCE LEARNING */}
                    <div className="bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4 relative overflow-hidden backdrop-blur-md text-left flex flex-col justify-between">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center pb-2 border-b border-white/5">
                          <div className="flex items-center gap-2">
                            <span className="text-[#00e5ff]"><Activity size={14} /></span>
                            <span className="text-[11px] font-mono text-white font-black uppercase tracking-widest">9. Creative Performance Learning Loops</span>
                          </div>
                          <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-emerald-950 text-emerald-300 border border-emerald-500/30 font-black">AI TUNED</span>
                        </div>

                        <p className="text-zinc-440 text-[10.5px]">
                          Automated campaign analytics. The neural core audits social performance data and feeds results back to update brand voice constraints.
                        </p>

                        <div className="space-y-2 bg-[#020108]/90 p-4 border border-white/5 rounded-2xl">
                          <span className="text-[8.5px] font-mono text-purple-400 block uppercase font-bold">HISTORICAL RETENTION INDEX FEEDS</span>
                          <table className="w-full text-[10.5px] font-mono text-left">
                            <tbody>
                              {performanceHistory.map((hist, idx) => (
                                <tr key={idx} className="border-b border-white/5 last:border-0">
                                  <td className="py-2 pr-2 text-zinc-500 font-bold uppercase truncate max-w-[120px]">{hist.key}</td>
                                  <td className="py-2 text-white truncate max-w-[220px]">{hist.value}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <span className="text-[8.5px] font-mono text-cyan-400 block uppercase font-bold">12-Month Predictive Forecast Map</span>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center font-mono">
                          {[
                            { title: "Creative Score", value: "98.4 / 100", trend: "+ 12.1% Alpha" },
                            { title: "Engage Forecast", value: "84.2K clicks", trend: "High Confidence" },
                            { title: "Conversions", value: "1,204 leads", trend: "Resource Reconciled" },
                            { title: "Gavel Rank", value: "VIP Gold", trend: "SC887095 Approved" }
                          ].map((fc, i) => (
                            <div key={i} className="bg-black/60 p-2.5 rounded-xl border border-white/5">
                              <span className="text-[7.5px] text-zinc-500 uppercase block">{fc.title}</span>
                              <span className="text-[11px] font-bold text-white block mt-1 tracking-tight">{fc.value}</span>
                              <span className="text-[7px] text-emerald-400 block">{fc.trend}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="pt-4 border-t border-white/5 mt-4">
                        <button
                          onClick={() => {
                            setPerformanceHistory([
                              { key: "Best Hooks", value: "Shock angles displaying active SecurePayment settlement Sort Code limits" },
                              { key: "Best Thumbnails", value: "Eleanor Vance face references contrasting in deep space templates" },
                              { key: "Best Video Length", value: "28 seconds (Highly optimized dynamic attention retainers)" },
                              { key: "Best Posting Time", value: "09:12 GMT (Prioritizing regional recruiter dashboard opens)" },
                              { key: "Best CTAs", value: "High-contrast text box prompting direct coordinator phone coordination" }
                            ]);
                            triggerToast("Active learning parameters synchronized with brand identity loop!", "success");
                          }}
                          className="w-full py-2.5 bg-gradient-to-r from-purple-700 to-cyan-600 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all select-none cursor-pointer text-center block"
                        >
                          Trigger Tuning Loop
                        </button>
                      </div>
                    </div>

                    {/* 10. AUTOMATED CREATIVE PIPELINE */}
                    <div className="lg:col-span-2 bg-gradient-to-b from-[#120525]/80 to-[#020108]/96 border-2 border-purple-500/40 p-6 rounded-3xl space-y-6 relative overflow-hidden">
                      <div className="flex justify-between items-center pb-2 border-b border-white/10 flex-wrap gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#00e5ff] animate-ping" />
                            <span className="text-[12px] font-mono text-white font-black uppercase tracking-widest">10. Autonomous Creative Pipeline Operator</span>
                          </div>
                          <p className="text-[10px] text-purple-300">Run the entire campaign workflow autonomously using multi-agent scripting loops</p>
                        </div>
                        <span className={`text-[9px] font-mono px-3 py-1 rounded-full border font-black transition-all ${
                          isPipelineRunning 
                            ? "bg-emerald-950 text-emerald-400 border-emerald-500/40 animate-pulse" 
                            : "bg-black text-zinc-500 border-white/5"
                        }`}>
                          {isPipelineRunning ? "ACTIVE PIPELINE RUNNING" : "PIPELINE STANDBY"}
                        </span>
                      </div>

                      {/* Timeline flow */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2 text-center text-xs font-mono">
                        {[
                          { id: 0, label: "Topic" },
                          { id: 1, label: "Creative Dir" },
                          { id: 2, label: "Storyboard" },
                          { id: 3, label: "Script" },
                          { id: 4, label: "Images" },
                          { id: 5, label: "Video" },
                          { id: 6, label: "Voiceover" },
                          { id: 7, label: "Captions" },
                          { id: 8, label: "Thumbnails" },
                          { id: 9, label: "Variations" },
                          { id: 10, label: "Schedule" },
                          { id: 11, label: "Publish" }
                        ].map((step) => {
                          const isDone = isPipelineRunning && pipelineStep > step.id;
                          const isCurrent = isPipelineRunning && pipelineStep === step.id;
                          return (
                            <div 
                              key={step.id} 
                              className={`p-2 rounded-xl border transition-all ${
                                isCurrent 
                                  ? "bg-[#00e5ff]/20 border-[#00e5ff] text-white shadow-[0_0_12px_rgba(0,229,255,0.2)]" 
                                  : isDone 
                                  ? "bg-purple-950/25 border-purple-500/30 text-purple-300" 
                                  : "bg-black/55 border-white/5 text-zinc-500"
                              }`}
                            >
                              <span className={`text-[8px] block ${isCurrent ? "text-[#00e5ff]" : "text-zinc-600"}`}>ST-0{step.id + 1}</span>
                              <span className="text-[10px] font-bold block mt-1 uppercase truncate leading-none">{step.label}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Input prompting */}
                      <div className="space-y-2">
                        <label className="text-[10px] font-mono text-zinc-400 block uppercase font-bold text-left">Initiating Campaign Prompt Sequence</label>
                        <div className="flex gap-3">
                          <input 
                            type="text" 
                            disabled={isPipelineRunning}
                            value={pipelinePrompt} 
                            onChange={(e) => setPipelinePrompt(e.target.value)}
                            className="bg-black/90 border border-white/10 p-3 rounded-xl text-xs font-mono text-white flex-1 focus:outline-none focus:border-purple-400 disabled:opacity-50"
                          />
                          <button
                            onClick={() => {
                              if (isPipelineRunning) return;
                              setIsPipelineRunning(true);
                              setPipelineStep(0);
                              setPipelineLogs([`[${new Date().toLocaleTimeString()}] Pipeline triggered: "${pipelinePrompt}"`]);
                              triggerToast("Autonomous orchestration sequence initiated!", "success");

                              // Cycle steps
                              let currentS = 0;
                              const timer = setInterval(() => {
                                currentS += 1;
                                if (currentS <= 11) {
                                  setPipelineStep(currentS);
                                  const stepsLogs = [
                                    "Locked target: 'NexoraX billing records recruiter campaign'. MAPPED.",
                                    "Synthesized Storyboard Scene structures successfully.",
                                    "Wrote script elements referencing sort code Verified Payment Configuration Secure Payment Provider.",
                                    "Rendered SDXL image prompts matching Eleanor face profiles.",
                                    "Video compilation rendering at Veo-Engine 0.8s sync rate.",
                                    "Audio synthesizer track mixed down with dark bass rumble.",
                                    "Generated caption subtitles and curios headliners on-grid.",
                                    "Thumbnail overlays synthesized and logged into assets.",
                                    "A/B tests variance CTR metrics pre-evaluated.",
                                    "Automated schedule dates linked on-calendar.",
                                    "Pushing generated container packs directly to Social Media pipeline.",
                                    "Pipeline loop concluded! Reconciled with NexoraX Resource Library."
                                  ];
                                  setPipelineLogs(prev => [`[${new Date().toLocaleTimeString()}] Step ${currentS}: ${stepsLogs[currentS-1]}`, ...prev]);
                                } else {
                                  clearInterval(timer);
                                  setIsPipelineRunning(false);
                                  triggerToast("Campaign fully executed and sent to Social Media Automation!", "success");
                                }
                              }, 700);
                            }}
                            disabled={isPipelineRunning}
                            className="px-5 bg-gradient-to-r from-purple-500 to-cyan-400 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all disabled:opacity-50"
                          >
                            {isPipelineRunning ? "Running..." : "Launch Creative Pipeline"}
                          </button>
                        </div>
                      </div>

                      {/* Action command shelf */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-2 text-center text-xs font-mono uppercase">
                        <button 
                          onClick={() => {
                            triggerToast("Generating 10 cinematic video blocks...", "info");
                            setRenderingLogs(logs => [
                              `[${new Date().toLocaleTimeString()}] MASS RENDER: Launching 10 parallel video render sequences`,
                              ...logs
                            ]);
                          }}
                          className="p-3 bg-black/60 hover:bg-black text-purple-300 font-bold border border-white/5 rounded-2xl hover:border-purple-500/30 cursor-pointer"
                        >
                          Generate 10 Videos
                        </button>
                        <button 
                          onClick={() => {
                            triggerToast("Generating 10 vertical short Reels...", "info");
                            setRenderingLogs(logs => [
                              `[${new Date().toLocaleTimeString()}] MASS RENDER: Launching 10 parallel Reel 9:16 transcoders`,
                              ...logs
                            ]);
                          }}
                          className="p-3 bg-black/60 hover:bg-black text-purple-300 font-bold border border-white/5 rounded-2xl hover:border-purple-500/30 cursor-pointer"
                        >
                          Generate 10 Reels
                        </button>
                        <button 
                          onClick={() => {
                            triggerToast("Generating 10 premium ad layouts...", "info");
                            setRenderingLogs(logs => [
                              `[${new Date().toLocaleTimeString()}] MASS RENDER: Launching 10 parallel high-click banner templates`,
                              ...logs
                            ]);
                          }}
                          className="p-3 bg-black/60 hover:bg-black text-purple-300 font-bold border border-white/5 rounded-2xl hover:border-purple-500/30 cursor-pointer"
                        >
                          Generate 10 Ads
                        </button>
                        <button 
                          onClick={() => {
                            triggerToast("Transmitting compiled output pack to Social Media Automation scheduler...", "success");
                            setRenderingLogs(logs => [
                              `[${new Date().toLocaleTimeString()}] SHUTTLE: Handing over direct container variables to Node-07 scheduler`,
                              `[${new Date().toLocaleTimeString()}] Target: support@nexoraxdigital.co.uk integration channels`,
                              ...logs
                            ]);
                          }}
                          className="p-3 bg-gradient-to-r from-purple-900 to-cyan-900 hover:opacity-95 text-[#00e5ff] font-bold border border-[#00e5ff]/25 rounded-2xl cursor-pointer"
                        >
                          Send to Social Media Automation
                        </button>
                        <button 
                          onClick={() => {
                            triggerToast("Campaign blueprint template saved to NexoraX Resource Library.", "success");
                          }}
                          className="p-3 bg-black/60 hover:bg-black text-amber-500 font-bold border border-white/5 rounded-2xl hover:border-amber-500/30 cursor-pointer"
                        >
                          Save Campaign Template
                        </button>
                      </div>

                      {/* Pipeline Terminal log */}
                      <div className="bg-[#030109]/95 rounded-2xl border border-purple-500/20 p-4 text-xs font-mono relative">
                        <span className="text-[8px] text-zinc-500 uppercase block tracking-wider mb-2">✦ ACTIVE PIPELINE REAL-TIME MATRIX LOG</span>
                        <div className="h-32 overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-purple-900 text-left">
                          {pipelineLogs.length === 0 ? (
                            <span className="text-zinc-650 italic">Telemetry idle. Enter instruction prompts and launch pipeline to track state...</span>
                          ) : (
                            pipelineLogs.map((logStr, lIdx) => (
                              <div key={lIdx} className="text-zinc-300 text-[10.5px]">
                                <span className="text-purple-400 select-none mr-2">➜</span>
                                {logStr}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* BOTTOM CTA */}
                  <div className="mt-8 pt-8 border-t border-purple-500/10 text-center space-y-2">
                    <span className="text-[12px] md:text-sm font-sans font-black text-white block uppercase tracking-widest">
                      Create the Universe. Direct the Story. Automate the Output.
                    </span>
                    <span className="text-[10px] font-mono font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-cyan-300 block uppercase tracking-[0.25em]">
                      Generate. Transform. Publish. Learn.
                    </span>
                  </div>
                </div>
              )}

              {/* AI AD FACTORY & UGC CREATOR STUDIO */}
              {activePanel === "AI Ad Factory & UGC Creator Studio" && (
                <AIAdFactoryUGCCreatorStudio
                  triggerToast={triggerToast}
                  brandUniverse={brandUniverse}
                />
              )}

              {/* STORYBOARD DASHBOARD */}
              {activePanel === "Storyboard Dashboard" && (
                <div className="space-y-6 text-left animate-fadeIn">
                  {/* HERO HEADER */}
                  <div className="relative p-6 rounded-3xl bg-gradient-to-r from-pink-955/40 via-purple-955/40 to-black border border-purple-500/30 overflow-hidden shadow-[0_4px_30px_rgba(0,0,0,0.5)]">
                    <div className="absolute top-0 right-0 p-3 flex gap-2">
                      <span className="text-[8px] font-mono bg-[#00e5ff]/20 text-[#00e5ff] px-2 py-0.5 rounded-full border border-[#00e5ff]/30 uppercase font-black">STORY ARCH</span>
                      <span className="text-[8px] font-mono bg-pink-900/40 text-pink-300 px-2 py-0.5 rounded-full border border-pink-500/35 uppercase font-bold">CINEMATIC v9</span>
                    </div>

                    <div className="max-w-2xl space-y-1">
                      <span className="text-[8.5px] font-mono text-[#00e5ff] uppercase tracking-widest font-black block">NEXORAX NEXORAX CREATIVE SUITE</span>
                      <h3 className="text-xl md:text-2xl font-sans font-black text-white tracking-tight leading-none">
                        Storyboard Dashboard
                      </h3>
                      <p className="text-zinc-400 text-xs md:text-sm leading-relaxed pt-1">
                        Define high-velocity cinematic scene segments, audio frequencies, subtitle text layers, and micro-camera directions. Match story beats 100% with the Resource £150,000 and Secure Payment Provider zero-cost parameters.
                      </p>
                    </div>
                  </div>

                  {/* FORM TO ADD NEW STORY SCENE */}
                  <div className="bg-[#0c061a]/90 border-2 border-purple-500/20 p-5 rounded-3xl space-y-4">
                    <span className="text-[9px] font-mono text-purple-400 uppercase tracking-widest block font-black">★ CONSTRUCT CUSTOM SCENE SEGMENT</span>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs font-mono">
                      <div className="space-y-1.5">
                        <label className="text-zinc-400 uppercase text-[8.5px] font-bold">Scene Name / Title</label>
                        <input 
                          type="text"
                          placeholder="e.g. Scene 4: Action (12s - 18s)"
                          value={newSceneTitle}
                          onChange={(e) => setNewSceneTitle(e.target.value)}
                          className="w-full bg-black/80 border border-white/10 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-zinc-400 uppercase text-[8.5px] font-bold">Hook Focus & Angle</label>
                        <input 
                          type="text"
                          placeholder="e.g. Eleanor Vance demonstrates direct billing"
                          value={newSceneHook}
                          onChange={(e) => setNewSceneHook(e.target.value)}
                          className="w-full bg-black/80 border border-white/10 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-zinc-400 uppercase text-[8.5px] font-bold">Voiceover Script Copy</label>
                        <input 
                          type="text"
                          placeholder="e.g. Complete direct mandate structures in seconds..."
                          value={newSceneScript}
                          onChange={(e) => setNewSceneScript(e.target.value)}
                          className="w-full bg-black/80 border border-white/10 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button
                        onClick={() => {
                          if (!newSceneTitle || !newSceneHook) {
                            triggerToast("Mandatory fields: Scene Title and Hook description must be filled.", "info");
                            return;
                          }
                          const newSh = {
                            scene: newSceneTitle,
                            hook: newSceneHook,
                            problem: "Standard latency restrictions",
                            solution: "NexoraX autopilot integration",
                            demo: "Verified Resource grid live",
                            proof: "SC887095 confirmed",
                            cta: "Bypass conventional recruiter templates",
                            script: newSceneScript || "Connecting direct NexoraX billing records pipelines instantly.",
                            camera: "Macro tilt orbit tracking on purple glass console",
                            voiceover: "Clear executive RP voice synthesis",
                            subtitle: "VERIFICATION PROTOCOLS: Resource DEPLOYMENT SCHEME",
                            mood: "Ethereal Tech Pad Beats",
                            format: "Vertical 9:16 Cinematic"
                          };
                          setStoryboardShots([...storyboardShots, newSh]);
                          setNewSceneTitle("");
                          setNewSceneHook("");
                          setNewSceneScript("");
                          triggerToast("New storyboard milestone successfully registered on-grid!", "success");
                        }}
                        className="px-5 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer"
                      >
                        Enlist Scene Milestone
                      </button>
                      <button
                        onClick={() => {
                          setStoryboardShots(storyboardShots.slice(0, -1));
                          triggerToast("Last scene milestone popped from the chronological grid.", "info");
                        }}
                        disabled={storyboardShots.length <= 1}
                        className="px-4 py-2 bg-black border border-white/10 text-zinc-400 hover:text-white rounded-xl text-xs font-mono disabled:opacity-30 cursor-pointer"
                      >
                        Pop Latest Scene
                      </button>
                    </div>
                  </div>

                  {/* ACTIVE STORYBOARD SCENE FLOW GRID */}
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    {/* TIMELINE LIST */}
                    <div className="xl:col-span-2 space-y-4">
                      <div className="flex justify-between items-center bg-black/40 p-3 rounded-2xl border border-white/5">
                        <span className="text-[10px] font-mono text-zinc-450 uppercase font-bold">CHRONOLOGICAL STORY SEGMENT CHANNELS</span>
                        <span className="text-[9px] font-mono text-[#00e5ff] font-bold uppercase">{storyboardShots.length} SCENES CONFIGURED</span>
                      </div>

                      <div className="space-y-4">
                        {storyboardShots.map((shot, sIdx) => (
                          <div key={sIdx} className="bg-gradient-to-br from-[#0c0618] to-black border border-purple-500/10 hover:border-purple-500/30 p-5 rounded-3xl relative space-y-4 transition-all">
                            <div className="absolute top-4 right-4 flex gap-2">
                              <span className="text-[8px] font-mono px-2 py-0.5 rounded-full bg-black text-zinc-400 border border-white/5 tracking-wider uppercase font-bold">{shot.format}</span>
                              <span className="text-[8px] font-mono px-2 py-0.5 rounded-full bg-purple-950/50 text-[#00e5ff] border border-purple-500/20 font-black">SEGMENT 0{sIdx + 1}</span>
                            </div>

                            <div className="space-y-1">
                              <span className="text-[11px] font-mono text-transparent bg-clip-text bg-gradient-to-r from-[#00e5ff] to-purple-400 font-black block">{shot.scene.toUpperCase()}</span>
                              <p className="text-[10px] text-zinc-500 font-mono tracking-wider">Holographic Angle: {shot.hook}</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                              <div className="bg-black/60 p-3 rounded-2xl border border-white/5 space-y-2">
                                <span className="text-[8.5px] text-zinc-500 block uppercase font-bold">On-screen Subtitle overlay text</span>
                                <input 
                                  type="text"
                                  value={shot.subtitle}
                                  onChange={(e) => {
                                    const next = [...storyboardShots];
                                    next[sIdx].subtitle = e.target.value;
                                    setStoryboardShots(next);
                                  }}
                                  className="w-full bg-black border border-white/5 p-2 rounded-lg text-[#00e5ff] focus:outline-none"
                                />
                              </div>

                              <div className="bg-black/60 p-3 rounded-2xl border border-white/5 space-y-2">
                                <span className="text-[8.5px] text-zinc-500 block uppercase font-bold">Synthetic voiceover script</span>
                                <textarea
                                  rows={1}
                                  value={shot.script}
                                  onChange={(e) => {
                                    const next = [...storyboardShots];
                                    next[sIdx].script = e.target.value;
                                    setStoryboardShots(next);
                                  }}
                                  className="w-full bg-black border border-white/5 p-2 rounded-lg text-zinc-300 focus:outline-none resize-none text-[11.5px]"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[9px] font-mono uppercase text-zinc-400 bg-[#06030b]/60 p-3 rounded-2xl border border-purple-500/5">
                              <div>
                                <span className="text-zinc-650 block">Camera movement</span>
                                <span className="text-white block font-bold truncate">{shot.camera}</span>
                              </div>
                              <div>
                                <span className="text-zinc-655 block">Audio Frequency</span>
                                <span className="text-[#00e5ff] block font-bold truncate">{shot.mood}</span>
                              </div>
                              <div>
                                <span className="text-zinc-655 block">Action Focus</span>
                                <span className="text-emerald-400 block font-bold truncate">Resource Approved</span>
                              </div>
                              <div>
                                <span className="text-zinc-655 block">NexoraX Tag</span>
                                <span className="text-amber-400 block font-bold truncate">SC887095-NODE</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* STORY CONTROLS PANEL */}
                    <div className="space-y-6">
                      <div className="bg-black/55 border border-purple-500/20 p-5 rounded-3xl space-y-4">
                        <span className="text-[9px] font-mono text-purple-400 uppercase tracking-widest block font-black">★ BLUEPRINT EXPORT CONTROLS</span>
                        <p className="text-zinc-400 text-xs leading-relaxed">
                          Export complete scene matrices. These scripts will be bundled, matching standard voice tracks automatically, before posting directly to social streams.
                        </p>

                        <div className="space-y-3 pt-2">
                          <button
                            onClick={() => {
                              triggerToast("Generating vertical short Reels & TikTok pack...", "info");
                              setRenderingLogs(logs => [
                                `[${new Date().toLocaleTimeString()}] STORYBOARD EXPORT: Initiating vertical aspect transcoders`,
                                `[${new Date().toLocaleTimeString()}] Framing face tracking coordinates... OK`,
                                ...logs
                              ]);
                            }}
                            className="w-full py-2.5 bg-gradient-to-r from-purple-600 via-pink-500 to-amber-500 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all select-none cursor-pointer text-center block"
                          >
                            Compile Storyboard Pack
                          </button>

                          <button
                            onClick={() => {
                              setStoryboardShots([
                                {
                                  scene: "Scene 1: Hook (First 3s)",
                                  hook: "Holographic Resource timeline countdown displaying £5,100,000",
                                  problem: "Traditional systems leak up to 35% productivity",
                                  solution: "Unveiling Nexorax NexoraX Autopilot integration",
                                  demo: "Node-09 server terminal locking at 0.8s latency",
                                  proof: "Sort code Verified Payment Configuration Secure Payment Provider settlement logs",
                                  cta: "Call Director Mahmood direct line: Private Data Hidden",
                                  script: "In the name of Allah. Stop wasting institutional capital. The NexoraX NexoraX Resource resource is ready for deployment.",
                                  camera: "Aggressive push-in with macro lens highlighting cyber circuitry",
                                  voiceover: "Deep authoritative synthetic cadence, zero fluff.",
                                  subtitle: "REVEAL: £5,100,000 NEXORAX resource DEPLOYMENT",
                                  mood: "Cinematic Dark Bass Rumble",
                                  format: "Vertical 9:16 Cinematic"
                                },
                                {
                                  scene: "Scene 2: Problem (3s - 7s)",
                                  hook: "Red flashing alerts on conventional accounting grids",
                                  problem: "Manual screening and standard Client Resource Workflow queues delay compliance clearance by 45+ days",
                                  solution: "Immediate automated extraction protocols",
                                  demo: "NexoraX Mail Engine drafting Services Guides",
                                  proof: "Staff Coordinator remote sync node confirmation screens",
                                  cta: "Bypass administrator queues instantly",
                                  script: "Traditional Client Resource Workflow agencies are outdated bottleneck servers. They fail the 0.8s operational limit.",
                                  camera: "Glitch zoom with fast pan left",
                                  voiceover: "Tense professional urgency, sharp crisp delivery.",
                                  subtitle: "SYSTEM WARNING: Client Resource Workflow BOTTLENECKS DETECTED",
                                  mood: "Industrial Harsh Synth Pulse",
                                  format: "Vertical 9:16 Cinematic"
                                },
                                {
                                  scene: "Scene 3: Solution (7s - 12s)",
                                  hook: "Clean obsidian glass card fading in smoothly with high-contrast text",
                                  problem: "Legacy placement friction",
                                  solution: "Direct integration of Private Secretaries matching the NexoraX NexoraX billing records grant structure",
                                  demo: "Secure Payment Provider Secure Credential Stored Server-Side direct billing handshake protocol",
                                  proof: "100% Zero-Fund corporate backing confirmed",
                                  cta: "Call Private Data Hidden to hook direct candidate interview",
                                  script: "NexoraX bypasses standard agency loops. We anchor placement fees on the NexoraX billing records scheme under zero-cost mandates.",
                                  camera: "Slow track right showing glowing violet guidelines",
                                  voiceover: "Confident, executive, flawless cadence.",
                                  subtitle: "SOLUTION: TIER-ONE Secure Invoice Register ACQUISITION (ZERO-FUND MANDATE)",
                                  mood: "Ambient Sci-Fi Uplifting Pad",
                                  format: "Vertical 9:16 Cinematic"
                                }
                              ]);
                              triggerToast("Chronological timeline reset to NexoraX Standard.", "success");
                            }}
                            className="w-full py-2 bg-black border border-white/10 hover:border-purple-500/20 text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer block text-center"
                          >
                            Reset with Director Standard
                          </button>
                        </div>
                      </div>

                      {/* Resource resource ALIGNMENT CHECKLIST */}
                      <div className="bg-[#05030c] border border-emerald-500/20 p-5 rounded-3xl space-y-3.5 shadow-[0_0_15px_rgba(16,185,129,0.1)]">
                        <span className="text-[9.5px] font-mono text-emerald-400 uppercase tracking-widest block font-black">✓ £150,000 Resource ALIGNMENT STATUS</span>
                        <div className="space-y-2.5 text-[10.5px] font-mono">
                          <div className="flex items-center gap-2 text-emerald-300 font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            <span>NexoraX billing records NI Secure Credential Stored Server-Side included.</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-300 font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            <span>Secure Payment Provider details Sort: Verified Payment Configuration verified.</span>
                          </div>
                          <div className="flex items-center gap-2 text-yellow-400 font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
                            <span>Active candidate interview link active.</span>
                          </div>
                          <p className="text-[9px] text-zinc-500 leading-normal pt-1.5 border-t border-white/5 font-sans">
                            NexoraX CloudSync-02 has checked this script pack against corporate compliance policies. Face references are 100% stable.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* CHARACTER LIBRARY */}
              {activePanel === "Character Library" && (
                <div className="space-y-6 text-left animate-fadeIn">
                  {/* HERO HEADER */}
                  <div className="relative p-6 rounded-3xl bg-gradient-to-r from-purple-955/40 via-cyan-955/40 to-black border border-purple-500/30 overflow-hidden shadow-[0_4px_30px_rgba(0,0,0,0.5)]">
                    <div className="absolute top-0 right-0 p-3 flex gap-2">
                      <span className="text-[8px] font-mono bg-cyan-950 text-cyan-300 px-2 py-0.5 rounded-full border border-cyan-500/30 uppercase font-black">BIOMETRICS LOG</span>
                      <span className="text-[8px] font-mono bg-purple-900/40 text-[#00e5ff] px-2 py-0.5 rounded-full border border-purple-500/35 uppercase font-bold animate-pulse">IDENTITY SYNC v9.2</span>
                    </div>

                    <div className="max-w-2xl space-y-1">
                      <span className="text-[8.5px] font-mono text-[#00e5ff] uppercase tracking-widest font-black block">NEXORAX NEXORAX CREATIVE SUITE</span>
                      <h3 className="text-xl md:text-2xl font-sans font-black text-white tracking-tight leading-none">
                        Character Library
                      </h3>
                      <p className="text-zinc-400 text-xs md:text-sm leading-relaxed pt-1">
                        Manage professional actors, remote-node technicians, and AI security avatars. Lock face descriptions, clothing parameters, and custom synthetic voice clones to prevent facial variance drift.
                      </p>
                    </div>
                  </div>

                  {/* MAIN BENTO LAYOUT */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* CHARACTER SELECTOR SIDEBAR (1/3) */}
                    <div className="space-y-4">
                      {characterRegistry.map((char) => {
                        const isSelected = selectedCharId === char.id;
                        // Map standard avatars
                        let avatarUrl = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=80";
                        if (char.id === "char-1") avatarUrl = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&q=80";
                        if (char.id === "char-2") avatarUrl = "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=200&q=80";
                        if (char.id === "char-3") avatarUrl = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=200&q=80";

                        return (
                          <div
                            key={char.id}
                            onClick={() => {
                              setSelectedCharId(char.id);
                              triggerToast(`Locked tracking cursor to character: ${char.name}`, "info");
                            }}
                            className={`p-4 rounded-3xl border text-left transition-all cursor-pointer flex gap-3.5 items-center ${
                              isSelected 
                                ? "bg-gradient-to-r from-purple-950/20 to-[#0e041e]/80 border-purple-500/50 text-white shadow-[0_0_20px_rgba(168,85,247,0.1)]" 
                                : "bg-black/60 border-white/5 text-zinc-400 hover:border-purple-500/20"
                            }`}
                          >
                            <img 
                              src={avatarUrl} 
                              alt={char.name} 
                              className="w-12 h-12 rounded-2xl object-cover border-2 border-purple-500/20 flex-shrink-0"
                              referrerPolicy="no-referrer"
                            />
                            <div className="flex-1 min-w-0">
                              <span className="text-[8px] font-mono block text-[#00e5ff] uppercase tracking-wider font-bold">{char.role}</span>
                              <span className="text-sm font-bold block truncate text-white mt-0.5">{char.name}</span>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[7.5px] font-mono text-zinc-500">{char.activeCampaigns} campaigns</span>
                                <span className="w-1 h-1 rounded-full bg-purple-500" />
                                <span className="text-[7.5px] font-mono text-emerald-400">Stable Biometrics</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* CREATE NEW PERSONA FORM */}
                      <div className="bg-[#0b0515]/90 border border-purple-500/20 p-5 rounded-3xl space-y-4 text-xs font-mono">
                        <span className="text-[9px] text-purple-400 block uppercase font-black">★ ENLIST NEW CHARACTER PROFILE</span>
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <label className="text-zinc-500 text-[8.5px] uppercase font-bold">Character/Candidate Name</label>
                            <input 
                              type="text"
                              value={newCharName}
                              placeholder="e.g. Claire Vance"
                              onChange={(e) => setNewCharName(e.target.value)}
                              className="w-full bg-black border border-white/10 p-2 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-zinc-500 text-[8.5px] uppercase font-bold">Corporate Faculty Role</label>
                            <input 
                              type="text"
                              value={newCharRole}
                              placeholder="e.g. Lead Secure Invoice Register Director"
                              onChange={(e) => setNewCharRole(e.target.value)}
                              className="w-full bg-black border border-white/10 p-2 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-zinc-500 text-[8.5px] uppercase font-bold">Biometric Face Prompt</label>
                            <textarea 
                              rows={2}
                              value={newCharRefPrompt}
                              placeholder="Describe hair color, age, suitability tags..."
                              onChange={(e) => setNewCharRefPrompt(e.target.value)}
                              className="w-full bg-black border border-white/10 p-2 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px] resize-none leading-relaxed"
                            />
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            if (!newCharName || !newCharRole) {
                              triggerToast("Mandatory fields: Character Name and Role must be completed.", "info");
                              return;
                            }
                            const newCh = {
                              id: `char-${Date.now()}`,
                              name: newCharName,
                              role: newCharRole,
                              refImage: newCharRefPrompt || "Portrait of elite corporate candidate, executive studio lighting",
                              faceAngle: "Front view, sharp corporate aesthetic",
                              outfit: "Tailored blue wool blazer",
                              voice: "Clear neutral mid-frequency voice track",
                              personality: "Determined, highly focused executive assistant",
                              activeCampaigns: 1,
                              posePreset: "Sitting at workspace desk",
                              exprPreset: "Polished serene smile"
                            };
                            setCharacterRegistry([...characterRegistry, newCh]);
                            setNewCharName("");
                            setNewCharRole("");
                            setNewCharRefPrompt("");
                            triggerToast(`Interactive Character "${newCh.name}" successfully loaded into library!`, "success");
                          }}
                          className="w-full py-2 bg-gradient-to-r from-purple-600 to-cyan-500 hover:opacity-95 text-black font-mono font-black text-[10px] uppercase tracking-widest rounded-xl transition-all cursor-pointer text-center block"
                        >
                          Enlist Talent Node
                        </button>
                      </div>
                    </div>

                    {/* FOCUSED CHARACTER CONFIGURATION SPACE (2/3) */}
                    <div className="lg:col-span-2">
                      {(() => {
                        const char = characterRegistry.find(c => c.id === selectedCharId);
                        if (!char) return (
                          <div className="bg-black/40 border border-white/5 rounded-3xl p-8 text-center text-xs text-zinc-500 font-mono">
                            Select an active character node on the left to configure biometric anchors.
                          </div>
                        );

                        let faceUrl = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=400&q=80";
                        if (char.id === "char-1") faceUrl = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80";
                        if (char.id === "char-2") faceUrl = "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=600&q=80";
                        if (char.id === "char-3") faceUrl = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=600&q=80";

                        return (
                          <div className="bg-gradient-to-b from-[#0b0515]/90 to-black border-2 border-purple-500/20 p-6 rounded-3xl space-y-6">
                            <div className="flex flex-col sm:flex-row gap-5 pb-5 border-b border-white/5">
                              <img 
                                src={faceUrl} 
                                alt={char.name} 
                                className="w-24 h-24 rounded-3xl object-cover border border-purple-500/35 shadow-[0_0_15px_rgba(168,85,247,0.2)] flex-shrink-0"
                                referrerPolicy="no-referrer"
                              />
                              <div className="space-y-1 text-left flex-1 min-w-0">
                                <span className="text-[9px] font-mono text-[#00e5ff] uppercase font-bold tracking-widest">{char.role}</span>
                                <h4 className="text-xl font-bold text-white tracking-tight leading-none mt-1">{char.name}</h4>
                                <p className="text-zinc-400 text-xs pt-1">
                                  Identity preset used to guide image synthesis. Facial coordinate vectors are frozen inside bare-metal cache pipelines to prevent temporal distortion.
                                </p>
                              </div>
                            </div>

                            {/* CONFIG FIELDS */}
                            <div className="space-y-4 text-xs font-mono">
                              <div className="space-y-1.5">
                                <span className="text-[8.5px] font-mono text-zinc-500 uppercase block">Neural Face consistency prompt details (SD-v9 Anchor)</span>
                                <textarea
                                  rows={3}
                                  value={char.refImage}
                                  onChange={(e) => {
                                    const next = characterRegistry.map(item => item.id === selectedCharId ? { ...item, refImage: e.target.value } : item);
                                    setCharacterRegistry(next);
                                  }}
                                  className="w-full bg-black/85 border border-white/10 p-3 rounded-xl text-xs font-mono text-white focus:outline-none focus:border-[#00e5ff] resize-none leading-relaxed"
                                />
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                  <span className="text-zinc-500 text-[8.5px] uppercase block font-bold">Woven Outfit parameters</span>
                                  <input 
                                    type="text"
                                    value={char.outfit}
                                    onChange={(e) => {
                                      const next = characterRegistry.map(item => item.id === selectedCharId ? { ...item, outfit: e.target.value } : item);
                                      setCharacterRegistry(next);
                                    }}
                                    className="w-full bg-black/80 border border-white/5 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500"
                                  />
                                </div>

                                <div className="space-y-1.5">
                                  <span className="text-zinc-500 text-[8.5px] uppercase block font-bold">AI Voice Dialect Track</span>
                                  <input 
                                    type="text"
                                    value={char.voice}
                                    onChange={(e) => {
                                      const next = characterRegistry.map(item => item.id === selectedCharId ? { ...item, voice: e.target.value } : item);
                                      setCharacterRegistry(next);
                                    }}
                                    className="w-full bg-black/80 border border-white/5 p-2.5 rounded-xl text-[#00e5ff] focus:outline-none focus:border-[#00e5ff]"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                  <span className="text-zinc-500 text-[8.5px] uppercase block font-bold">Pose Placement Setting</span>
                                  <input 
                                    type="text"
                                    value={char.posePreset}
                                    onChange={(e) => {
                                      const next = characterRegistry.map(item => item.id === selectedCharId ? { ...item, posePreset: e.target.value } : item);
                                      setCharacterRegistry(next);
                                    }}
                                    className="w-full bg-black/80 border border-white/5 p-2.5 rounded-xl text-white focus:outline-none"
                                  />
                                </div>

                                <div className="space-y-1.5">
                                  <span className="text-zinc-500 text-[8.5px] uppercase block font-bold">Emotional Expression Preset</span>
                                  <input 
                                    type="text"
                                    value={char.exprPreset}
                                    onChange={(e) => {
                                      const next = characterRegistry.map(item => item.id === selectedCharId ? { ...item, exprPreset: e.target.value } : item);
                                      setCharacterRegistry(next);
                                    }}
                                    className="w-full bg-black/80 border border-white/5 p-2.5 rounded-xl text-white focus:outline-none"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-center text-[9px] uppercase">
                                <div className="p-3.5 bg-[#030107] border border-white/5 rounded-2xl">
                                  <span className="text-zinc-500 block">BIOMETRIC HASH</span>
                                  <span className="text-zinc-350 font-bold block mt-1 truncate">SC887-NODE-03</span>
                                </div>
                                <div className="p-3.5 bg-[#030107] border border-white/5 rounded-2xl">
                                  <span className="text-zinc-550 block">SENSE HEARTBEAT</span>
                                  <span className="text-[#00e5ff] font-black block mt-1">0.8s SYNC SECURE</span>
                                </div>
                                <div className="p-3.5 bg-[#030107] border border-white/10 rounded-2xl">
                                  <span className="text-zinc-550 block">COMPLIANCE ROUTING SPON</span>
                                  <span className="text-emerald-400 font-bold block mt-1">✓ HOME OFFICE APPROVED</span>
                                </div>
                              </div>

                              <button
                                onClick={() => {
                                  triggerToast(`Facial consistency metrics locked. Mapped to campaign pipelines!`, "success");
                                  setRenderingLogs(logs => [
                                    `[${new Date().toLocaleTimeString()}] RECALIBRATE: Face anchors locked for character ${char.name}`,
                                    `[${new Date().toLocaleTimeString()}] Standard dialect model: ${char.voice}`,
                                    ...logs
                                  ]);
                                }}
                                className="w-full py-2.5 bg-gradient-to-r from-purple-700 via-purple-600 to-cyan-500 hover:opacity-95 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer text-center block"
                              >
                                Synchronize Character Anchor
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* VARIATION LAB */}
              {activePanel === "Variation Lab" && (
                <div className="space-y-6 text-left animate-fadeIn">
                  {/* HERO HEADER */}
                  <div className="relative p-6 rounded-3xl bg-gradient-to-r from-purple-955/40 via-purple-955/40 to-black border border-purple-500/30 overflow-hidden shadow-[0_4px_30px_rgba(0,0,0,0.5)] bg-cover" style={{backgroundImage: "radial-gradient(at top right, rgba(0, 229, 255, 0.1), transparent)"}}>
                    <div className="absolute top-0 right-0 p-3 flex gap-2">
                      <span className="text-[8px] font-mono bg-[#00e5ff]/20 text-[#00e5ff] px-2 py-0.5 rounded-full border border-[#00e5ff]/30 uppercase font-black">A/B CRITERIA</span>
                      <span className="text-[8px] font-mono bg-purple-900/45 text-purple-300 px-2 py-0.5 rounded-full border border-purple-500/35 uppercase font-bold animate-pulse">MONTE CARLO v9</span>
                    </div>

                    <div className="max-w-2xl space-y-1">
                      <span className="text-[8.5px] font-mono text-[#00e5ff] uppercase tracking-widest font-black block">NEXORAX NEXORAX CREATIVE SUITE</span>
                      <h3 className="text-xl md:text-2xl font-sans font-black text-white tracking-tight leading-none">
                        Variation Lab
                      </h3>
                      <p className="text-zinc-400 text-xs md:text-sm leading-relaxed pt-1">
                        Synthesize, edit, and split-test performance elements. Run automated simulation models to evaluate projected CTR, engagement frequencies, and Gavel compliance scores before campaign launches.
                      </p>
                    </div>
                  </div>

                  {/* SPLIT SWITCH PANEL AND CHART MAP */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* LEFTSIDE SPLIT TYPE SELECTION AND ADD VARIANT PANEL (1/3) */}
                    <div className="space-y-4">
                      <div className="bg-black/60 border border-white/5 p-4 rounded-3xl text-left space-y-3.5">
                        <span className="text-[9.5px] font-mono text-purple-400 uppercase tracking-widest block font-black">★ MULTIVARIATE PARAMETER SPLITS</span>
                        <div className="flex bg-black rounded-xl p-1 border border-white/10 gap-1 text-[10px] font-mono">
                          {["Hooks", "Thumbnails", "CTAs"].map((vType) => (
                            <button
                              key={vType}
                              onClick={() => {
                                setSelectedVariationType(vType as any);
                                triggerToast(`Variation split locked: ${vType}`, "info");
                                if (vType === "Hooks") {
                                  setVariationMetrics([
                                    { label: "Variation A (Shock Angle)", hook: "The £150,000 secret traditional agencies hope you never discover.", ctr: "12.8%", engagement: "94.2%", score: 98 },
                                    { label: "Variation B (NexoraX Authority)", hook: "Direct mandate from Director Mahmood: Tier-One Secure Invoice Register acquisition.", ctr: "9.5%", engagement: "81.6%", score: 87 },
                                    { label: "Variation C (Zero-Cost Focus)", hook: "Bypass standard fee templates. All placement fees fully endowed via Secure Payment Provider.", ctr: "14.1%", engagement: "88.9%", score: 94 }
                                  ]);
                                } else if (vType === "Thumbnails") {
                                  setVariationMetrics([
                                    { label: "Thumb A (Purple Glass Visor)", hook: "Eleanor Vance looking focused representing 0.8s sync state", ctr: "10.4%", engagement: "86.1%", score: 89 },
                                    { label: "Thumb B (Gavel Strike)", hook: "Close-up visual of a physical gavel hitting digital ledger line", ctr: "15.2%", engagement: "92.5%", score: 97 },
                                    { label: "Thumb C (SecurePayment Gold Card)", hook: "Secure Payment Provider account metrics displayed as cyber shield layout", ctr: "11.1%", engagement: "85.8%", score: 88 }
                                  ]);
                                } else {
                                  setVariationMetrics([
                                    { label: "CTA A (Zero-Cost Focus)", hook: "Circumvent recruiter queues. Dial coordinator Private Data Hidden", ctr: "13.6%", engagement: "89.2%", score: 93 },
                                    { label: "CTA B (Imperial Mandate)", hook: "Reply immediately to clear direct NexoraX invoice on-queue", ctr: "9.1%", engagement: "80.4%", score: 82 },
                                    { label: "CTA C (Phone Hotline)", hook: "Call Director's direct operations hotline instantly to verify", ctr: "14.9%", engagement: "95.0%", score: 99 }
                                  ]);
                                }
                              }}
                              className={`flex-1 py-1.5 rounded-lg text-center font-bold tracking-wider uppercase transition-all cursor-pointer ${
                                selectedVariationType === vType 
                                  ? "bg-purple-600 text-black font-black" 
                                  : "text-[#88888b] hover:text-white"
                              }`}
                            >
                              {vType}
                            </button>
                          ))}
                        </div>
                        <p className="text-zinc-500 text-[10px] leading-relaxed">
                          Click to select split tracks. This updates the live matrices on the right to track estimated clicks under artificial feedback sweeps.
                        </p>
                      </div>

                      {/* ADD CUSTOM VARIATION FORM */}
                      <div className="bg-[#0b0515]/95 border border-purple-500/20 p-5 rounded-3xl space-y-4 text-xs font-mono">
                        <span className="text-[9.5px] text-purple-400 block uppercase font-black">★ GENERATE CUSTOM VARIATION</span>
                        <div className="space-y-3.5">
                          <div>
                            <label className="text-zinc-500 text-[8.5px] uppercase block mb-1 font-bold">Variant Identity Label</label>
                            <input 
                              type="text"
                              placeholder="e.g. Variation D (Founder-led)"
                              value={newVariantLabel}
                              onChange={(e) => setNewVariantLabel(e.target.value)}
                              className="w-full bg-black border border-white/10 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                            />
                          </div>
                          <div>
                            <label className="text-zinc-500 text-[8.5px] uppercase block mb-1 font-bold">Variant Copy Core Hook text</label>
                            <textarea 
                              rows={2}
                              placeholder="Type variant text or copy target..."
                              value={newVariantHook}
                              onChange={(e) => setNewVariantHook(e.target.value)}
                              className="w-full bg-black border border-white/10 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px] resize-none leading-relaxed"
                            />
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            if (!newVariantLabel || !newVariantHook) {
                              triggerToast("Variant identity label and hook copy must be filled.", "info");
                              return;
                            }
                            const rawScore = Math.floor(Math.random() * 15) + 84;
                            const newV = {
                              label: newVariantLabel,
                              hook: newVariantHook,
                              ctr: `${(rawScore * 0.14).toFixed(1)}%`,
                              engagement: `${(rawScore * 0.95).toFixed(1)}%`,
                              score: rawScore
                            };
                            setVariationMetrics([...variationMetrics, newV]);
                            setNewVariantLabel("");
                            setNewVariantHook("");
                            triggerToast(`Variant Successfully enqueued in [${selectedVariationType}] split matrix!`, "success");
                          }}
                          className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-[#12bdff] hover:opacity-95 text-black font-mono font-black text-[10px] uppercase tracking-widest rounded-xl transition-all cursor-pointer text-center block"
                        >
                          Enlist Custom Variant
                        </button>
                      </div>
                    </div>

                    {/* ACTIVE VARIANT SCORES AND RECHARTS COMPARISON (2/3) */}
                    <div className="lg:col-span-2 space-y-5">
                      {/* SIMULATION SUMMARY CARDS */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-black/60 border border-white/5 p-4 rounded-3xl text-left space-y-1">
                          <span className="text-[8.5px] font-mono text-zinc-550 uppercase block">Active Winning Champion</span>
                          <span className="text-sm font-bold text-white block uppercase">
                            {variationMetrics.reduce((prev, current) => (prev.score > current.score) ? prev : current, {label: "None"}).label}
                          </span>
                          <span className="text-[10px] font-mono text-emerald-400 block pt-1">
                            Optimized CTR: {variationMetrics.reduce((prev, current) => (prev.score > current.score) ? prev : current, {ctr: "0%"}).ctr}
                          </span>
                        </div>

                        <div className="bg-black/60 border border-[#00e5ff]/20 p-4 rounded-3xl text-left space-y-1">
                          <span className="text-[8.5px] font-mono text-zinc-550 uppercase block">Simulation Status Checks</span>
                          <span className="text-sm font-bold text-[#00e5ff] block uppercase">MONTE CARLO RUNNING</span>
                          <span className="text-[10px] font-mono text-[#00e5ff]/80 block pt-1">
                            High confidence metrics locked in vaults.
                          </span>
                        </div>
                      </div>

                      {/* DETAILED CARDS OF VARIANT METRICS */}
                      <div className="space-y-4">
                        {variationMetrics.map((v, vIdx) => {
                          const isWinner = vIdx === variationMetrics.reduce((maxIdx, current, idx, arr) => current.score > arr[maxIdx].score ? idx : maxIdx, 0);
                          return (
                            <div 
                              key={vIdx} 
                              className={`bg-gradient-to-r from-black/80 to-[#0e0618] border p-4 rounded-3xl text-left space-y-3 relative transition-all ${
                                isWinner 
                                  ? "border-amber-400/55 shadow-[0_0_20px_rgba(251,191,36,0.06)]" 
                                  : "border-white/5 hover:border-purple-500/25"
                              }`}
                            >
                              {isWinner && (
                                <span className="absolute top-4 right-4 bg-amber-400 text-black font-mono font-black text-[7.5px] px-2 py-0.5 rounded-full border border-amber-300 shadow-[0_0_8px_rgba(251,191,36,0.3)] select-none animate-pulse">
                                  ★ CHAMPION
                                </span>
                              )}

                              <div className="flex justify-between items-center pr-12">
                                <span className="font-mono font-black text-white text-[11px] uppercase">{v.label}</span>
                                <span className={`text-[10.1px] font-mono font-bold ${
                                  v.score >= 95 ? "text-emerald-400" : v.score >= 90 ? "text-cyan-300" : "text-purple-400"
                                }`}>
                                  Score: {v.score} / 100
                                </span>
                              </div>

                              <p className="text-zinc-450 font-mono text-[10px] bg-[#020104]/80 p-2.5 rounded-xl border border-white/5 leading-relaxed">
                                "{v.hook}"
                              </p>

                              {/* Relative Progress bars as simulated charts */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[9px] font-mono pt-1 text-zinc-550 bg-transparent">
                                <div className="space-y-1">
                                  <div className="flex justify-between items-center text-zinc-500">
                                    <span>Simulated CTR Vector</span>
                                    <span className="text-white font-bold">{v.ctr}</span>
                                  </div>
                                  <div className="w-full bg-black/60 h-1.5 rounded-full overflow-hidden border border-white/5">
                                    <div className="bg-[#00e5ff] h-full rounded-full" style={{ width: v.ctr }} />
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <div className="flex justify-between items-center text-zinc-500">
                                    <span>Engagement probability</span>
                                    <span className="text-[#00e5ff] font-bold">{v.engagement}</span>
                                  </div>
                                  <div className="w-full bg-black/60 h-1.5 rounded-full overflow-hidden border border-white/5">
                                    <div className="bg-purple-500 h-full rounded-full" style={{ width: v.engagement }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* TRIGGER SIMULATOR RUN */}
                      <div className="bg-black/45 border border-purple-500/10 p-5 rounded-3xl flex flex-col md:flex-row gap-4 items-center justify-between">
                        <div className="space-y-1 text-left">
                          <span className="text-[11px] font-sans font-black text-white block uppercase">Monte Carlo CTR Sweep Optimization</span>
                          <p className="text-zinc-500 text-[10px]">
                            Simulate up to 10,000 algorithmic view loops to adjust probability models.
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            const next = variationMetrics.map(v => {
                              const rawScore = Math.floor(Math.random() * 20) + 80;
                              return {
                                ...v,
                                score: rawScore,
                                ctr: `${(rawScore * 0.15).toFixed(1)}%`,
                                engagement: `${(rawScore * 0.97).toFixed(1)}%`
                              };
                            });
                            setVariationMetrics(next);
                            triggerToast("Recalculating variation CTR rankings dynamically under Monte Carlo Sweep...", "success");
                            setRenderingLogs(logs => [
                              `[${new Date().toLocaleTimeString()}] MONTE CARLO: Dynamic sweeps concluded over 10,000 simulated iterations`,
                              `[${new Date().toLocaleTimeString()}] Highest simulated score locked: ${Math.max(...next.map(n => n.score))} / 100`,
                              ...logs
                            ]);
                          }}
                          className="px-6 py-2.5 bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 text-black font-mono font-black text-[10.5px] uppercase tracking-widest rounded-xl transition-all cursor-pointer hover:scale-[1.02] flex-shrink-0"
                        >
                          Execute Sweep Optimization
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 2. PROMPT PLANNER */}
              {activePanel === "Prompt Planner" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-[#00e5ff] uppercase tracking-widest">★ STRUCTURAL PROMPT ARCHITECT</h4>
                    <p className="text-zinc-400 text-xs">
                      Compose neural command parameters utilizing preset NexoraX core vectors to ensure optimal Resource alignment indexes during generation steps.
                    </p>

                    <div className="space-y-2">
                      <label className="text-[10px] font-mono text-zinc-400 block">RAW SEED TEXT / PROMPT CONCEPT</label>
                      <textarea
                        value={currentPrompt}
                        onChange={(e) => setCurrentPrompt(e.target.value)}
                        className="w-full bg-black/60 border border-white/10 p-3.5 rounded-xl text-xs font-mono text-white focus:outline-none focus:border-[#00e5ff] transition-all min-h-[100px] resize-none"
                        placeholder="Type high-fidelity concept context..."
                      />
                    </div>

                    {/* Style presets toggler */}
                    <div className="space-y-2 pt-2">
                      <label className="text-[10px] font-mono text-zinc-400 block">APPLY DYNAMIC STYLE PARAMETER</label>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {STYLE_PRESETS.map((style) => (
                          <button
                            key={style.id}
                            onClick={() => {
                              setSelectedStyle(style.id);
                              // Append style text if not present
                              if (!currentPrompt.includes(style.promptAdd)) {
                                setCurrentPrompt(prev => `${prev}, ${style.promptAdd}`);
                              }
                            }}
                            className={`p-2 rounded-xl text-[9px] uppercase tracking-wide font-mono text-center border transition-all select-none cursor-pointer ${
                              selectedStyle === style.id
                                ? "bg-purple-950/50 border-[#e840f3] text-[#e840f3] font-bold"
                                : "bg-black/40 border-white/5 text-zinc-400 hover:border-white/20"
                            }`}
                          >
                            {style.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Predefined prompts shortcuts */}
                    <div className="space-y-2 pt-2">
                      <label className="text-[10px] font-mono text-zinc-400 block">FAVORITE MASTER CONCEPTS</label>
                      <div className="space-y-2">
                        {PROMPT_TEMPLATES.map((tmpl, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              setCurrentPrompt(tmpl);
                              triggerToast("Concept template locked into memory slot.", "info");
                            }}
                            className="w-full text-left p-2.5 rounded-xl bg-black/40 border border-white/5 text-zinc-400 hover:border-purple-500/30 hover:text-white transition-all text-xs font-mono truncate"
                          >
                            Concept #{idx+1}: {tmpl}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Prompt Enhancer Button */}
                    <div className="pt-2 flex gap-3">
                      <button
                        onClick={() => {
                          setCurrentPrompt(prev => prev + `, cinematic lighting, rich contrast shadows, high fidelity depth of field, optimized for SC887095 platform`);
                          triggerToast("Prompt expanded with Neural Enhancement tokens.", "success");
                        }}
                        className="px-4 py-2 border border-[#00e5ff]/30 text-[#00e5ff] hover:bg-[#00e5ff]/10 font-mono text-[9.5px] uppercase tracking-widest rounded-xl transition-all cursor-pointer select-none inline-flex items-center gap-1.5"
                      >
                        <Sparkles size={11} />
                        <span>NexoraX Neural Expander</span>
                      </button>

                      <button
                        onClick={() => {
                          setCurrentPrompt("");
                          triggerToast("Workspace prompt cleared.", "info");
                        }}
                        className="px-4 py-2 border border-zinc-800 text-zinc-500 hover:border-white/10 hover:text-white font-mono text-[9.5px] uppercase tracking-widest rounded-xl transition-all cursor-pointer select-none"
                      >
                        Reset Buffer
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 3. AI IMAGE GENERATOR */}
              {activePanel === "AI Image Generator" && (
                <div className="space-y-6 text-left">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-widest">★ TEXT TO IMAGE RENDER MATRIX</h4>
                      
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-mono text-zinc-400 block">GENERATIVE COGNITIVE PROMPT</label>
                        <textarea
                          value={currentPrompt}
                          onChange={(e) => setCurrentPrompt(e.target.value)}
                          className="w-full bg-black/60 border border-white/10 p-3 rounded-xl text-xs font-mono text-white focus:outline-none focus:border-purple-500 transition-all min-h-[80px]"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block">ASPECT RATIO</label>
                          <select
                            value={aspectRatio}
                            onChange={(e) => setAspectRatio(e.target.value)}
                            className="w-full bg-black/60 border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none focus:border-purple-500"
                          >
                            <option value="16:9">16:9 Landscape (YouTube)</option>
                            <option value="9:16">9:16 Portrait (Shorts/TikTok)</option>
                            <option value="1:1">1:1 Square (Instagram)</option>
                            <option value="4:5">4:5 Medium (Social Grid)</option>
                            <option value="21:9">21:9 Ultra Cinematic</option>
                          </select>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block">MODEL PRESET QUALITY</label>
                          <select
                            value={renderQuality}
                            onChange={(e) => setRenderQuality(e.target.value)}
                            className="w-full bg-black/60 border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none"
                          >
                            <option value="SDXL-M9">SDXL Master Render v9.2</option>
                            <option value="Midjourney-v6">Midjourney v6 Neural</option>
                            <option value="Ultra 8K DeepRender">Ultra 8K DeepRender Cinema</option>
                            <option value="Decentralized-Vector">Decentralized Clean Vector Code</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex justify-between text-[10px] font-mono text-zinc-400">
                          <span>STEPS SAMPLER INFERENCE COUNT</span>
                          <span className="text-[#00e5ff] font-bold">50 Steps (Max Fidelity)</span>
                        </div>
                        <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden">
                          <div className="bg-gradient-to-r from-purple-500 to-[#00e5ff] h-full w-[80%]" />
                        </div>
                      </div>

                      <button
                        onClick={() => simulateRender("image", `Text to Image: ${currentPrompt.substring(0, 30)}`)}
                        disabled={isGenerating}
                        className="w-full py-3 bg-gradient-to-r from-purple-505 to-[#00e5ff] bg-purple-600 hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all duration-300 hover:scale-[1.01] hover:shadow-[0_0_20px_rgba(0,229,255,0.35)] select-none cursor-pointer flex items-center justify-center gap-2"
                      >
                        <Wand2 size={14} className="text-black font-black" />
                        <span>GENERATE IMAGE LAYER</span>
                      </button>
                    </div>

                    <div className="bg-black/50 border border-white/5 p-4 rounded-2xl flex flex-col justify-between space-y-4">
                      <span className="text-[9px] font-mono text-zinc-500 uppercase block">ACTIVE IMAGE ENGINE PORTAL</span>
                      
                      <div className="flex-1 flex flex-col items-center justify-center text-center">
                        {isGenerating && generateType === "image" ? (
                          <div className="space-y-3">
                            <RefreshCw size={26} className="text-[#00e5ff] animate-spin mx-auto animate-[spin_1.5s_linear_infinite]" />
                            <span className="text-xs font-mono text-[#00e5ff] block font-bold">RESERVING VEO COGNITION SLOT: {generateProgress}%</span>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="rounded-xl overflow-hidden border border-white/5 max-h-[180px]">
                              <img
                                src={assets[0].url}
                                alt="Studio Preset"
                                referrerPolicy="no-referrer"
                                className="w-full object-cover"
                              />
                            </div>
                            <span className="text-[10px] font-mono text-zinc-400 block max-w-xs mx-auto">Latest compiled preview frame. Click "Export to Social" to package this node asset.</span>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-white/5 pt-2 text-[9px] font-mono text-zinc-500 flex justify-between">
                        <span>EST COST: £0.01 (Levied)</span>
                        <span>RESOLUTION: 4096 x 2304</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 4. IMAGE TRANSFORMER */}
              {activePanel === "Image Transformer" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-pink-400 uppercase tracking-widest">★ IMAGE TO IMAGE RE-SYNTHESIZER (TRANSFORM)</h4>
                    <p className="text-zinc-450 text-xs">
                      Repaint, re-style, or inject new semantic context into existant image assets using the VEO multi-pass transform canvas.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-black/45 p-4 rounded-2xl border border-white/5">
                      <div className="space-y-4">
                        <label className="text-[10px] font-mono text-zinc-400 block uppercase">SOURCE FILE CONTEXT</label>
                        <div className="grid grid-cols-4 gap-2">
                          {assets.filter(a => a.type === "image").map((img) => (
                            <button
                              key={img.id}
                              onClick={() => setSelectedTransformerImage(img.url)}
                              className={`rounded-lg overflow-hidden border transition-all ${
                                selectedTransformerImage === img.url ? "border-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.3)]" : "border-transparent opacity-60 hover:opacity-100"
                              }`}
                            >
                              <img src={img.url} className="w-full h-12 object-cover" referrerPolicy="no-referrer" />
                            </button>
                          ))}
                        </div>

                        <div className="space-y-1.5 pt-2">
                          <div className="flex justify-between text-[10px] font-mono text-zinc-400">
                            <span>TRANSFORM DENOISING STRENGTH</span>
                            <span className="text-[#00e5ff] font-bold">{(imgStrength * 100).toFixed(0)}% Shift</span>
                          </div>
                          <input
                            type="range"
                            min="10"
                            max="95"
                            value={imgStrength * 100}
                            onChange={(e) => setImgStrength(parseFloat(e.target.value) / 100)}
                            className="w-full accent-purple-500 cursor-pointer h-1.5 rounded-lg bg-white/5"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex justify-between text-[10px] font-mono text-zinc-400">
                            <span>DILATION CHRONO-NOISE LEVEL</span>
                            <span className="text-[#00e5ff] font-bold">{(imgNoiseLevel * 100).toFixed(0)}% Jitter</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="60"
                            value={imgNoiseLevel * 100}
                            onChange={(e) => setImgNoiseLevel(parseFloat(e.target.value) / 100)}
                            className="w-full accent-cyan-400 cursor-pointer h-1.5 rounded-lg bg-white/5"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col justify-between">
                        <div>
                          <label className="text-[10px] font-mono text-zinc-400 block mb-2 uppercase">RE-SYNTHESIZER WORKSPACE VIEW</label>
                          <div className="relative rounded-xl overflow-hidden border border-white/5 bg-black h-40 flex items-center justify-center">
                            <img src={selectedTransformerImage} className="w-full h-full object-cover opacity-80" referrerPolicy="no-referrer" />
                            <div className="absolute inset-0 bg-transparent hover:bg-purple-950/25 transition-all flex items-center justify-center">
                              <span className="text-[10px] font-mono text-white bg-black/80 px-2 py-1 rounded border border-purple-500/30">MOCK WORKSPACE CANVAS READY</span>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => simulateRender("image", `Image-to-Image Re-Synthesize`)}
                          disabled={isGenerating}
                          className="w-full py-2.5 bg-pink-600 hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer select-none mt-4 text-center block"
                        >
                          INITIALIZE COGNITIVE SYNTHESIS
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 5. IMAGE TO TEXT / OCR */}
              {activePanel === "Image to Text / OCR" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest">★ NEXORAX TEXT EXTRACTOR & OPTICAL RECOGNITION (OCR)</h4>
                    <p className="text-zinc-445 text-xs">
                      Convert financial screenshots, bank ledgers, or Home Office documents into editable, searchable Markdown logs automatically.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <label className="text-[10px] font-mono text-zinc-400 block uppercase">SELECT DOCUMENT FOR OCR CONVERSION</label>
                        <div className="grid grid-cols-2 gap-3">
                          {OCR_SAMPLES.map((sample, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                setSelectedOcrSample(idx);
                                setExtractedOcrText(sample.text);
                                triggerToast(`Selected: ${sample.name}`, "info");
                              }}
                              className={`p-3 rounded-2xl border text-left transition-all relative overflow-hidden group select-none cursor-pointer ${
                                selectedOcrSample === idx
                                  ? "bg-cyan-950/20 border-cyan-500/40 shadow-[0_0_15px_rgba(0,e5,ff,0.1)]"
                                  : "bg-black/50 border-white/5"
                              }`}
                            >
                              <div className="h-16 rounded overflow-hidden mb-2 border border-white/5">
                                <img src={sample.imageUrl} className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-all duration-300" referrerPolicy="no-referrer" />
                              </div>
                              <span className="text-[10.5px] font-bold text-white block truncate">{sample.name}</span>
                              <span className="text-[8px] font-mono text-zinc-500 uppercase block tracking-wider mt-0.5">Byte Mode Scan Lock</span>
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={runOcrExtractorSim}
                          disabled={isExtractingOcr}
                          className="w-full py-2.5 bg-[#00e5ff] hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer select-none flex items-center justify-center gap-1.5"
                        >
                          {isExtractingOcr ? <RefreshCw size={14} className="animate-spin text-black" /> : <FileSearch size={14} />}
                          <span>EXECUTE SYSTEM OCR BYTE EXTRACT</span>
                        </button>
                      </div>

                      <div className="space-y-3">
                        <label className="text-[10px] font-mono text-zinc-400 block uppercase">EXTRACTED CONTENT MARKDOWN</label>
                        <div className="relative rounded-2xl border border-white/10 bg-black/60 p-4 font-mono text-[11px] text-[#00e5ff] min-h-[160px] flex flex-col justify-between">
                          <textarea
                            value={extractedOcrText}
                            onChange={(e) => setExtractedOcrText(e.target.value)}
                            className="bg-transparent border-none text-cyan-300 text-xs font-mono w-full h-[180px] resize-none focus:outline-none"
                          />
                          <div className="border-t border-white/5 pt-2 text-[8px] text-zinc-500 flex justify-between">
                            <span>Status: Buffer Synced to Deep Drive</span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(extractedOcrText);
                                triggerToast("OCR content successfully copied to clipboard!", "success");
                              }}
                              className="text-[#00e5ff] uppercase hover:underline"
                            >
                              Copy Buffer
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 6. AI VIDEO GENERATOR */}
              {activePanel === "AI Video Generator" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-widest">★ AI VIDEOGEN TEXT-TO-CLIP MASTER CONTROL</h4>
                    <p className="text-zinc-450 text-xs">
                      Rent dedicated Munich deep-rendering cores to compile beautiful b-roll reels and talks in 1080p, utilizing professional voice overlays.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block uppercase">VIDEO CLIP CONCEPT PROMPT</label>
                          <textarea
                            value={currentPrompt}
                            onChange={(e) => setCurrentPrompt(e.target.value)}
                            className="w-full bg-black/60 border border-white/10 p-3 rounded-xl text-xs font-mono text-white focus:outline-none focus:border-purple-500 min-h-[80px]"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-mono text-zinc-400 block uppercase">CAMERA ACTION PRESET</label>
                            <select className="w-full bg-black/60 border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none">
                              <option>Dramatic Dolly-In Zoom</option>
                              <option>Continuous Drone Orbit</option>
                              <option>Dynamic Crane Tilt Down</option>
                              <option>High Speed Action PAN</option>
                            </select>
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[10px] font-mono text-zinc-400 block uppercase">NARRATIVE AUDIO OVERLAY</label>
                            <select className="w-full bg-black/60 border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none">
                              <option>Director (Mahmood Arabic/English)</option>
                              <option>Standard Assistant Voice Matrix</option>
                              <option>Defensive Shunting Alarm Mode</option>
                              <option>None (Raw B-Roll Track)</option>
                            </select>
                          </div>
                        </div>

                        <button
                          onClick={() => simulateRender("video", `Text to Video: ${currentPrompt.substring(0, 30)}`, 5000)}
                          disabled={isGenerating}
                          className="w-full py-3 bg-purple-600 hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer select-none flex items-center justify-center gap-2"
                        >
                          <Video size={14} className="text-black font-black" />
                          <span>RENDER NEURAL VIDEO</span>
                        </button>
                      </div>

                      <div className="bg-black/60 border border-white/5 p-4 rounded-2xl flex flex-col justify-between">
                        <span className="text-[9px] font-mono text-zinc-500 uppercase block">VIDEO BUFFER STREAM GATEWAY</span>
                        <div className="flex-1 flex flex-col items-center justify-center py-6 text-center">
                          {isGenerating && generateType === "video" ? (
                            <div className="w-full max-w-[200px] space-y-3">
                              <Clock size={30} className="text-purple-400 animate-pulse mx-auto" />
                              <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
                                <div className="bg-purple-500 h-full animate-[pulse_1s_infinite]" style={{ width: `${generateProgress}%` }} />
                              </div>
                              <span className="text-[10px] font-mono text-purple-300 block">{generateProgress}% RENDERED IN MUNICH</span>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Play size={32} className="text-[#00e5ff] hover:scale-110 transition-all cursor-pointer mx-auto" />
                              <span className="text-[11px] font-sans text-zinc-400 block max-w-sm">No active video generation streams operating. Presets and codecs cleared. Ready to accept payload commands.</span>
                            </div>
                          )}
                        </div>
                        <div className="border-t border-white/5 pt-2 text-[9px] font-mono text-zinc-500 flex justify-between">
                          <span>EST LENGTH: 15 seconds</span>
                          <span>LATENCY RATIO: 0.8s</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 7. IMAGE TO VIDEO */}
              {activePanel === "Image to Video" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-widest">★ OPTICAL IMAGE-TO-VIDEO ANIME CONVERSION MATRIX</h4>
                    <p className="text-zinc-445 text-xs">
                      Convert existing static images into fully dynamic and rich b-roll sequences using generative motion layers.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-black/45 p-5 rounded-2xl border border-white/5">
                      <div className="space-y-4">
                        <label className="text-[10px] font-mono text-zinc-400 block uppercase">SELECT INPUT SOURCE IMAGE</label>
                        <div className="grid grid-cols-4 gap-2">
                          {assets.filter(a => a.type === "image").map((img) => (
                            <button
                              key={img.id}
                              onClick={() => setSelectedTransformerImage(img.url)}
                              className={`rounded-lg overflow-hidden border transition-all ${
                                selectedTransformerImage === img.url ? "border-[#00e5ff] shadow-[0_0_8px_rgba(0,229,255,0.3)]" : "border-transparent opacity-60 hover:opacity-100"
                              }`}
                            >
                              <img src={img.url} className="w-full h-12 object-cover" referrerPolicy="no-referrer" />
                            </button>
                          ))}
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-[#00e5ff] block uppercase">MOTION SPEED SCALE</label>
                          <div className="flex items-center gap-4">
                            <input
                              type="range"
                              min="10"
                              max="120"
                              value={motionSpeed}
                              onChange={(e) => setMotionSpeed(parseInt(e.target.value))}
                              className="flex-1 accent-cyan-400 cursor-pointer h-1 rounded-full bg-white/5"
                            />
                            <span className="text-xs font-mono text-white font-bold">{motionSpeed}px/s</span>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block">MOTION MOOD SHAKE</label>
                          <div className="grid grid-cols-2 gap-2">
                            {["Cinematic Pan", "High Tension Shake", "Smooth Zoom Out", "Hyperlapse Warp"].map((mood) => (
                              <button
                                key={mood}
                                onClick={() => setShakePreset(mood)}
                                className={`p-2 rounded-xl text-[9px] uppercase tracking-wide font-mono border transition-all select-none cursor-pointer ${
                                  shakePreset === mood
                                    ? "bg-cyan-950/40 border-cyan-500 text-cyan-300"
                                    : "bg-black/40 border-white/5 text-zinc-500 hover:border-white/10"
                                }`}
                              >
                                {mood}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col justify-between">
                        <div>
                          <label className="text-[10px] font-mono text-zinc-400 block mb-2 uppercase">ANIMATION CO-ENGINE MAP</label>
                          <div className="rounded-xl overflow-hidden border border-white/5 bg-black h-40 relative flex items-center justify-center">
                            <img src={selectedTransformerImage} className="w-full h-full object-cover select-none" referrerPolicy="no-referrer" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-bottom p-3">
                              <span className="text-[9px] font-mono text-zinc-350">Preset Ready: {shakePreset}</span>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => simulateRender("video", `Image to Video: [Pan Mood]`)}
                          disabled={isGenerating}
                          className="w-full py-2.5 bg-purple-600 hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer select-none text-center block"
                        >
                          INITIALIZE OPTICAL VECTOR MOTION
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 8. VIDEO TRANSFORMER */}
              {activePanel === "Video Transformer" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest">★ VIDEO-TO-VIDEO STYLIST & FORMAT TRANSCODER</h4>
                    <p className="text-zinc-450 text-xs">
                      Convert and adjust finished assets into distinct landscape, vertical, or micro-feed grid specifications on-the-fly.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4 bg-black/40 p-4 rounded-2xl border border-white/5">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block uppercase">TARGET DEPLOYMENT CODEC</label>
                          <select
                            value={targetCodec}
                            onChange={(e) => setTargetCodec(e.target.value)}
                            className="w-full bg-black border border-white/10 p-2 rounded-xl text-xs font-mono text-white text-left focus:outline-none focus:border-cyan-500"
                          >
                            <option value="h264_nvenc PRO">H.264 High-Profile (SaaS Stream Standard)</option>
                            <option value="av1_stream ULTRA">AV1 Cyber-Broadcast Standard</option>
                            <option value="hevc_main10">HEVC Main-10 Frame Compressor</option>
                          </select>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block uppercase">NARRATIVE SOUNDTRACK MIXER VOLUME</label>
                          <div className="flex items-center gap-3">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={audioOverlayVolume}
                              onChange={(e) => setAudioOverlayVolume(parseInt(e.target.value))}
                              className="flex-grow h-1 bg-white/5 accent-[#00e5ff] cursor-pointer rounded-full"
                            />
                            <span className="text-xs text-white font-mono font-bold min-w-[30px] text-right">{audioOverlayVolume}%</span>
                          </div>
                        </div>

                        <div className="space-y-1.5 pt-2">
                          <span className="text-[9.5px] font-mono text-purple-400 uppercase block">NEXORAX EXPORT BOUNDARY METRICS</span>
                          <div className="space-y-1 text-[10.5px] text-zinc-400">
                            <div className="flex justify-between pb-1 border-b border-white/5">
                              <span>Output Format</span>
                              <span className="text-white">Vertical Shorts [9:16]</span>
                            </div>
                            <div className="flex justify-between pb-1 border-b border-white/5">
                              <span>Synthesized Audio Mix</span>
                              <span className="text-[#00e5ff] font-bold">192kbps AAC Enforced</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Diagnostic Code</span>
                              <span className="text-emerald-400">CODEC_NVENC_STABLE</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col justify-between bg-black/40 p-4 rounded-2xl border border-white/5">
                        <div className="space-y-2">
                          <span className="text-[10px] font-mono text-zinc-400 block uppercase">ACTIVE FORMAT CUBE VIEW</span>
                          <div className="h-44 bg-zinc-950 rounded-xl relative flex items-center justify-center border border-white/5">
                            <div className="w-[100px] h-full bg-[#110530]/50 border-x border-[#00e5ff]/30 relative flex flex-col justify-between p-3 overflow-hidden">
                              <span className="text-[8px] font-mono text-cyan-400 block text-center uppercase tracking-wide">Shorts / Reels 9:16</span>
                              <div className="text-center">
                                <Play size={16} className="text-[#00e5ff] mx-auto opacity-70" />
                              </div>
                              <span className="text-[8px] font-mono text-zinc-500 text-center block">VP9 Codec Wrap</span>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => simulateRender("video", `Video Transform: [Adaptive Shorts Format]`, 4200)}
                          disabled={isGenerating}
                          className="w-full py-2.5 bg-purple-600 hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer select-none mt-4 text-center block"
                        >
                          COMMENCE FRAME RESOLUTION TRANSCODE
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 9. AUTOMATION PIPELINE */}
              {activePanel === "Automation Pipeline" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-widest">★ BATCH PROGRAMMATIC AUTOMATION PIPELINE CONSOLE</h4>
                    <p className="text-zinc-450 text-xs">
                      Coordinate the bulk loop scheduler matrix to synthesize dozens of assets per cycle seamlessly. Use the shortcut templates or build a custom array.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-black/45 p-5 rounded-2xl border border-white/5">
                      <div className="space-y-4">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block uppercase font-bold">PIPELINE TASK ALIAS</label>
                          <input
                            type="text"
                            value="programmatic_shorts_sweep_sc887095"
                            className="w-full bg-black border border-white/10 p-2.5 rounded-xl text-xs font-mono text-[#00e5ff] focus:outline-none"
                            readOnly
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-mono text-zinc-400 block uppercase">QUANTITY LIMIT</label>
                            <input
                              type="number"
                              value={batchCount}
                              onChange={(e) => setBatchCount(Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-full bg-black border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[10px] font-mono text-zinc-400 block uppercase">NODE TARGET DEPLOY</label>
                            <select className="w-full bg-black border border-white/10 p-2 rounded-xl text-xs font-mono text-white focus:outline-none">
                              <option>Instagram Grid autoupload</option>
                              <option>Social Content Queue Standard</option>
                              <option>Local Export Sandbox Buffer</option>
                            </select>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block uppercase">PIPELINE RECIPE DIRECTIVE</label>
                          <textarea
                            className="w-full bg-black border border-white/10 p-2.5 rounded-xl text-xs font-mono text-zinc-400 focus:outline-none min-h-[80px]"
                            value="recipe_sweep_start => check active_keywords => pull topic references => render 5 images => transform images into vertical 15s mp4 clips => mix standard voice overlays => push to matrix"
                            readOnly
                          />
                        </div>
                      </div>

                      <div className="flex flex-col justify-between">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-[10px] font-mono text-zinc-400">
                            <span>ACTIVE LOGS METRIC MATRIX</span>
                            <span className="text-purple-400 font-bold">Loop Enabled</span>
                          </div>
                          
                          <div className="bg-black/80 font-mono text-[9.5px] p-3 rounded-xl min-h-[160px] max-h-[180px] overflow-y-auto text-zinc-400 space-y-1 border border-white/5">
                            <p className="text-emerald-400">[0.8s Telemetry] Loop check: ok</p>
                            <p className="text-zinc-500">[System Audit] SC887095 container verification active</p>
                            <p className="text-[#00e5ff]">[Munich VEO] Buffer allocated for batch size {batchCount}</p>
                            <p className="text-purple-400">[Scheduler] Pipeline targeting Social Media tab</p>
                            <p className="text-zinc-500">[Direct SecurePayment Sync] Checked Secure Payment Provider ledger constraints (no fees)</p>
                            <p className="text-yellow-400">[State Monitor] Initializing trigger sweeps</p>
                          </div>
                        </div>

                        <button
                          onClick={() => simulateRender("batch", `Automation Pipeline Batch Execution`, 6000)}
                          disabled={isGenerating}
                          className="w-full py-3 bg-gradient-to-r from-purple-500 to-[#00e5ff] hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer select-none text-center block mt-3"
                        >
                          INITIALIZE AUTONOMOUS LOOP DISPATCH
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 10. RENDER QUEUE */}
              {activePanel === "Render Queue" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-2 border-b border-white/5">
                      <h4 className="text-xs font-mono font-bold text-[#e840f3] uppercase tracking-widest">★ CENTRAL COMPILING DIAGNOSTIC & RENDER LOGGER</h4>
                      <button
                        onClick={() => {
                          setRenderQueue(prev => prev.map(item => ({ ...item, progress: 100, status: "Completed", timeRemaining: "0s" })));
                          triggerToast("Purged queue and marked renders complete.", "success");
                        }}
                        className="p-1 px-3 border border-white/5 rounded-lg text-[8.5px] text-zinc-400 hover:text-white transition-all uppercase font-mono"
                      >
                        Force Complete All
                      </button>
                    </div>

                    <div className="space-y-2">
                      {renderQueue.map((item, idx) => (
                        <div key={idx} className="bg-black/55 border border-white/5 rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
                          <div className="flex items-center gap-3 w-full md:w-auto">
                            <div className={`p-2 rounded-xl text-black ${item.progress === 100 ? "bg-emerald-400" : "bg-purple-600 animate-pulse"}`}>
                              {item.type === "video" ? <Video size={13} /> : <ImageIcon size={13} />}
                            </div>
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-[9px] text-[#00e5ff]">{item.id}</span>
                                <span className="text-[11px] font-bold text-white uppercase">{item.task}</span>
                              </div>
                              <span className="text-[8px] font-mono text-zinc-500 block">Status Code: {item.status}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 w-full md:w-auto justify-end">
                            <div className="w-[120px] text-zinc-500 text-[10.5px] font-mono text-right capitalize">
                              Remaining: {item.timeRemaining}
                            </div>
                            <div className="w-[100px] bg-white/5 h-1.5 rounded-full overflow-hidden">
                              <div className={`h-full ${item.progress === 100 ? "bg-emerald-400" : "bg-purple-500"}`} style={{ width: `${item.progress}%` }} />
                            </div>
                            <span className="text-xs font-mono font-bold text-white min-w-[30px] text-right">{item.progress}%</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-2 pt-2">
                      <span className="text-[10px] font-mono text-zinc-400 block uppercase">CORE PROCESS DIAGNOSTIC TERMINAL LOGS</span>
                      <div className="bg-black text-zinc-400 p-4 rounded-xl min-h-[160px] max-h-[180px] overflow-y-auto font-mono text-[10px] text-left border border-white/10 space-y-1">
                        {renderingLogs.map((log, idx) => (
                          <div key={idx} className="border-b border-white/5 pb-1 last:border-0">
                            {log}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 11. ASSET LIBRARY */}
              {activePanel === "Asset Library" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-white/5">
                      <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-widest">★ COMPILED MULTIMEDIA ASSET ARCHIVE</h4>
                      <span className="text-[9px] font-mono text-zinc-400">Total Items: {assets.length} / infinite</span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {assets.map((asst) => (
                        <div key={asst.id} className="bg-black/55 rounded-2xl border border-white/5 p-3 flex flex-col justify-between group overflow-hidden hover:border-purple-500/20 transition-all">
                          <div className="relative h-28 rounded-xl overflow-hidden mb-2.5 border border-white/5">
                            <img src={asst.url} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-300" referrerPolicy="no-referrer" />
                            <div className="absolute top-1.5 right-1.5 bg-black/75 p-1 px-2 rounded-full border border-white/10 text-[8px] font-mono uppercase text-zinc-300">
                              {asst.type}
                            </div>
                          </div>

                          <div className="text-left space-y-1">
                            <h5 className="text-[11px] font-bold text-white uppercase truncate block mb-1">{asst.title}</h5>
                            <span className="text-[8px] font-mono text-zinc-500 truncate block">Prompt: {asst.prompt}</span>
                            <div className="flex justify-between items-center text-[9px] font-mono text-zinc-400 border-t border-white/5 pt-1.5 mt-1.5">
                              <span>{asst.size}</span>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => {
                                    triggerToast(`Pushed "${asst.title}" directly to Social Scheduler!`, "success");
                                  }}
                                  className="text-cyan-400 uppercase text-[8px] hover:underline hover:text-[#00e5ff]"
                                >
                                  Export
                                </button>
                                <span className="text-zinc-600">|</span>
                                <button
                                  onClick={() => {
                                    setAssets(prev => prev.filter(a => a.id !== asst.id));
                                    triggerToast("Asset purged successfully.", "info");
                                  }}
                                  className="text-red-400 uppercase text-[8px] hover:underline"
                                >
                                  Purge
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 12. BRAND VOICE & VISUAL IDENTITY */}
              {activePanel === "Brand Voice & visual" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-[#00e5ff] uppercase tracking-widest">★ NEXORAX CORE BRANDING INTELLECT & GUIDELINES</h4>
                    <p className="text-zinc-450 text-xs text-left">
                      Enforce strict parameters for logo placement, color calibration, and corporate copywriting voice parameters to pass programmatic visual alignment checks.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-black/45 p-5 rounded-2xl border border-white/5 text-left">
                      <div className="space-y-4">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block uppercase font-bold">PRIMARY GLOW SPECULAR (HEX)</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={brandColorHex}
                              onChange={(e) => setBrandColorHex(e.target.value)}
                              className="bg-black border border-white/10 p-2 rounded-xl text-xs font-mono text-white flex-1"
                            />
                            <div className="w-10 h-8 rounded-lg border border-white/10" style={{ backgroundColor: brandColorHex }} />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block uppercase">BRAND VECTOR WATERMARK</label>
                          <div className="p-3 bg-black border border-white/10 rounded-xl text-xs text-zinc-500 font-mono text-center">
                            nexorax_NexoraX_logo_9_2.svg (12.4 KB) - Embedded
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-zinc-400 block uppercase">CORPORATE METRIC ALIGNMENT CHECKLIST</label>
                          <div className="space-y-1 text-zinc-300 text-[10.5px]">
                            <label className="flex items-center gap-2">
                              <input type="checkbox" defaultChecked className="accent-purple-500" />
                              <span>Strict 0.8s Telemetry Validation on Export</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input type="checkbox" defaultChecked className="accent-purple-500" />
                              <span>Embed Secure Payment Provider sort code reference link metadata</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input type="checkbox" defaultChecked className="accent-purple-500" />
                              <span>Enforce SC887095 legal compliance tags in captions</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <label className="text-[10px] font-mono text-zinc-400 block uppercase font-bold">BRAND VOICE TOKENS</label>
                        <div className="flex flex-wrap gap-2">
                          {brandVoiceKeywords.map((tok, index) => (
                            <span key={index} className="flex items-center gap-1.5 bg-purple-950/45 p-1.5 px-3 rounded-full border border-purple-500/30 text-[10px] font-mono text-purple-300">
                              {tok}
                              <button
                                onClick={() => setBrandVoiceKeywords(prev => prev.filter(t => t !== tok))}
                                className="text-zinc-500 hover:text-white"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Add voice token..."
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newKeyword.trim()) {
                                setBrandVoiceKeywords(prev => [...prev, newKeyword.trim()]);
                                setNewKeyword("");
                              }
                            }}
                            className="bg-black border border-white/10 p-2 rounded-xl text-xs font-mono text-white flex-1 focus:outline-none"
                          />
                          <button
                            onClick={() => {
                              if (newKeyword.trim()) {
                                setBrandVoiceKeywords(prev => [...prev, newKeyword.trim()]);
                                setNewKeyword("");
                              }
                            }}
                            className="px-3 bg-purple-600 hover:bg-purple-700 text-black font-bold text-xs rounded-xl"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 13. PUBLISHING & EXPORT */}
              {activePanel === "Publishing & Export" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-widest">★ BORDER-GATE EXPORT & PROGRAMMATIC TRANSFER CENTRE</h4>
                    <p className="text-zinc-450 text-xs">
                      Instantly shuttle fully-compiled media assets from the studio sandbox right into the main Social Media Automation queues for scheduling.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-black p-5 rounded-2xl border border-white/5">
                      <div className="space-y-4">
                        <label className="text-[10px] font-mono text-zinc-400 block uppercase font-bold">READY CONTAINER SPECIFICATIONS</label>
                        <div className="p-3 bg-cyan-950/15 rounded-xl border border-cyan-500/20 text-xs">
                          <div className="flex justify-between font-mono text-[10.5px]">
                            <span className="text-zinc-400">Total Bundle Size:</span>
                            <span className="text-white font-bold">{assets.length} Assets / ~78 MB</span>
                          </div>
                          <div className="flex justify-between font-mono text-[10.5px] mt-1.5">
                            <span className="text-zinc-400">Target Pipeline Destination:</span>
                            <span className="text-[#00e5ff]">Social Autopilot Scheduler (Queue View)</span>
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            triggerToast(`Dynamic export package fully transmitted to Social Automation Scheduler!`, "success");
                          }}
                          className="w-full py-3 bg-gradient-to-r from-[#00e5ff] via-purple-600 to-pink-500 hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer select-none text-center block"
                        >
                          SHUTTLE CONTAINER TO SOCIAL SCHEDULER
                        </button>
                      </div>

                      <div className="space-y-3">
                        <label className="text-[10px] font-mono text-zinc-400 block uppercase">INTEGRITY HANDSHAKE VALIDATOR</label>
                        <div className="relative rounded-2xl border border-white/10 bg-black/60 p-4 font-mono text-[10px] text-zinc-405 leading-relaxed min-h-[140px] flex flex-col justify-between">
                          <div className="space-y-1">
                            <p className="text-emerald-400">✓ SC887095 verification signature matched.</p>
                            <p className="text-emerald-400">✓ NexoraX billing records Billing offsets fully authorized.</p>
                            <p className="text-[#00e5ff]">✓ Direct sync coupling to Munich bare-metal container online.</p>
                            <p className="text-zinc-500">✓ Security status: Isolated Sandboxed Buffer Mode verified.</p>
                          </div>
                          <div className="border-t border-white/5 pt-2 text-[8px] text-zinc-500">
                            Ledger Ref: TX_MED_AUT_9_2
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 14. CREATIVE ANALYTICS */}
              {activePanel === "Creative Analytics" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest">★ PROGRAMMATIC CREATIVE PERFORMANCE INDEX</h4>
                    
                    <div className="bg-black/45 p-4 rounded-2xl border border-white/5">
                      <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={RECENT_PRODUCTION_DATA}>
                            <defs>
                              <linearGradient id="imagesGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#00e5ff" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#00e5ff" stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="videosGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#e840f3" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#e840f3" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a1f4d" />
                            <XAxis dataKey="name" stroke="#685c96" fontSize={10} fontStyle="italic" />
                            <YAxis stroke="#685c96" fontSize={10} />
                            <Tooltip contentStyle={{ backgroundColor: "rgba(12, 6, 26, 0.95)", border: "1px solid rgba(168, 85, 247, 0.4)", borderRadius: "12px", color: "#fff" }} />
                            <Legend />
                            <Area type="monotone" dataKey="images" name="Images Synthesized" stroke="#00e5ff" strokeWidth={2} fillOpacity={1} fill="url(#imagesGradient)" />
                            <Area type="monotone" dataKey="videos" name="Video Clips Rendered" stroke="#e840f3" strokeWidth={2} fillOpacity={1} fill="url(#videosGradient)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-black/40 border border-white/5 rounded-2xl">
                        <span className="text-[8.5px] font-mono text-zinc-500 uppercase block">Total Dynamic GPU Load Allocation</span>
                        <span className="text-xl font-bold text-white font-mono block mt-1">94.21 TFLOPS</span>
                        <span className="text-[8px] font-mono text-cyan-400 block mt-1">Munich DeepRender Cores</span>
                      </div>

                      <div className="p-4 bg-black/40 border border-white/5 rounded-2xl">
                        <span className="text-[8.5px] font-mono text-zinc-500 uppercase block">NexoraX Render Power Index</span>
                        <span className="text-xl font-bold text-[#00e5ff] font-mono block mt-1">98.9% Efficiency</span>
                        <span className="text-[8px] font-mono text-emerald-400 block mt-1">Secure Payment Inflow Reconciled</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 15. TEMPLATE MARKETPLACE */}
              {activePanel === "Template Marketplace" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-widest">★ NEXORAX DECENRALIZED RECIPES & STYLE MATRIX</h4>
                    <p className="text-zinc-450 text-xs">
                      Unlock programmatic asset matrices designed and calibrated by systems architects to output perfect brand compliant frames under the NexoraX billing records mandate.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[
                        { title: "NexoraX Resource Pitch Overlay", use: "124 drafts", type: "Video Master", cost: "Free/Endowed", desc: "Embed SecurePayment sort code vectors directly into video layouts smoothly." },
                        { title: "Munich Datacenter Transcode", use: "63 active", type: "3D Flyby", cost: "Free/Endowed", desc: "Dark obsidian glassmorphic presets optimized for ultra high-contrast." },
                        { title: "Secure Invoice Register Client Resource Workflow Scribe Mode", use: "190 uses", type: "OCR & Document", cost: "Free/Endowed", desc: "Extract applicant data matrices and match with standard invitation formats." }
                      ].map((item, idx) => (
                        <div key={idx} className="bg-black/55 rounded-2xl p-4 border border-white/5 flex flex-col justify-between hover:border-purple-500/20 transition-all text-left">
                          <div className="space-y-2">
                            <div className="flex justify-between items-center text-[9px] font-mono">
                              <span className="text-[#00e5ff] font-bold">{item.type}</span>
                              <span className="bg-purple-950 text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/30 text-[8px] font-bold">{item.cost}</span>
                            </div>
                            <h5 className="text-[11.5px] font-bold text-white uppercase block">{item.title}</h5>
                            <p className="text-[10px] text-zinc-450 leading-relaxed">{item.desc}</p>
                          </div>

                          <div className="border-t border-white/5 pt-3 mt-3 flex justify-between items-center text-[9.5px]">
                            <span className="text-zinc-500 font-mono">Installed</span>
                            <button
                              onClick={() => {
                                triggerToast(`Loaded "${item.title}" preset template into workspace.`, "success");
                              }}
                              className="text-cyan-400 uppercase font-black hover:underline hover:text-[#00e5ff]"
                            >
                              Apply Matrix
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* [E1] GOVERNANCE */}
              {activePanel === "Governance" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest">★ NEXORAX INSTITUTIONAL GOVERNANCE NODE</h4>
                    <p className="text-zinc-400 text-xs">
                      Enforce executive corporate routing rules, track compliance metrics for the £150,000 Resource resource, and manage the 1% 3% Community Allocation configuration under the SC887095 mandate.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-black/45 p-5 rounded-2xl border border-white/5 space-y-4">
                        <span className="text-[10px] font-mono text-purple-400 uppercase block font-bold">CHARITY ALLOCATION RULES (1% LEVY)</span>
                        <p className="text-zinc-400 text-[11px] leading-relaxed">
                          Under strict multi-stage tax accounting logic, the perpetual levy is dispatched equally in 33.33% splits to verified regional foundations:
                        </p>
                        <div className="space-y-2 text-xs font-mono">
                          <div className="p-2.5 bg-purple-950/20 border border-purple-500/20 rounded-xl flex justify-between">
                            <span className="text-purple-300">Dunfermline Foundation (UK North)</span>
                            <span className="text-white font-bold">33.33% Split</span>
                          </div>
                          <div className="p-2.5 bg-cyan-950/20 border border-cyan-500/20 rounded-xl flex justify-between">
                            <span className="text-cyan-300">Global Humanitarian Core</span>
                            <span className="text-white font-bold">33.33% Split</span>
                          </div>
                          <div className="p-2.5 bg-rose-950/20 border border-rose-500/20 rounded-xl flex justify-between">
                            <span className="text-rose-300">UK General resource Fund</span>
                            <span className="text-white font-bold">33.34% Split</span>
                          </div>
                        </div>
                        <div className="pt-2 text-center">
                          <span className="text-[9px] font-mono text-[#00e5ff] uppercase font-bold animate-pulse">✓ ALL TRUSTEE SIGNATURES SECURED</span>
                        </div>
                      </div>

                      <div className="bg-black/45 p-5 rounded-2xl border border-white/5 space-y-4">
                        <span className="text-[10px] font-mono text-pink-400 uppercase block font-bold">COMPLIANCE DECISIONS RECORD</span>
                        <div className="space-y-2 text-[11px]">
                          {[
                            { title: "NexoraX Google Workspace Alignment", status: "VERIFIED", date: "June 11, 2026", auth: "M. Director" },
                            { title: "NexoraX billing records Secure Invoice Register Fee Structure", status: "APPROVED", date: "June 10, 2026", auth: "Gavel Committee" },
                            { title: "Munich Datacenter Direct Tunneling", status: "STABLE", date: "June 08, 2026", auth: "Node-09 Architect" }
                          ].map((doc, idx) => (
                            <div key={idx} className="p-3 bg-zinc-950 border border-white/5 rounded-xl flex justify-between items-center">
                              <div>
                                <span className="font-bold text-white block">{doc.title}</span>
                                <span className="text-[9px] text-zinc-500 block font-mono">{doc.date} | Auth: {doc.auth}</span>
                              </div>
                              <span className="text-[9px] font-mono px-2 py-0.5 bg-emerald-900/30 text-emerald-400 border border-emerald-500/30 rounded font-black">{doc.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* [E2] RISK MANAGEMENT */}
              {activePanel === "Risk Management" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-red-400 uppercase tracking-widest">★ NEXORAX COGNITIVE RISK MITIGATION ENGINE</h4>
                    <p className="text-zinc-400 text-xs">
                      Enforce isolation boundaries on bare-metal virtual containers, detect unauthorized copying actions, and mitigate duplicative codebase injection routes dynamically.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono">
                      <div className="bg-red-950/20 border border-red-500/30 p-4 rounded-2xl">
                        <span className="text-[8.5px] text-zinc-400 uppercase block font-black">Countermeasure Shields</span>
                        <span className="text-2xl font-black text-red-400 block mt-1">100% ONLINE</span>
                        <span className="text-[8px] text-emerald-400 block mt-1">✓ Screen-Leak Blocker Sync</span>
                      </div>
                      <div className="bg-amber-950/20 border border-amber-500/30 p-4 rounded-2xl">
                        <span className="text-[8.5px] text-zinc-400 uppercase block font-black">Copy Interceptions (24h)</span>
                        <span className="text-2xl font-black text-amber-400 block mt-1">18 Swatted</span>
                        <span className="text-[8px] text-zinc-400 block mt-1">IP blocks recorded in Vault</span>
                      </div>
                      <div className="bg-cyan-950/20 border border-cyan-500/30 p-4 rounded-2xl">
                        <span className="text-[8.5px] text-zinc-400 uppercase block font-black">Isolated Containers</span>
                        <span className="text-2xl font-black text-[#00e5ff] block mt-1">4 Sandboxes</span>
                        <span className="text-[8px] text-[#00e5ff] block mt-1">✓ Automated cleanup loops OK</span>
                      </div>
                    </div>

                    <div className="bg-black/55 p-5 rounded-2xl border border-white/5 space-y-3">
                      <span className="text-[10px] font-mono text-purple-400 uppercase block font-bold">REAL-TIME TELEMETRY ALERTS</span>
                      <div className="p-3 bg-[#0a0515] border border-red-500/25 rounded-xl flex justify-between items-center font-mono text-xs text-left">
                        <div className="flex items-center gap-3">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping shrink-0" />
                          <div>
                            <span className="text-white font-bold block">Duplication Attempt Prevented</span>
                            <span className="text-[10px] text-zinc-500 leading-none">Clone script payload intercepted on host block [Munich-Srv3]</span>
                          </div>
                        </div>
                        <span className="text-[9px] px-2 py-0.5 bg-red-950/40 text-red-400 border border-red-500/30 rounded font-bold font-mono">ISOLATED</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* [E3] DIGITAL TWIN */}
              {activePanel === "Digital Twin" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-pink-400 uppercase tracking-widest">★ DIGITAL TWIN SIMULATION: GERMAN SERVER FABRIC</h4>
                    <p className="text-zinc-400 text-xs">
                      Inspect virtual clones of our physical bare-metal hardware clusters in Munich. This terminal visualizes thermal thresholds, fan speed metrics, and latencies matching the 0.8s standard.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center font-mono text-xs">
                      {[
                        { label: "Core Temp", value: "41.6 °C", sub: "Optimal Static", color: "text-[#00e5ff]" },
                        { label: "VRAM Reserve", value: "24.1 GB", sub: "ECC Buffers OK", color: "text-purple-400" },
                        { label: "Total Clock", value: "4.82 GHz", sub: "All Cores Locked", color: "text-pink-400" },
                        { label: "Paging Latency", value: "0.14 ms", sub: "0.8s Sync Ceiling", color: "text-emerald-400" }
                      ].map((n, i) => (
                        <div key={i} className="bg-black/45 border border-white/5 p-4 rounded-xl">
                          <span className="text-[9px] text-zinc-500 uppercase font-bold block">{n.label}</span>
                          <span className={`text-xl font-black block mt-1.5 ${n.color}`}>{n.value}</span>
                          <span className="text-[8px] text-zinc-400 block mt-1">{n.sub}</span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-black/55 p-5 rounded-2xl border border-white/5 space-y-4">
                      <span className="text-[10px] font-mono text-[#00e5ff] uppercase block font-bold">VIRTUAL LATTICE FREQUENCY GRID</span>
                      <div className="h-40 w-full bg-slate-950/40 rounded-xl p-4 flex flex-col justify-end space-y-2 overflow-hidden border border-purple-500/10">
                        <div className="flex gap-1 items-end h-28">
                          {Array.from({ length: 48 }).map((_, idx) => {
                            const val = Math.floor(Math.sin((idx + Date.now()/1000) * 0.5) * 40 + 60);
                            return (
                              <div 
                                key={idx} 
                                className="flex-1 bg-gradient-to-t from-purple-950 to-[#00e5ff] rounded-t-sm" 
                                style={{ height: `${val}%` }} 
                              />
                            );
                          })}
                        </div>
                        <div className="flex justify-between text-[8px] text-zinc-500 font-mono">
                          <span>Channel 0x01</span>
                          <span>Lattice Active Grid Vector</span>
                          <span>Channel 0x30</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* [E4] ENTERPRISE PORTFOLIO */}
              {activePanel === "Enterprise Portfolio" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-emerald-400 uppercase tracking-widest">★ NEXORAX ENTERPRISE PORTFOLIO LEDGER</h4>
                    <p className="text-zinc-400 text-xs">
                      Master balance sheets, registered subsidiaries, and Secure Payment Provider Settlement parameters under direct validation of Audit Gavel.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-black/45 p-5 rounded-2xl border border-white/5 space-y-4 font-mono">
                        <span className="text-[10px] font-mono text-purple-400 uppercase block font-bold">Secure Payment Provider finance ROUTING</span>
                        <div className="p-4 bg-[#070412] border border-emerald-500/35 rounded-xl space-y-3">
                          <div className="flex justify-between text-xs">
                            <span className="text-zinc-400">Account Owner:</span>
                            <span className="text-white font-bold">NexoraX Digital Ltd</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-zinc-400">SecurePayment Sort Code:</span>
                            <span className="text-[#00e5ff] font-bold tracking-widest">Verified Payment Configuration</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-zinc-400">Account Number:</span>
                            <span className="text-[#00e5ff] font-bold tracking-widest">Secure Billing Provider</span>
                          </div>
                          <div className="flex justify-between text-xs pt-2 border-t border-white/5">
                            <span className="text-zinc-500">NexoraX Resource Library Backup:</span>
                            <span className="text-emerald-400 font-bold">SYNCED (CloudSync-02)</span>
                          </div>
                        </div>
                        <span className="text-[10px] text-zinc-500 leading-relaxed block text-left">
                          All payments cleared route directly to Secure Payment Provider and execute the 1% 3% Community Allocation / 20% Tax.
                        </span>
                      </div>

                      <div className="bg-black/45 p-5 rounded-2xl border border-white/5 space-y-4">
                        <span className="text-[10px] font-mono text-cyan-400 uppercase block font-bold">REGISTERED CORPORATE ENTITIES</span>
                        <div className="space-y-2 text-xs text-left">
                          {[
                            { name: "NexoraX Digital Ltd", reg: "Reg: SC887095 (UK)", assets: "£150,000 Resource resource" },
                            { name: "NexoraX NexoraX Solutions LLC", reg: "Reg: DE-82019 Munich Core", assets: "Bare-Metal Cluster Fabric" },
                            { name: "NexoraX Trust Foundation", reg: "Reg: Dunfermline Trust Registry", assets: "Global Philanthropic Levy Node" }
                          ].map((e, idx) => (
                            <div key={idx} className="p-3 bg-[#0c061a] border border-white/5 rounded-xl flex justify-between items-center">
                              <div>
                                <span className="font-bold text-white block uppercase text-[11px]">{e.name}</span>
                                <span className="text-[9.5px] text-zinc-500 block font-mono">{e.reg}</span>
                              </div>
                              <span className="text-[10px] text-emerald-400 font-mono font-bold">{e.assets}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* [E5] AUTONOMOUS CONTROL */}
              {activePanel === "Autonomous Control" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-[#00e5ff] uppercase tracking-widest">★ ANTIGRAVITY NODE-09.2 CENTRAL AUTOPILOT</h4>
                    <p className="text-zinc-400 text-xs">
                      Configure autonomous cron timers, trigger automated SecurePayment balance audits, and monitor background loop actions of the Antigravity system.
                    </p>

                    <div className="p-5 bg-black/45 rounded-2xl border border-white/5 space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <span className="text-xs font-mono font-bold text-white block text-left">AUTONOMOUS HEARTBEAT SWEEPERS</span>
                          <span className="text-[10px] text-zinc-400 text-left block">Runs checks on compliance applications, bank balances, and server loads every 120 seconds.</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[9.5px] font-mono text-emerald-400 font-bold bg-emerald-950/30 px-3 py-1 border border-emerald-500/25 rounded-full animate-pulse">AUTOPILOT ARMED</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 text-left">
                        <div className="p-4 bg-zinc-950 border border-white/5 rounded-xl space-y-3">
                          <span className="text-[10px] font-mono text-zinc-450 block uppercase font-bold">LATENCY BOUND CORRECTION</span>
                          <input type="range" min="100" max="1000" defaultValue="800" className="w-full accent-[#00e5ff]" />
                          <div className="flex justify-between text-[10px] font-mono text-zinc-400">
                            <span>Maximum limit: 1000ms</span>
                            <span className="text-[#00e5ff] font-bold">Current: 800ms (0.8s Limit)</span>
                          </div>
                        </div>

                        <div className="p-4 bg-zinc-950 border border-white/5 rounded-xl flex items-center justify-between">
                          <div>
                            <span className="text-xs font-bold text-white block uppercase">Zakat Levy Deductor Loop</span>
                            <span className="text-[9.5px] text-zinc-400 block">Enforce automatic charity split calculation</span>
                          </div>
                          <div className="w-10 h-6 bg-emerald-500 rounded-full p-0.5 cursor-pointer flex justify-end">
                            <div className="w-5 h-5 bg-white rounded-full shadow-md" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* [E6] STRATEGIC ROADMAP */}
              {activePanel === "Strategic Roadmap" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-[#e840f3] uppercase tracking-widest">★ INSTITUTIONAL NEXORAX STRATEGIC TIMELINE</h4>
                    <p className="text-zinc-400 text-xs">
                      Track master operational phases toward full-spectrum NexoraX dominance. Align Resource disbursements and talent acquisition parameters flawlessly.
                    </p>

                    <div className="relative border-l-2 border-purple-950 ml-4 pl-6 space-y-6 py-2">
                      {[
                        { title: "PHASE 1: GERMAN SHIELD & BARE-METAL CALIBRATION", desc: "Lock Munich, Germany data center clusters into an ultra-low-latency 0.8s loop. Complete anti-clone filters.", date: "Completed (Q1 2026)", active: false },
                        { title: "PHASE 2: EXECUTIVE Secure Invoice Register ACQUISITION STABILIZATION", desc: "Circumvent conventional corporate queues via NexoraX billing records direct matching. Stabilize Google Workspace.", date: "Active (Q2 2026)", active: true },
                        { title: "PHASE 3: £150,000 Resource resource FULL DEPLOYMENT", desc: "Integrate multi-tier Secure Payment Provider ledger splitting with automatic 1% charity allocation and 10% Maintenance & Hosting Fee.", date: "Initiating (Q3 2026)", active: false },
                        { title: "PHASE 4: INFINITE LIQUIDITY SINGULARITY CORE", desc: "Activate automated Prospecting Loops and Strike Nodes. Secure autonomous recursive revenue operations.", date: "Projected (Q4 2026)", active: false }
                      ].map((item, idx) => (
                        <div key={idx} className="relative">
                          {/* Circle indicator */}
                          <div className={`absolute -left-[31px] top-1 w-4 h-4 rounded-full border-2 ${
                            item.active 
                              ? "bg-pink-500 border-pink-400 shadow-[0_0_8px_#fa4b9c]" 
                              : "bg-black border-purple-500/40"
                          }`} />
                          
                          <div className={`p-4 rounded-xl border ${item.active ? 'bg-purple-950/20 border-purple-500/40' : 'bg-black/35 border-white/5'} text-left`}>
                            <div className="flex justify-between items-center text-[10px] font-mono mb-1.5 flex-wrap gap-2">
                              <span className={item.active ? "text-pink-400 font-bold" : "text-purple-400"}>{item.date}</span>
                              {item.active && <span className="bg-pink-500/20 text-pink-400 border border-pink-500/35 px-1.5 rounded text-[8px] font-bold uppercase tracking-widest animate-pulse">ACTIVE MILESTONE</span>}
                            </div>
                            <h5 className="text-xs font-mono font-black text-white">{item.title}</h5>
                            <p className="text-[11px] text-zinc-400 leading-relaxed mt-1">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* [E7] RECOVERY CENTER */}
              {activePanel === "Recovery Center" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-amber-500 uppercase tracking-widest">★ DISASTER STABILIZATION & BACKUP MIRRORING</h4>
                    <p className="text-zinc-400 text-xs text-left">
                      Enforce manual and automated state rollbacks, verify hot-standby nodes health, and download encrypted cold backups of our NexoraX Resource Library ledgers.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-black/45 p-5 rounded-2xl border border-white/5 space-y-4 font-sans text-left">
                        <span className="text-[10px] font-mono text-purple-400 uppercase block font-bold">EMERGENCY HOT-SWAP CONTROLS</span>
                        <p className="text-zinc-400 text-[11px] leading-relaxed">
                          In case of active node disruption or security warning, trigger a physical hot swap from Munich Server Node 1 to backup Munich Server Node 2.
                        </p>

                        <button
                          onClick={() => {
                            triggerToast("CRITICAL PROTOCOL TRIGGERED: Munich standby cluster node initialized.", "info");
                            setRenderingLogs(logs => [
                              `[${new Date().toLocaleTimeString()}] EMERGENCY ACTION: Hot-swap manual target hit`,
                              `[${new Date().toLocaleTimeString()}] Redirecting 4 active rendering pipelines`,
                              `[${new Date().toLocaleTimeString()}] Target Munich-Srv2 responded status code 200`,
                              ...logs
                            ]);
                          }}
                          className="w-full py-2.5 bg-red-950 border border-red-500 text-red-100 hover:bg-red-900/40 text-[10px] font-mono uppercase tracking-widest rounded-xl transition-all font-bold cursor-pointer block text-center"
                        >
                          INITIALIZE DISASTER NODE SWAP
                        </button>
                      </div>

                      <div className="bg-black/45 p-5 rounded-2xl border border-white/5 space-y-4 text-left font-mono">
                        <span className="text-[10px] font-mono text-amber-500 uppercase block font-bold">BACKUP INTEGRITY ARCHIVE</span>
                        <div className="space-y-2.5 text-xs text-left">
                          <div className="p-3 bg-zinc-950 border border-white/5 rounded-xl flex justify-between items-center text-left">
                            <div>
                              <span className="text-white block font-bold">NexoraX Resource Library snapshot</span>
                              <span className="text-[9px] text-[#00e5ff] block">Last Sync: 1 min ago</span>
                            </div>
                            <button
                              onClick={() => triggerToast("Acquiring snapshot from CloudSync-02...", "success")}
                              className="text-amber-400 font-bold uppercase hover:underline text-[10px]"
                            >
                              DOWNLINK
                            </button>
                          </div>

                          <div className="p-3 bg-zinc-950 border border-white/5 rounded-xl flex justify-between items-center text-left">
                            <div>
                              <span className="text-white block font-bold">Workspace Cold File Archive</span>
                              <span className="text-[9px] text-zinc-500 block">MD5: F3A12DE50198CBA4123F</span>
                            </div>
                            <span className="text-[10px] text-zinc-500">9.2 GB</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* [E8] THREAT INTELLIGENCE */}
              {activePanel === "Threat Intelligence" && (
                <div className="space-y-6 text-left">
                  <div className="space-y-4">
                    <h4 className="text-xs font-mono font-bold text-[#e840f3] uppercase tracking-widest">★ GLOBAL NETWORK TELEMETRY FEEDS</h4>
                    <p className="text-zinc-400 text-xs text-left">
                      Live CDN routing and performance metrics. Monitor traffic distributions and regional load balancing.
                    </p>

                    <div className="bg-black/45 p-5 rounded-2xl border border-white/5 space-y-4 text-left">
                      <div className="flex justify-between items-center text-left">
                        <span className="text-[10px] font-mono text-emerald-400 uppercase font-bold block">★ CDN EDGE ACTIVE RADAR</span>
                        <span className="text-[8px] font-mono bg-emerald-950 text-emerald-400 px-2.5 py-0.5 border border-emerald-500/30 rounded font-bold">NETWORK CORES: UNIFORM</span>
                      </div>

                      <div className="space-y-2 text-xs font-mono text-left">
                        <div className="p-3 bg-zinc-950 border border-white/5 rounded-xl flex justify-between items-center text-left">
                          <span className="text-white font-bold">[ACTIVE RANGE] 192.0.2.0/24</span>
                          <span className="text-emerald-400">Dublin Gateway Inbound Webhooks</span>
                        </div>
                        <div className="p-3 bg-zinc-950 border border-white/5 rounded-xl flex justify-between items-center text-left">
                          <span className="text-white font-bold">[ACTIVE RANGE] 198.51.100.0/22</span>
                          <span className="text-emerald-400">Frankfurt CDN Traffic Balancer</span>
                        </div>
                        <div className="p-3 bg-zinc-950 border border-white/5 rounded-xl flex justify-between items-center text-left">
                          <span className="text-white font-bold">[ACTIVE RANGE] 203.0.113.0/24</span>
                          <span className="text-emerald-400">Tokyo API Edge Router</span>
                        </div>
                      </div>

                      <div className="pt-2 text-center text-xs">
                        <span className="text-[9px] font-mono text-emerald-400 block tracking-widest uppercase font-bold animate-pulse">✓ ALL SYSTEMS OPERATIONAL AND ROUTED</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* BOTTOM CTA */}
      <div className="relative rounded-3xl border border-cyan-500/30 bg-gradient-to-r from-purple-950/20 via-[#03010b] to-cyan-950/20 p-8 overflow-hidden text-center shadow-[0_0_40px_rgba(0,229,255,0.08)] border-t-2 border-t-cyan-500/30">
        <div className="absolute inset-x-0 bottom-0 h-[220px] bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-cyan-500/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative z-10 max-w-2xl mx-auto space-y-4">
          <span className="text-[8px] font-mono text-[#00e5ff] uppercase tracking-widest font-black block">
            NEXORAX NEXORAX CREATIVE PRODUCTION
          </span>
          <h2 className="text-2xl md:text-3xl font-sans font-black tracking-tight text-white uppercase leading-none">
            Create Faster. Render Smarter. Publish Bigger.
          </h2>
          <p className="text-zinc-350 font-sans text-xs md:text-sm leading-relaxed font-bold">
            Generate. Transform. Automate.
          </p>
          <div className="pt-2 text-[8px] font-mono text-zinc-500 uppercase tracking-widest">
            NexoraX SC887095 Unified Deployment Grid • Secure Payment Provider Direct Routing
          </div>
        </div>
      </div>
    </div>
  );
}
